import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { Wallet, CreditCard, TrendingUp, Clock, CheckCircle, AlertCircle, Loader2, X, FileSpreadsheet } from 'lucide-react';
import { BrowserRouter as Router, Routes, Route, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "./contexts/AuthContext";
import { useLanguage } from "./contexts/LanguageContext";
import { useWallet, useThor } from "@vechain/dapp-kit-react";
import { disconnectEverywhere, getSignClient, findExistingSession } from "./utils/walletconnectClient";
import { apiUrl, apiPost } from "./utils/apiBase";
import { fetchDatasets, trackPlatformActivity } from "./api.js";
import AdminActivityDashboard from "./components/AdminActivityDashboard";
import B2BInvoiceAdmin from "./components/B2BInvoiceAdmin";
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { getDefaultLanguage, t, getUiLanguage } from './utils/translations';
import { APP_VERSION_LABEL } from "./appVersion";
import desktopLogoCopy from "./assets/brand/desktop-logo-copy.png";
import { V3TAX_MOBILE_LOGO_PATH } from "./constants/v3taxBranding.js";
import { shouldUseMobileBrandHeroLogo } from "./utils/heroBrandLogo.js";
/** veDelegate: nie endlos auf RPC warten — Abort nach ms (Browser-seitig). */
async function fetchJsonWithClientTimeout(url, ms = 9000) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), ms);
  try {
    const response = await fetch(url, { signal: controller.signal });
    const json = await response.json().catch(() => ({}));
    return { ok: response.ok, json };
  } finally {
    clearTimeout(timer);
  }
}

// 🚀 Drag Hook (wie in RoadmapModal & HowV3TaxWorksModal)
function useDraggable() {
  const ref = useRef(null);
  const [pos, setPos] = useState({ x: 0, y: 0 });

  const onMouseDown = (e) => {
    if (e.button !== 0) return;

    const startX = e.clientX - pos.x;
    const startY = e.clientY - pos.y;

    const onMove = (ev) => {
      setPos({
        x: ev.clientX - startX,
        y: ev.clientY - startY,
      });
    };

    const onUp = () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };

    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };

  return { ref, pos, onMouseDown, setPos };
}

// ---- Toast-Texte für Geo-Fehler (Sprache via getUiLanguage / translations) ----
function toastGeoBlocked() {
  const lang = getUiLanguage();
  const msg = t('toast.geo.blocked', lang);
  if (typeof window !== 'undefined' && window.toast?.error) {
    window.toast.error(msg);
  } else {
      }
}

function toastGeoError(kind = 'unknown') {
  const lang = getUiLanguage();
  const keyByKind = {
    permission_denied: 'toast.geo.permissionDenied',
    unavailable: 'toast.geo.unavailable',
    timeout: 'toast.geo.timeout',
    backend_unreachable: 'toast.geo.backendUnreachable',
    policy_violation: 'toast.geo.policyViolation',
    unknown: 'toast.geo.unknown',
  };
  const msg = t(keyByKind[kind] || keyByKind.unknown, lang);
  if (typeof window !== 'undefined' && window.toast?.error) {
    window.toast.error(msg);
  } else {
      }
}

function normalizeGeoError(err) {
  if (!err) return 'unknown';
  const code = err.code ?? err.name;
  if (code === 1 || /permission/i.test(err.message || '')) return 'permission_denied';
  if (code === 2 || /unavailable/i.test(err.message || '')) return 'unavailable';
  if (code === 3 || /timeout/i.test(err.message || '')) return 'timeout';
  return 'unknown';
}

// bricht *sofort* mit Toast ab, wenn die Seite blockiert ist
async function ensureGeoNotBlockedOrThrow() {
  try {
    if (navigator.permissions?.query) {
      const status = await navigator.permissions.query({ name: 'geolocation' });
      if (status.state === 'denied') {
        // dauerhaft blockiert → jedes Mal Toast + Abbruch
                toastGeoBlocked();
        const e = new Error('geo_blocked');
        e.uiKind = 'blocked';
        throw e;
      }
    }
  } catch {
    // Permissions-API nicht verfügbar → nichts tun, normaler Flow prüft weiter
  }
}

// GPS Country Detection Utility Function - ROBUST VERSION
async function getGpsCountryOrThrow() {
  // 0) erst prüfen, ob blockiert
  await ensureGeoNotBlockedOrThrow();

  // Hinweis vor GPS-Popup
  if (typeof window !== 'undefined' && window.toast?.info) {
    window.toast.info(t('toast.geo.infoBeforePrompt', getUiLanguage()), { duration: 4000 });
  }

    const pos = await new Promise((resolve, reject) => {
    if (!('geolocation' in navigator)) {
      const e = new Error('geo_unavailable'); e.uiKind = 'unavailable'; return reject(e);
    }
    navigator.geolocation.getCurrentPosition(
      (p) => resolve(p),
      (err) => { const e = new Error('geo_failed'); e.uiKind = normalizeGeoError(err); reject(e); },
      { enableHighAccuracy: false, timeout: 10000, maximumAge: 0 }
    );
  }).catch((e) => { 
    if (e.uiKind === 'blocked') toastGeoBlocked();
    else toastGeoError(e.uiKind || 'unknown');
    throw e;
  });

  const { latitude: lat, longitude: lon } = pos.coords;

  let res;
  try {
    res = await fetch('/api/geo/reverse-country', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ lat, lon })
    });
  } catch {
    toastGeoError('backend_unreachable');
    const e = new Error('geo_backend_unreachable'); e.uiKind = 'backend_unreachable'; throw e;
  }

  if (!res.ok) {
    toastGeoError('backend_unreachable');
    const e = new Error('geo_backend_error'); e.uiKind = 'backend_unreachable'; throw e;
  }

  const j = await res.json().catch(() => ({}));
  const country = (j?.country || '').toUpperCase();
  const proof = j?.proof;
  
  if (!/^[A-Z]{2}$/.test(country) || !proof) {
    toastGeoError('unknown');
    const e = new Error('geo_country_invalid'); e.uiKind = 'unknown'; throw e;
  }
  
    return { country, proof };
}
import { 
  Calculator, 
  BarChart3, 
  Download, 
  Settings, 
  HelpCircle, 
  ChevronDown,
  ChevronUp,
  Plus,
  Trash2,
  Copy,
  Check,
  Info,
  Star,
  Sparkles,
  Zap,
  Shield,
  Crown,
  Gem,
  Diamond,
  Package,
  Rocket
} from "lucide-react";

// 🚀 Stripe Configuration
const STRIPE_PUBLISHABLE_KEY = 'pk_test_51Rs8YP1vBDcPfpvKiK9Vm8laB93V7nHDQBNnfZStjZkChbWkhnDvB8cBG2KqOaqr6YSEW42VPtuvb8QrbGB7SyZx00vSediViT';

import { fetchWalletData, cryptoPaymentAPI, createPendingOrder, veWorldPaymentAPI } from "./api";
import { encodeFunctionData } from 'viem';
import Decimal from "decimal.js";
import PlanModal from "./PlanModal";
import Footer from './components/Footer';
import WalletConnectModal from './components/WalletConnectModal';
import MyWalletsDashboard from './components/MyWalletsDashboard';
import ExchangeImportsManager from './components/ExchangeImportsManager';
import LedgerConnect from './components/LedgerConnect';
// import TrustpilotReviewCollector from './components/TrustpilotReviewCollector'; // 🎯 Trustpilot: Zum Wiedereinfügen einfach auskommentieren
import RoadmapModal from './components/RoadmapModal';
import HowV3TaxWorksModal from './components/HowV3TaxWorksModal';
import LegalNoticesModal from './components/LegalNoticesModal';
import HelpSupportModal from './components/HelpSupportModal';
import ModalMotionShell from './components/ModalMotionShell';
import ModalDragHandle from './components/ModalDragHandle';

// Tax Rules sind jetzt im Backend - lokale Länderliste für UI
const TAX_COUNTRY_CODES = ['DE', 'US', 'IT', 'UK'];

const normalizeCountryCode = (value) => {
  const c = String(value || '').trim().toUpperCase().replace(/\s+/g, '_');
  if (c === 'GB' || c === 'UNITED_KINGDOM' || c === 'UNITED KINGDOM') return 'UK';
  return c;
};

const getFlagCodeForCountry = (countryCode) => {
  const c = normalizeCountryCode(countryCode);
  if (c === 'UK') return 'gb';
  return c.toLowerCase();
};

/** SVG statt kleines PNG — Union Jack bleibt bei 20px scharf. */
const getFlagUrlForCountry = (countryCode) =>
  `https://flagcdn.com/${getFlagCodeForCountry(countryCode)}.svg`;

const getAvailableCountries = () => [
  { code: 'US', name: 'United States', currency: 'USD' },
  { code: 'DE', name: 'Germany', currency: 'EUR' },
  { code: 'UK', name: 'United Kingdom', currency: 'GBP' },
  { code: 'IT', name: 'Italy', currency: 'EUR' },
];

const TAX_YEAR_OPTIONS = [
  { value: '2024', isFree: true },
  { value: '2025', isFree: false },
  { value: '2026', isFree: false },
];

const isUkTaxCountryCode = (countryCode) => {
  const c = String(countryCode || '').trim().toUpperCase().replace(/\s+/g, '_');
  return c === 'UK' || c === 'GB' || c === 'UNITED_KINGDOM';
};

const formatTaxYearDisplayLabel = (selectedCountry = 'DE', taxYear) => {
  const yearInt = Number(taxYear);
  if (!Number.isFinite(yearInt) || yearInt <= 0) return String(taxYear || '');
  if (isUkTaxCountryCode(selectedCountry)) return `${yearInt}/${yearInt + 1}`;
  return String(yearInt);
};
import LoginComponent from './components/LoginComponent';
import PaywallComponent from './components/PaywallComponent';
import { savePairing } from './utils/walletconnectClient';
import {
  getPlanInfo,
  getPlanTxLimit,
  getPlanPriceEUR,
  isEarlyAdopterPricingActive,
  formatTxLimitDisplay,
  getPlanDiscountPercent,
  getPlanSavingsEUR,
} from './utils/planUtils';


// 🔎 Normalisiert Plan-Strings ("Ultimate", "ultimate", "Ultimate Plan" etc.) → stabile IDs
const normalizePlanId = (raw) => {
  const s = (raw ?? "").toString().trim().toLowerCase();
  if (!s) return "free";
  if (s === "dev" || s.includes("dev") || s.includes("admin")) return "dev";
  if (s.includes("ultimate")) return "ultimate";
  if (s.includes("elite")) return "elite";
  if (s.includes("pro")) return "pro";
  if (s.includes("standard")) return "standard";
  if (s.includes("free")) return "free";
  return s;
};

// 🎨 Formatiert Plan-Id für die Anzeige (immer korrekte Groß-/Kleinschreibung)
const formatPlanDisplay = (planId) => {
  if (!planId) return "";
  const normalized = normalizePlanId(planId);
  const displayMap = {
    "free": "Free",
    "standard": "Standard",
    "pro": "Pro",
    "elite": "Elite",
    "ultimate": "Ultimate",
    "dev": "Dev"
  };
  return displayMap[normalized] || (normalized ? normalized.charAt(0).toUpperCase() + normalized.slice(1) : "");
};

const FeatureYes = () => (
  <span className="inline-flex h-4 w-4 items-center justify-center rounded-[5px] bg-emerald-500 text-white shadow-sm shadow-emerald-500/20">
    <Check className="h-3 w-3 stroke-[3]" />
  </span>
);

const FeatureNo = () => (
  <span className="inline-flex h-4 w-4 items-center justify-center rounded-[5px] bg-rose-50 text-rose-500 ring-1 ring-rose-100">
    <X className="h-3 w-3 stroke-[3]" />
  </span>
);

// ✅ Re-usable Plan Badge Icon (Free/Standard/Pro/Elite/Ultimate) – konsistent & "premium"
const PlanBadge = ({ planId, size = "sm", variant = "hero", className = "", title }) => {
  const id = normalizePlanId(planId);

  const styles = {
    // Free = ✅ grüner Haken
    free: {
      Icon: Check,
      bg: "from-emerald-500 via-green-600 to-lime-500",
      ring: "ring-emerald-200/70",
      glow: "from-emerald-200/60 via-lime-200/50 to-green-300/60",
    },

    // Standard = Karton
    standard: {
      Icon: Package,
      bg: "from-slate-500 via-slate-600 to-sky-600",
      ring: "ring-slate-200/60",
      glow: "from-slate-300/40 via-sky-300/40 to-blue-400/40",
    },

    // Pro = Blitz (lila)
    pro: {
      Icon: Zap,
      // VeChain-ish: blau/purple, dunkler, kein Pink
      bg: "from-[#1E3A8A] via-[#4338CA] to-[#7266FF]",
      ring: "ring-indigo-200/60",
      glow: "from-[#1E3A8A] via-[#4338CA] to-[#7266FF]",
    },

    // Elite = Diamant (gelb)
    elite: {
      Icon: Gem,
      bg: "from-amber-400 via-yellow-500 to-orange-500",
      ring: "ring-amber-200/70",
      glow: "from-amber-200/60 via-yellow-200/55 to-orange-200/55",
    },

    // Ultimate = Rakete (rot)
    ultimate: {
      Icon: Rocket,
      bg: "from-red-500 via-rose-600 to-orange-500",
      ring: "ring-rose-200/60",
      glow: "from-red-300/55 via-rose-300/50 to-orange-300/50",
    },

    // Dev
    dev: {
      Icon: Crown,
      bg: "from-blue-600 via-indigo-600 to-violet-700",
      ring: "ring-blue-200/60",
      glow: "from-blue-300/50 via-indigo-300/45 to-violet-400/50",
    },
  };

  const cfg = styles[id] || styles.free;

  const sizes = {
    // 🔥 für Header: kleiner & cleaner
    xxs: { wrap: "w-4 h-4 rounded-md", icon: "w-2.5 h-2.5", sparkle: false },
    xs:  { wrap: "w-5 h-5 rounded-lg", icon: "w-3 h-3", sparkle: false },

    // Standard UI
    sm:  { wrap: "w-8 h-8 rounded-xl", icon: "w-4 h-4", sparkle: true,  sparklePos: "top-1.5 right-1.5", sparkleClass: "w-2 h-2" },
    lg:  { wrap: "w-16 h-16 rounded-2xl", icon: "w-8 h-8", sparkle: true, sparklePos: "top-2 right-2", sparkleClass: "w-2.5 h-2.5" },
  };

  const s = sizes[size] || sizes.sm;
  const Icon = cfg.Icon;

  const compact = variant === "compact";

  return (
    <div
      title={title}
      className={[
        "relative inline-flex items-center justify-center overflow-hidden group",
        s.wrap,
        "bg-gradient-to-br",
        cfg.bg,

        // compact: dezent im Header / hero: fett im Dropdown
        compact ? "border border-white/20 shadow-sm" : `ring-1 ${cfg.ring} shadow-lg`,
        "transform-gpu transition-all duration-300",
        compact ? "" : "hover:shadow-xl hover:scale-[1.03]",
        className,
      ].join(" ")}
    >
      {/* Outer glow (nur hero) */}
      {!compact && (
        <div className={`absolute inset-0 bg-gradient-to-br ${cfg.glow} opacity-40 blur-lg`} />
      )}

      {/* Inner highlight + Depth */}
      <div className="absolute inset-0 rounded-[inherit] bg-gradient-to-b from-white/35 to-transparent opacity-40" />
      <div className="absolute inset-0 rounded-[inherit] shadow-[inset_0_1px_0_rgba(255,255,255,0.35),inset_0_-1px_0_rgba(0,0,0,0.25)]" />

      {/* Shine sweep (nur hero) */}
      {!compact && (
        <div className="absolute -left-1/2 top-0 h-full w-1/2 rotate-12 bg-white/20 blur-md opacity-0 translate-x-0 group-hover:opacity-100 group-hover:translate-x-[220%] transition-all duration-700" />
      )}

      {/* Icon */}
      <Icon className={`${s.icon} text-white drop-shadow-sm relative z-10`} />
    </div>
  );
};

// 🎨 Helper: plan-farbige Hover-Akzentfarben für Chips und Headers
const accentClassByPlanId = (planId) => {
  const id = normalizePlanId(planId);
  return ({
    free:     "hover:bg-emerald-50/60 hover:border-emerald-200/60 hover:shadow-emerald-200/40",
    standard: "hover:bg-slate-50/60   hover:border-slate-200/60   hover:shadow-slate-200/40",
    pro:      "hover:bg-indigo-50/60  hover:border-indigo-200/60  hover:shadow-indigo-200/40",
    elite:    "hover:bg-amber-50/60   hover:border-amber-200/60   hover:shadow-amber-200/40",
    ultimate: "hover:bg-rose-50/60    hover:border-rose-200/60    hover:shadow-rose-200/40",
    dev:      "hover:bg-violet-50/60  hover:border-violet-200/60  hover:shadow-violet-200/40",
  }[id] || "hover:bg-slate-50/60 hover:border-slate-200/60 hover:shadow-slate-200/40");
};

// 🔹 Reusable Token-Icon für Transaktionskarten – Premium (Ziel-Layout)
const TokenIcon = ({ src, alt, bgClass }) => (
  <div
    className={`
      mx-auto flex items-center justify-center
      rounded-[23px] ${bgClass}
      h-[68px] w-[68px] sm:h-[74px] sm:w-[74px]
      border border-white/70
      shadow-[0_12px_28px_rgba(15,23,42,0.10)]
      transition-transform duration-200 ease-out
      hover:-translate-y-0.5
    `}
  >
    <img
      src={src}
      alt={alt}
      className="h-10 w-10 sm:h-[52px] sm:w-[52px] object-contain shrink-0"
      loading="lazy"
      decoding="async"
    />
  </div>
);

// 🚀 WICHTIG: Verwende DAppKit's eigenen SignClient, nicht einen separaten!

// ✅ Re-usable Wallet-Set icon (gleiches Symbol wie im Dashboard)
const WalletSetIcon = ({ className = "w-4 h-4 text-white" }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"
       strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
       className={className}>
    {/* Vorderes Kärtchen */}
    <rect x="3" y="7" width="14" height="10" rx="2" />
    {/* Überlappendes Kärtchen / Set-Hinweis */}
    <path d="M17 9h1a3 3 0 0 1 3 3v5a2 2 0 0 1-2 2h-9" />
    <path d="M7 7V5a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v5" />
  </svg>
);

function App() {
  // 🌐 Sprachverwaltung
  const { language, changeLanguage } = useLanguage();
  
  // 🔐 Auth-Hooks
  const { user, isAuthenticated, isLoading, checkPlanAccess, logout, checkAuthStatus, devBypass, setSource: setAuthSource, latestPlan, applyLocalAuth } = useAuth(); // 🆕 NEU: latestPlan hinzugefügt, FIX: authLoading → isLoading, 🔹 applyLocalAuth für sofortiges UI-Update

  // 👑 Admin Wallet (für direkten Full-Analysis-Flow ohne Payment)
  const ADMIN_WALLET = "0x3c1d3cc2a44569cecbef586c1fcd8cbb821b9c5e";

  // 🚀 DAppKit React Hooks (wie von GPT5 empfohlen)
  const { account, connect, source, setSource } = useWallet();
  const { thor } = useThor();

  const isAdminWallet = useMemo(() => {
    const norm = (addr) => {
      const raw = String(addr || "").trim();
      if (!raw) return "";
      const stripped = raw.includes(":") ? raw.split(":").pop() : raw;
      return stripped.toLowerCase();
    };
    let fromStorage = "";
    try {
      fromStorage = norm(JSON.parse(localStorage.getItem("v3tax_auth") || "{}")?.walletAddress);
    } catch {}
    const connected = norm(user?.walletAddress || fromStorage || account);
    return connected === ADMIN_WALLET;
  }, [user?.walletAddress, account, ADMIN_WALLET]);
  
  // 🚀 NEU: State für effektiven Plan pro Jahr
  const [effectivePlanByYear, setEffectivePlanByYear] = useState({});
  
  // 🚀 NEU: Funktion zum Laden des effektiven Plans
  const loadEffectivePlan = useCallback(async () => {
    // Erst nach Auth-Check: sonst kurz „eingeloggt“ im State, Session aber noch nicht → 401 in der Konsole
    if (isLoading || !isAuthenticated || !user?.walletAddress) return;

    try {
      const response = await fetch(apiUrl('/api/veworld/user-purchases'), {
        credentials: 'include'
      });

      if (response.status === 401) return;

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.purchasesByYear) {
          // Konvertiere Jahr-Strings zu Zahlen für Konsistenz
          // 🚀 FIX: Normalisiere effectivePlan beim Laden (damit nie "Free/Pro/Elite" als raw rumfliegt)
          const effectivePlans = {};
          Object.keys(data.purchasesByYear).forEach(yearStr => {
            effectivePlans[yearStr] = normalizePlanId(data.purchasesByYear[yearStr].effectivePlan);
          });
          setEffectivePlanByYear(effectivePlans);
                  }
      }
    } catch (error) {
          }
  }, [isLoading, isAuthenticated, user?.walletAddress]);

  // 🚀 NEU: Lade effektiven Plan beim Start
  useEffect(() => {
    loadEffectivePlan();
  }, [loadEffectivePlan]);

  // 🚀 NEU: Funktion zum Neuladen von Datasets und Plan-Info (nach Upgrade)
  const reloadDatasetsAndPlanInfo = useCallback(async () => {
        
    // 1) Plan-Info neu laden
    await loadEffectivePlan();
    
    // 2) Datasets neu laden (via CustomEvent für MyWalletsDashboard)
    window.dispatchEvent(new CustomEvent('datasets:reload', {
      detail: { timestamp: Date.now() }
    }));
    
    // 3) Optional: Auch latestPlan im AuthContext neu laden
    if (window.loadLatestPlan) {
      window.loadLatestPlan(user?.walletAddress);
    }
    
      }, [loadEffectivePlan, user?.walletAddress]);
  
  // NOTE: selectedYearStr must be computed AFTER 'year' useState is declared to avoid TDZ
  
  // 🚀 FIX: Refs für VeWorld-Flow-Tracking (verhindert Reload beim Tab-Wechsel)
  const veWorldFlowActiveRef = useRef(false);   // läuft gerade ein VeWorld Flow?
  const reloadOnCloseRef = useRef(false);       // nach Close wirklich neu laden?
  
  // 🚀 BONUS FIX: useRef-Guards für DAppKit Debug-Logs (verhindert Spam bei jedem Render)
  const dappKitDebugLoggedRef = useRef(false);
  const providerStatusLoggedRef = useRef(false);
  const sessionStatusLoggedRef = useRef(false);
  
  // 🚀 FIX: Doppelklick-Schutz für handleAddToDashboard (verhindert parallele POSTs)
  const addInFlightRef = useRef(false);

  // Client-attached Analyse-Waiter: neuer Klick / Unmount detacht den alten
  // transaction-count Request. Backend SingleFlight läuft weiter, solange
  // andere Waiter (z.B. zweiter Tab) noch hängen.
  const analyzeAbortRef = useRef(null);

  useEffect(() => {
    return () => {
      analyzeAbortRef.current?.abort();
    };
  }, []);

  // 🔍 DEBUG: Überprüfe DAppKit-Status
  // Toast-Bridge für GPS-Funktionen
  useEffect(() => {
    if (!window.toast) window.toast = toast;
  }, []);

  // 🚀 BONUS FIX: DAppKit Debug nur einmal loggen (nicht bei jedem Render)
  useEffect(() => {
    if (dappKitDebugLoggedRef.current) return;
    
                    if (thor) {
                }
    
    // 🚀 WICHTIG: Teste Provider-Initialisierung
        
    // 🚀 WICHTIG: Teste alle verfügbaren Hooks
        
    dappKitDebugLoggedRef.current = true;
  }, [account, source, thor]);
  
  // 🚀 Event-Listener für WalletConnect Account-Wechsel
  useEffect(() => {
    const handleAccountsChanged = (event) => {
            
      // Hier könnte die dApp ihren State aktualisieren
      // z.B. neuen Account setzen, UI aktualisieren
      if (event.detail.accounts && event.detail.accounts.vechain) {
        const newAccounts = event.detail.accounts.vechain.accounts;
        if (newAccounts && newAccounts.length > 0) {
          const newWalletAddress = newAccounts[0].split(':').pop(); // Extrahiere Adresse aus "vechain:chain:address"
                    
          // TODO: Hier könnte der neue Account in der dApp gesetzt werden
          // z.B. navigate mit neuem walletAddress
        }
      }
    };

    window.addEventListener('walletconnect:accountsChanged', handleAccountsChanged);
    
    return () => {
      window.removeEventListener('walletconnect:accountsChanged', handleAccountsChanged);
    };
  }, []);
  
  // 🆕 WICHTIG: Prüfe URL-Parameter für automatisches Modal-Öffnen
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const shouldOpenWalletModal = urlParams.get('openWalletModal');
    
    if (shouldOpenWalletModal === 'true') {
            setShowWalletModal(true);
      
      // 🚀 WICHTIG: URL-Parameter entfernen nach Modal-Öffnung
      const newUrl = window.location.pathname;
      window.history.replaceState({}, document.title, newUrl);
          }
  }, []);
  
  // 🚀 WICHTIG: Kein Singleton mehr - nur React Hooks verwenden!
  
  // 🚀 WICHTIG: Session-Status überwachen (nur einmal loggen)
  useEffect(() => {
    if (sessionStatusLoggedRef.current || !account) return;
    
        
    // 🎯 Bestehende Session-Info anzeigen
    const existingSession = findExistingSession();
    if (existingSession) {
          }
    
    sessionStatusLoggedRef.current = true;
  }, [account, source, thor]);
  
  // 🚀 WICHTIG: Teste Provider-Initialisierung (nur einmal loggen)
  useEffect(() => {
    if (providerStatusLoggedRef.current) return;
    
        
    // 🚀 WICHTIG: Teste Provider-Initialisierung
    if (!thor) {
                  
      // 🚀 WICHTIG: Teste alle verfügbaren Hooks
          } else {
                }
    
    providerStatusLoggedRef.current = true;
  }, [account, source, thor]);
  
  // 🚀 WICHTIG: Kein disconnect Hook verfügbar - Session-Management über WalletConnect
  
  // 🎯 Trustpilot Script-Loading (einmalig) - AUSKOMMENTIERT
  // useEffect(() => {
  //   // Trustpilot Script nur einmal laden
  //   const id = "trustpilot-widget-script";
  //   if (document.getElementById(id)) return;
  //
  //   const s = document.createElement("script");
  //   s.id = id;
  //   s.type = "text/javascript";
  //   s.async = true;
  //   s.src = "//widget.trustpilot.com/bootstrap/v5/tp.widget.bootstrap.min.js";
  //   document.head.appendChild(s);
  // }, []);

  
  // 🎉 Success Notification States
  const [showWalletConnectedNotification, setShowWalletConnectedNotification] = useState(false);
  const [showPurchaseSuccessNotification, setShowPurchaseSuccessNotification] = useState(false);
  const [purchaseSuccessMessage, setPurchaseSuccessMessage] = useState('');
  const [isLanguageDropdownOpen, setIsLanguageDropdownOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [showExportHint, setShowExportHint] = useState(false);
  const [showScrollToTop, setShowScrollToTop] = useState(false);
  const showScrollToTopRef = useRef(false);
  const scrollRafRef = useRef(null);
  const [showLegalModal, setShowLegalModal] = useState(false);
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [showFunctionModal, setShowFunctionModal] = useState(false);
  const [showRoadmap, setShowRoadmap] = useState(false);
  const [helpActiveSection, setHelpActiveSection] = useState('overview');
  const [showMyWalletsDashboard, setShowMyWalletsDashboard] = useState(false);
  const [showPreviewImports, setShowPreviewImports] = useState(false);
  const [previewExchangeRows, setPreviewExchangeRows] = useState([]);
  const [showPlanModal, setShowPlanModal] = useState(false);
  const [isHandheldDevice, setIsHandheldDevice] = useState(false);

  const toggleMobileMenu = useCallback(() => {
    setIsLanguageDropdownOpen(false);
    setShowDropdown(false);
    setIsMobileMenuOpen((prev) => !prev);
  }, [setShowDropdown]);

  const closeMobileMenu = useCallback(() => {
    setIsLanguageDropdownOpen(false);
    setIsMobileMenuOpen(false);
  }, []);

  const mobileNavItems = [
    {
      key: 'how-it-works',
      label: language === 'en' ? 'How V3Tax Works' : 'Wie V3Tax funktioniert',
      description: language === 'en' ? 'Functions & Features' : 'Funktionen & Features',
      onPress: () => {
        closeMobileMenu();
        setShowFunctionModal(true);
      }
    },
    {
      key: 'my-wallets',
      label: t('myWallets', language),
      description: t('manageActiveWallets', language),
      onPress: () => {
        closeMobileMenu();
        setShowMyWalletsDashboard(true);
      }
    },
    {
      key: 'plans',
      label: language === 'en' ? 'Pricing & Plans' : 'Preise & Pläne',
      description: language === 'en' ? 'Compare plan benefits' : 'Vergleiche Plan-Vorteile',
      onPress: () => {
        closeMobileMenu();
        setShowPlanModal(true);
      }
    },
    {
      key: 'roadmap',
      label: language === 'en' ? 'Roadmap' : 'Roadmap',
      description: language === 'en' ? 'Planned features & limitations' : 'Geplante Features & Limits',
      onPress: () => {
        closeMobileMenu();
        setShowRoadmap(true);
      }
    },
    {
      key: 'legal',
      label: t('legalNotices', language),
      description: t('termsAndPrivacy', language),
      onPress: () => {
        closeMobileMenu();
        setShowLegalModal(true);
      }
    },
    {
      key: 'help',
      label: t('helpAndSupport', language),
      description: t('getHelpAndDocumentation', language),
      onPress: () => {
        closeMobileMenu();
        setHelpActiveSection("overview");
        setShowHelpModal(true);
      }
    }
  ];

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 768) {
        setIsMobileMenuOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;

    const mediaQuery = window.matchMedia('(pointer: coarse)');

    const compute = () => {
      // Coarse Pointer (Touch) ist okay – aber nur "handheld", wenn auch wirklich small viewport
      const isSmall = window.innerWidth < 768;
      setIsHandheldDevice(mediaQuery.matches && isSmall);
    };

    const updateIsHandheld = () => compute();
    compute();

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', updateIsHandheld);
    } else if (mediaQuery.addListener) {
      mediaQuery.addListener(updateIsHandheld);
    }

    window.addEventListener('resize', compute);

    return () => {
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener('change', updateIsHandheld);
      } else if (mediaQuery.removeListener) {
        mediaQuery.removeListener(updateIsHandheld);
      }
      window.removeEventListener('resize', compute);
    };
  }, []);

  // ⬇️ NEU: Listener für "Upgrade-only" aus dem Dashboard
  useEffect(() => {
    const onDashboardUpgradeOpen = async (e) => {
      try {
        const { datasetId, basePurchaseId, year, fromPlan, toPlan, token, residenceCountry: detailResidenceCountry } = e.detail || {};

        if (!isAuthenticated) {
          // vorhandenes Login/Wallet-Modal nutzen
          if (setShowWalletModal) setShowWalletModal(true);
          return;
        }

        // 1) Upgrade-Preis & Tokenmenge vorbereiten (Backend)
        const normalizedToken = (token || 'vet').toLowerCase();
        const yearNumber = Number(year);
        // ✅ Normalisiere beide Pläne vor dem API-Call (lowercase IDs)
        const normalizedFromPlan = normalizePlanId(fromPlan);
        const normalizedToPlan = normalizePlanId(toPlan);
        const upgradeData = await veWorldPaymentAPI.prepareUpgradeTransaction(
          normalizedFromPlan,
          normalizedToPlan,
          yearNumber,
          normalizedToken
        );

        // 2) GPS/EU-Nachweis wie im bestehenden Flow holen
        let geoResult = null;
        try {
          geoResult = await getGpsCountryOrThrow();
        } catch (gpsErr) {
                    return;
        }

        // 3) Transaktionsdaten fürs bestehende Auto-Modal bauen (Upgrade ONLY)
        // 🚀 BONUS: localStorage aufräumen, damit keine alten Wallets mitgeschleppt werden
        localStorage.removeItem('wallet_pairs');
        
        const upgradeResidenceCountry = resolveResidenceCountryForApi(
          detailResidenceCountry ||
          country ||
          ''
        );
        if (!upgradeResidenceCountry) {
          toast.error(
            language === 'en'
              ? 'Tax country is missing for this dataset. Please select your tax country and try again.'
              : 'Steuerland für dieses Dataset fehlt. Bitte wähle dein Steuerland und versuche es erneut.'
          );
          return;
        }

        const transactionData = {
          token: normalizedToken,
          tokenSymbol: normalizedToken.toUpperCase(),
          value: Number(upgradeData.expectedCryptoAmount).toFixed(7),
          priceEUR: upgradeData.priceEUR,
          tokenPriceEUR: upgradeData.tokenPriceEUR,
          to: getVeWorldTargetAddress(), // 🚀 FIX: Nutzt Hostname/ENV und gibt in Staging die Staging-Wallet zurück
          plan: toPlan,
          year: yearNumber,
          walletCount: 0,            // 👈 Upgrade only: keine Wallets
          walletAddresses: [],       // 👈 Upgrade only
          residenceCountry: upgradeResidenceCountry,
          geoResult,
          isUpgrade: true,
          upgradeFromPlan: fromPlan,
          upgradeBasePurchaseId: basePurchaseId || datasetId || null
        };

        setAutoTransactionData(transactionData);
        setShowAutoTransactionModal(true);
      } catch (err) {
                toast.error(language === 'en'
          ? 'Upgrade could not be prepared.'
          : 'Upgrade konnte nicht vorbereitet werden.');
      }
    };

    window.addEventListener('upgrade:open', onDashboardUpgradeOpen);
    return () => window.removeEventListener('upgrade:open', onDashboardUpgradeOpen);
  }, [isAuthenticated, language]);

  // 🔗 Wallet Connect Modal State
  const [showWalletModal, setShowWalletModal] = useState(false);
  const [resetQrCode, setResetQrCode] = useState(false);
  const [forceFreshVeWorldLogin, setForceFreshVeWorldLogin] = useState(false);

  const clearWalletConnectArtifacts = useCallback((hard = false) => {
    localStorage.removeItem("walletconnect");
    localStorage.removeItem("walletconnect-v2");

    if (hard) {
      localStorage.removeItem("wc_v3tax_pairing_topic");
    }

    Object.keys(localStorage).forEach((key) => {
      if (
        key.startsWith("wc@") ||
        key.startsWith("wc_") ||
        key.includes("walletconnect")
      ) {
        localStorage.removeItem(key);
      }
    });
  }, []);

  const logoutFromV3TaxOnly = useCallback(async () => {
    
    sessionStorage.setItem("v3tax:force_logged_out", "1");

    try {
      setSource(null);
      setAuthSource(null);
      clearWalletConnectArtifacts(false);
          } catch (cleanupError) {
          }

    try {
      localStorage.removeItem("v3tax_auth");
      window.dispatchEvent(new Event("v3tax-auth"));
    } catch (storageError) {
          }

    try {
      await logout();
          } catch (logoutError) {
          }

    try {
      await checkAuthStatus();
    } catch (statusError) {
          }

    setShowUserDropdown(false);

    window.location.assign(window.location.pathname);
  }, [setSource, setAuthSource, clearWalletConnectArtifacts, logout, checkAuthStatus]);

  const startFreshVeWorldSwitch = useCallback(async () => {
    
    sessionStorage.setItem("v3tax:force_logged_out", "1");

    try {
      const result = await disconnectEverywhere();
      
      if (!result?.success) {
              }
    } catch (disconnectError) {
          }

    try {
      setSource(null);
      setAuthSource(null);
      clearWalletConnectArtifacts(true);
          } catch (cleanupError) {
          }

    try {
      localStorage.removeItem("v3tax_auth");
      window.dispatchEvent(new Event("v3tax-auth"));
    } catch (storageError) {
          }

    try {
      await logout();
          } catch (logoutError) {
          }

    try {
      await checkAuthStatus();
    } catch (statusError) {
          }

    setShowUserDropdown(false);

    setForceFreshVeWorldLogin(true);
    setShowWalletModal(true);
    setResetQrCode(true);

    setTimeout(() => {
      setResetQrCode(false);
    }, 150);
  }, [
    disconnectEverywhere,
    setSource,
    setAuthSource,
    clearWalletConnectArtifacts,
    logout,
    checkAuthStatus,
  ]);

  const isUserRejectedWalletRequest = useCallback((error) => {
    const code =
      error?.code ??
      error?.cause?.code ??
      error?.data?.code ??
      error?.error?.code;

    const message = String(
      error?.message ||
        error?.cause?.message ||
        error?.data?.message ||
        error?.error?.message ||
        error?.reason ||
        ""
    ).toLowerCase();

    return (
      code === 4001 ||
      code === 5000 ||
      code === "4001" ||
      code === "5000" ||
      code === 12001 ||
      code === "12001" ||
      message.includes("user rejected") ||
      message.includes("rejected") ||
      message.includes("denied") ||
      message.includes("cancel") ||
      message.includes("cancelled") ||
      message.includes("canceled") ||
      message.includes("closed") ||
      message.includes("popup closed") ||
      message.includes("window closed") ||
      message.includes("abgebrochen") ||
      message.includes("abgelehnt")
    );
  }, []);

  const connectVeWorldExtension = useCallback(async () => {
    if (typeof window === "undefined" || !window.vechain) {
      throw new Error("VeWorld extension not available");
    }

    setSource("veworld");
    setAuthSource("veworld-extension");

    const result = await connect();
    const rawAccount =
      result?.account ||
      result?.address ||
      result ||
      account;

    const candidate = String(rawAccount || "");
    const walletAddress = candidate.includes(":")
      ? candidate.split(":").pop()
      : candidate;

    if (!/^0x[a-fA-F0-9]{40}$/.test(walletAddress)) {
      throw new Error("VeWorld Extension returned no valid wallet address");
    }

    const loginResult = await devBypass(walletAddress, {
      source: "veworld-extension",
    });

    const authData = {
      walletAddress,
      plan: loginResult?.data?.plan || "free",
      isDev: loginResult?.data?.isDev || false,
      authenticated: true,
      loginTime: new Date().toISOString(),
      source: "veworld-extension",
    };

    localStorage.setItem("v3tax_auth", JSON.stringify(authData));
    applyLocalAuth(authData);
    window.dispatchEvent(new Event("v3tax-auth"));
    await checkAuthStatus();
    showSuccessToast(walletAddress, "veworld-extension");
  }, [
    setSource,
    setAuthSource,
    connect,
    account,
    devBypass,
    applyLocalAuth,
    checkAuthStatus,
  ]);

  const handleWalletLoginClick = useCallback(async () => {
    const isDesktop =
      typeof window !== "undefined" &&
      !window.matchMedia("(pointer: coarse)").matches;

    const hasVeWorldExtension =
      typeof window !== "undefined" && !!window.vechain;

    if (isDesktop && hasVeWorldExtension) {
      try {
        await connectVeWorldExtension();
      } catch (error) {
        if (isUserRejectedWalletRequest(error)) {
          toast.info(language === "en" ? "Connection cancelled." : "Verbindung abgebrochen.");
          return;
        }
        setShowWalletModal(true);
      }
      return;
    }

    setShowWalletModal(true);
  }, [connectVeWorldExtension, isUserRejectedWalletRequest, language]);

  const dragAutoModal = useDraggable();
  const { setPos: setAutoModalPos } = dragAutoModal;
  
  // 🎯 NEUE STATES: Automatische VeWorld-Transaktion
  const [showAutoTransactionModal, setShowAutoTransactionModal] = useState(false);
  const [autoTransactionData, setAutoTransactionData] = useState(null);
  const [selectedToken, setSelectedToken] = useState('vet');
  const [autoTransactionLoading, setAutoTransactionLoading] = useState(false);
  const [autoTransactionError, setAutoTransactionError] = useState(null);
  const [autoTransactionSuccess, setAutoTransactionSuccess] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);

  // 🔐 Sichere Ermittlung der Ziel-Wallet für automatische VeWorld-Transaktionen
  const LIVE_VET_WALLET = "0xa25f5ae9dce5c4f136eac6e56a73381a61ee39a1";
  const STAGING_VET_WALLET = "0x6Deae2Aa14e318B620B511B01d1601fbcaCFb671";

  const getVeWorldTargetAddress = () => {
    // 1) Hostname hat Vorrang — verhindert, dass ein Staging-Build auf v3tax.app
    //    die Staging-Wallet aus VITE_VET_WALLET_ADDRESS nutzt.
    if (typeof window !== "undefined") {
      const host = (window.location?.hostname || "").toLowerCase();
      if (host.includes("staging")) {
        return STAGING_VET_WALLET;
      }
      if (host.includes("localhost") || host === "127.0.0.1") {
        return STAGING_VET_WALLET;
      }
      if (host === "v3tax.app" || (host.endsWith(".v3tax.app") && !host.includes("staging"))) {
        return LIVE_VET_WALLET;
      }
    }

    // 2) Explizite Frontend-Env nur für unbekannte Hosts / lokale Builds
    const envAddr = (import.meta.env.VITE_VET_WALLET_ADDRESS || "").trim();
    if (envAddr && /^0x[a-fA-F0-9]{40}$/.test(envAddr)) {
      return envAddr;
    }

    // 3) Letzter Rückfall: Live-Wallet
    return LIVE_VET_WALLET;
  };
  
  // 🧠 Helper: wallet_pairs nach einem erfolgreichen Kauf mit DB-Daten befüllen
  const hydrateWalletPairsFromServer = useCallback(
    async (yearHint) => {
      try {
        const targetYear = yearHint || new Date().getFullYear();
        const data = await fetchDatasets(targetYear);
        if (!data?.success || !Array.isArray(data.datasets) || data.datasets.length === 0) {
                    return;
        }

        // Bevorzuge Datasets des gewünschten Jahres
        const candidates =
          data.datasets.filter((d) => String(d.year) === String(targetYear)) || data.datasets;

        // Nimm das zuletzt gekaufte Dataset (nach purchase_date / created_at)
        const latest = [...candidates].sort((a, b) => {
          const ad = a.purchase_date || a.purchaseDate || a.created_at || a.createdAt || 0;
          const bd = b.purchase_date || b.purchaseDate || b.created_at || b.createdAt || 0;
          return new Date(bd).getTime() - new Date(ad).getTime();
        })[0];

        if (!latest || !Array.isArray(latest.wallets)) {
                    return;
        }

        const merged = latest.wallets.map((w) => ({
          main_wallet: (w.main_wallet || w.mainWallet || '').toLowerCase(),
          delegate_wallet: (w.delegate_wallet || w.delegateWallet
            ? String(w.delegate_wallet || w.delegateWallet).toLowerCase()
            : null),
          label: w.label || null,
          chain: 'vechain',
          main_tx_count: Number(w.main_tx_count ?? w.mainTxCount ?? 0),
          delegate_tx_count: Number(w.delegate_tx_count ?? w.delegateTxCount ?? 0),
          vet_amount: Number(w.vet_amount ?? w.vetAmount ?? w.vet ?? 0),
          vtho_amount: Number(w.vtho_amount ?? w.vthoAmount ?? w.vtho ?? 0),
          b3tr_amount: Number(w.b3tr_amount ?? w.b3trAmount ?? w.b3tr ?? 0),
          nft_count: Number(w.nft_count ?? w.nftCount ?? w.nft ?? 0),
        }));

        localStorage.setItem('wallet_pairs', JSON.stringify(merged));

        const dsId = latest.id || latest.dataset_id;
        if (dsId) {
          window.dispatchEvent(
            new CustomEvent('datasets:refresh', {
              detail: { datasetId: dsId },
            }),
          );
        }
      } catch (e) {
              }
    },
    [],
  );

  // 🎉 Manual Toast Trigger Function für Wallet Connect
  const showSuccessToast = (account, walletType) => {
        setShowWalletConnectedNotification(true);
    // Auto-hide after 7 seconds
    setTimeout(() => {
      setShowWalletConnectedNotification(false);
    }, 7000);
  };

  // 🎉 Elegante Toast-Funktion für Error-Messages
  const showErrorToast = (message) => {
    if (window.toast) {
      window.toast.error(message);
    } else {
      // Eleganter Fallback: Erstelle schönen Toast-Container
      const toastContainer = document.createElement('div');
      toastContainer.innerHTML = `
        <div style="
          position: fixed;
          top: 20px;
          right: 20px;
          background: linear-gradient(135deg, #ef4444, #dc2626);
          color: white;
          padding: 16px 20px;
          border-radius: 12px;
          box-shadow: 0 10px 25px rgba(239, 68, 68, 0.3);
          z-index: 10000;
          max-width: 400px;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          font-size: 14px;
          line-height: 1.5;
          border: 1px solid rgba(255, 255, 255, 0.1);
          backdrop-filter: blur(10px);
          animation: slideInRight 0.3s ease-out;
        ">
          <div style="display: flex; align-items: center; gap: 12px;">
            <div style="
              width: 24px;
              height: 24px;
              background: rgba(255, 255, 255, 0.2);
              border-radius: 50%;
              display: flex;
              align-items: center;
              justify-content: center;
              flex-shrink: 0;
            ">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
              </svg>
            </div>
            <div style="flex: 1;">
              ${message}
            </div>
            <button onclick="this.parentElement.parentElement.parentElement.remove()" style="
              background: none;
              border: none;
              color: rgba(255, 255, 255, 0.8);
              cursor: pointer;
              padding: 4px;
              border-radius: 4px;
              transition: background-color 0.2s;
            " onmouseover="this.style.backgroundColor='rgba(255,255,255,0.1)'" onmouseout="this.style.backgroundColor='transparent'">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
              </svg>
            </button>
          </div>
        </div>
        <style>
          @keyframes slideInRight {
            from { transform: translateX(100%); opacity: 0; }
            to { transform: translateX(0); opacity: 1; }
          }
          @keyframes slideOutRight {
            from { opacity: 1; }
            to { opacity: 0; }
          }
        </style>
      `;
      
      document.body.appendChild(toastContainer);
      
      // Auto-remove nach 8 Sekunden
      setTimeout(() => {
        if (toastContainer.parentElement) {
          toastContainer.style.animation = 'slideOutRight 0.3s ease-in forwards';
          setTimeout(() => {
            if (toastContainer.parentElement) {
              toastContainer.remove();
            }
          }, 300);
        }
      }, 8000);
      
          }
  };
  
  // 🎉 Elegante Success-Toast-Funktion für generische Nachrichten
  const showGenericSuccessToast = (message) => {
    if (window.toast) {
      window.toast.success(message);
    } else {
      // Eleganter Success-Toast
      const toastContainer = document.createElement('div');
      toastContainer.innerHTML = `
        <div style="
          position: fixed;
          top: 20px;
          right: 20px;
          background: linear-gradient(135deg, #10b981, #059669);
          color: white;
          padding: 16px 20px;
          border-radius: 12px;
          box-shadow: 0 10px 25px rgba(16, 185, 129, 0.3);
          z-index: 10000;
          max-width: 400px;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          font-size: 14px;
          line-height: 1.5;
          border: 1px solid rgba(255, 255, 255, 0.1);
          backdrop-filter: blur(10px);
          animation: slideInRight 0.3s ease-out;
        ">
          <div style="display: flex; align-items: center; gap: 12px;">
            <div style="
              width: 24px;
              height: 24px;
              background: rgba(255, 255, 255, 0.2);
              border-radius: 50%;
              display: flex;
              align-items: center;
              justify-content: center;
              flex-shrink: 0;
            ">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
              </svg>
            </div>
            <div style="flex: 1;">
              ${message}
            </div>
            <button onclick="this.parentElement.parentElement.parentElement.remove()" style="
              background: none;
              border: none;
              color: rgba(255, 255, 255, 0.8);
              cursor: pointer;
              padding: 4px;
              border-radius: 4px;
              transition: background-color 0.2s;
            " onmouseover="this.style.backgroundColor='rgba(255,255,255,0.1)'" onmouseout="this.style.backgroundColor='transparent'">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
              </svg>
            </button>
          </div>
        </div>
        <style>
          @keyframes slideInRight {
            from { transform: translateX(100%); opacity: 0; }
            to { transform: translateX(0); opacity: 1; }
          }
          @keyframes slideOutRight {
            from { opacity: 1; }
            to { opacity: 0; }
          }
        </style>
      `;
      
      document.body.appendChild(toastContainer);
      
      // Auto-remove nach 6 Sekunden
      setTimeout(() => {
        if (toastContainer.parentElement) {
          toastContainer.style.animation = 'slideOutRight 0.3s ease-in forwards';
          setTimeout(() => {
            if (toastContainer.parentElement) {
              toastContainer.remove();
            }
          }, 300);
        }
      }, 6000);
    }
  };

  // 🎉 Purchase Success Toast Function (Legacy)
  const showPurchaseSuccessToast = (message) => {
        setPurchaseSuccessMessage(message);
    setShowPurchaseSuccessNotification(true);
    // Auto-hide after 8 seconds
    setTimeout(() => {
      setShowPurchaseSuccessNotification(false);
    }, 8000);
  };



  // 🚀 NEUE FUNKTION: DAppKit Session-Status überwachen
  const getDAppKitSessionState = () => {
    try {
            
      return {
        isConnected: !!account,
        source: source,
        hasThor: !!thor,
        hasVendor: !!(thor?.vendor)
      };
    } catch (error) {
            return { isConnected: false, source: null, hasThor: false, hasVendor: false };
    }
  };

  // 🚀 NEUE FUNKTION: Bestehende DAppKit Session erkennen
  const findExistingSession = () => {
    try {
            
      const sessionState = getDAppKitSessionState();
      
      // 🎯 WICHTIG: Prüfe auch localStorage nach bestehenden Sessions
      const hasLocalSession = localStorage.getItem('walletconnect') || localStorage.getItem('walletconnect-v2');
      
      if (sessionState.isConnected || hasLocalSession) {
                return { hasSession: true, state: sessionState, hasLocal: !!hasLocalSession };
      } else {
                return { hasSession: false, state: sessionState, hasLocal: false };
      }
      
    } catch (error) {
            return { hasSession: false, state: null, hasLocal: false };
    }
  };

  // 🚀 NEUE FUNKTION: Alte WalletConnect Sessions löschen
  const cleanupOldSessions = () => {
    try {
            
      // 🎯 Alle WalletConnect-bezogenen localStorage Einträge löschen
      const keysToRemove = [
        'walletconnect',
        'walletconnect-v2',
        'walletconnect-client',
        'walletconnect-client-v2'
      ];
      
      keysToRemove.forEach(key => {
        if (localStorage.getItem(key)) {
          localStorage.removeItem(key);
                  }
      });
      
      // 🎯 Auch alle anderen Wallet-bezogenen Daten löschen
      const allKeys = Object.keys(localStorage);
      const walletKeys = allKeys.filter(key => 
        key.includes('wallet') || 
        key.includes('connect') || 
        key.includes('session')
      );
      
      walletKeys.forEach(key => {
        localStorage.removeItem(key);
              });
      
            
    } catch (error) {
          }
  };

  // 🚀 NEUE FUNKTION: Automatische VeWorld-Transaktion vorbereiten
  const handleAutoTransaction = async (plan, year, walletCount, token, geoResult = null, residenceCountryOverride = null) => {
    try {
      setAutoTransactionLoading(true);
      setAutoTransactionError(null);
      
            
      // 🎯 WICHTIG: Prüfe ob token korrekt übergeben wurde
      if (!token) {
        throw new Error('Kein Token übergeben. Bitte wähle einen Token aus.');
      }
      
            
      // 🎯 NEU: Plan-Preis in EUR definieren (robust, basiert auf normalizePlanId)
      const planId = normalizePlanId(plan);
      const priceEUR = getPlanPriceEUR(planId, { earlyAdopter: isEarlyAdopterPricingActive() });
      
      if (priceEUR === undefined) {
                throw new Error('Ungültiger Plan ausgewählt');
      }
      
            
      // 🌍 Residence Country aus Dropdown (kein stilles DE-Fallback)
      const residenceCountry = resolveResidenceCountryForApi(residenceCountryOverride || country);
      if (!residenceCountry) {
        throw new Error('Tax country is required before purchase.');
      }
            
      // 🚀 WICHTIG: Speichere ALLE analyzedWallets im localStorage für VeWorld!
      // 🎯 GENAU WIE BEI STRIPE: Behalte die Wallet-Paare unverändert!
      const allWalletsForPurchase = [...analyzedWallets];
      
      // Falls noch eine aktuelle Wallet vorhanden ist, auch diese hinzufügen
      if (wallet && !analyzedWallets.some(w => w.wallet === wallet)) {
        allWalletsForPurchase.push({
          wallet: wallet,
          delegateWallet: delegateWallet || null,
          mainTxCount: walletData?.mainWalletTxCount || 0,
          veDelegateTxCount: walletData?.delegateWalletTxCount || 0,
          vet: walletData?.vet?.transactions || 0,
          vtho: walletData?.vtho?.transactions || 0,
          b3tr: walletData?.b3tr?.transactions || 0,
          nft: walletData?.nft?.transactions || 0
        });
      }
      
      // Im localStorage speichern - das findet VeWorld!
      localStorage.setItem('analyzedWallets', JSON.stringify(allWalletsForPurchase));
      
      // 🎯 KRITISCH: Speichere auch als wallet_pairs für den Transaction Monitor!
      const walletPairs = allWalletsForPurchase.map((w) => {
        const pair = {
          main_wallet: w.wallet,
          delegate_wallet: w.delegateWallet || null,
          label: w.label || null,
          chain: 'vechain',
        };
        // Nur echte Werte persistieren – keine künstlichen 0er
        if (w.mainTxCount != null) pair.main_tx_count = w.mainTxCount;
        if (w.veDelegateTxCount != null) pair.delegate_tx_count = w.veDelegateTxCount;
        if (w.vet != null) pair.vet_amount = w.vet;
        if (w.vtho != null) pair.vtho_amount = w.vtho;
        if (w.b3tr != null) pair.b3tr_amount = w.b3tr;
        if (w.nft != null) pair.nft_count = w.nft;
        if (w.appContext) pair.app_context = w.appContext;
        return pair;
      });
      localStorage.setItem('wallet_pairs', JSON.stringify(walletPairs));
      
                  
      // 🌍 NEU: Pending-Order mit IP-Guard erstellen
      try {
                
        const walletAddresses = allWalletsForPurchase.map(w => ({
          main_wallet: w.wallet,
          delegate_wallet: w.delegateWallet || null,
          label: w.label || null,
          chain: 'vechain',
          main_tx_count: w.mainTxCount || 0,
          delegate_tx_count: w.veDelegateTxCount || 0,
          vet_amount: w.vet || 0,
          vtho_amount: w.vtho || 0,
          b3tr_amount: w.b3tr || 0,
          nft_count: w.nft || 0
        }));
        
        // --- Schritt 1: Live-Preis holen (wie im bestehenden Code) ---
        const tokenResponse = await fetch(apiUrl(`/api/crypto/token-price/${token}`), {
          method: 'GET',
          credentials: 'include'
        });
        if (!tokenResponse.ok) throw new Error(`Token-Preis für ${token.toUpperCase()} konnte nicht abgerufen werden`);
        const tokenData = await tokenResponse.json();
        const tokenPriceEUR = tokenData.price_eur;
        if (!tokenPriceEUR || tokenPriceEUR <= 0) throw new Error(`Ungültiger Token-Preis: €${tokenPriceEUR}`);

        // --- Schritt 2: Benötigte Token-Menge berechnen ---
        const tokenAmount = new Decimal(priceEUR)
          .div(tokenPriceEUR)
          .toDecimalPlaces(7, Decimal.ROUND_HALF_UP);
        
      // 🌍 NEU: Nur Modal öffnen, KEINE Pending-Order erstellen!
      // Die Pending-Order wird erst beim Klick auf "Transaktion starten" erstellt
            
      } catch (pendingOrderError) {
        if (pendingOrderError.code === 'NEEDS_IP_EVIDENCE') {
          // UX: Zeige klare, freundliche User-Meldung (mehrsprachig)
          const errorMessage = language === 'de' 
            ? 'Wir konnten dein Land nicht ermitteln. Bitte VPN/Relay deaktivieren oder ein Standardnetz verwenden und erneut versuchen.'
            : 'We could not determine your country. Please disable VPN/Relay or use a standard network and try again.';
          
          // Zeige Toast-Nachricht
          showErrorToast(errorMessage);
          return;
        } else {
          throw pendingOrderError;
        }
      }
      
      // 🎯 NEU: Direkte Token-Preis-Abfrage und Berechnung
      try {
                
        // 🚀 Token-Preis direkt von VeChainStats abrufen
        const tokenResponse = await fetch(apiUrl(`/api/crypto/token-price/${token}`), {
          method: 'GET',
          credentials: 'include'
        });
        
        if (!tokenResponse.ok) {
          throw new Error(`Token-Preis für ${token.toUpperCase()} konnte nicht abgerufen werden`);
        }
        
        const tokenData = await tokenResponse.json();
        const tokenPriceEUR = tokenData.price_eur;
        
        if (!tokenPriceEUR || tokenPriceEUR <= 0) {
          throw new Error(`Ungültiger Token-Preis für ${token.toUpperCase()}: €${tokenPriceEUR}`);
        }
        
                
        // 🎯 NEU: Token-Betrag berechnen (EUR-Preis / Token-Preis)
        const tokenAmount = priceEUR / tokenPriceEUR;
                
        // 🎯 NEU: Wallet-Adressen aus localStorage holen
        const walletPairs = JSON.parse(localStorage.getItem('wallet_pairs') || '[]');
                                
        // 🎯 KRITISCH: Wallet-Adressen für Pending-Order vorbereiten (MIT Transaktionsdaten!)
        const walletAddresses = walletPairs.map(pair => ({
          main_wallet: pair.main_wallet,
          delegate_wallet: pair.delegate_wallet || null,
          label: pair.label || null,
          chain: pair.chain || 'vechain',
          // 🎯 KRITISCH: Zähler & Mengen mitgeben!
          main_tx_count: Number(pair.main_tx_count || 0),
          delegate_tx_count: Number(pair.delegate_tx_count || 0),
          vet_amount: Number(pair.vet_amount || 0),
          vtho_amount: Number(pair.vtho_amount || 0),
          b3tr_amount: Number(pair.b3tr_amount || 0),
          nft_count: Number(pair.nft_count || 0)
        }));
        
                                
        // 🎯 NEU: Transaktionsdaten direkt erstellen (ohne Pending-Order!)
        const targetAddress = getVeWorldTargetAddress();
        
        const transactionData = {
          token: token,
          tokenSymbol: token.toUpperCase(),
          value: tokenAmount.toFixed(7),
          priceEUR: priceEUR,
          tokenPriceEUR: tokenPriceEUR,
          to: targetAddress,
          plan: plan,
          year: year,
          walletCount: walletCount,
          walletAddresses: walletAddresses, // 🎯 KRITISCH: Wallet-Adressen hinzugefügt!
          residenceCountry: residenceCountry, // 🌍 NEU: Land mitschicken (verwendet residenceCountry statt country)
          geoResult: geoResult // 🌍 NEU: GPS-Proof mitschicken
        };
        
        setAutoTransactionData(transactionData);
        setShowAutoTransactionModal(true);
                        
      } catch (apiError) {
                throw new Error(`Token-Preis für ${token.toUpperCase()} konnte nicht abgerufen werden: ${apiError.message}`);
      }
      
    } catch (error) {
            const errorMessage = language === 'de' 
        ? `Transaktion fehlgeschlagen: ${error.message}`
        : `Transaction failed: ${error.message}`;
      
      // Zeige Toast-Nachricht
      showErrorToast(errorMessage);
    } finally {
      setAutoTransactionLoading(false);
    }
  };

  // 🚀 NEUE FUNKTION: Krypto-Zahlung automatisch in neue Transaktion umwandeln
  const handleCryptoPayment = async (plan, year, walletCount, token, residenceCountryOverride = null) => {
    try {
                  
      // 🆕 NEU: Prüfen, ob VeWorld Browser Extension für Desktop-Zahlungen benötigt wird
      const ua = (typeof navigator !== 'undefined' && navigator.userAgent) || '';
      const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua);
      const hasVeWorldExtension =
        typeof window !== 'undefined' &&
        (window.veworld || window.vechain);

      // Für Desktop-Zahlungen benötigen wir die VeWorld Extension
      if (!isMobile && !hasVeWorldExtension) {
        const installUrl = 'https://www.veworld.net/';
        const msgDe =
          'Für diese Zahlung wird die VeWorld Browser Extension benötigt. Bitte installieren Sie die Extension und versuchen Sie es erneut.';
        const msgEn =
          'The VeWorld browser extension is required for this payment. Please install the extension and try again.';

        // Zeige Toast-Nachricht (Sprachlogik wie übrige Toasts: en vs. de)
        toast.error(language === 'en' ? msgEn : msgDe, {
          position: "top-right",
          autoClose: 5000
        });

        // Öffne Installationsseite nach 6 Sekunden (gibt User Zeit zum Lesen)
        setTimeout(() => {
          try {
            window.open(installUrl, '_blank', 'noopener,noreferrer');
          } catch (openErr) {
                      }
        }, 6000);

        return; // Flow abbrechen
      }
      
      // 🎯 WICHTIG: Token korrekt setzen bevor Transaktion startet
      setSelectedToken(token);
            
      // 🌍 NEU: GPS-Land miterheben vor dem Erstellen der Transaktion
      try {
        // GPS *erzwingen* – bei Block/Denied kommt Toast und wir werfen
        const geoResult = await getGpsCountryOrThrow();
                
        // Neue automatische Transaktion starten
        await handleAutoTransaction(plan, year, walletCount, token, geoResult, residenceCountryOverride);
      } catch (gpsError) {
        // Abbruch: wir haben bereits getoastet; NICHT weiter machen
                return;
      }
      
    } catch (error) {
            // Fallback zum alten Format
          }
  };

  // 🚀 NEU: Upgrade-Flow Handler
  const handleUpgradeClick = async () => {
    try {
      if (!upgradeMode || !upgradeDatasetData) return;

      // 🆕 NEU: Prüfen, ob VeWorld Browser Extension für Desktop-Upgrades benötigt wird
      const ua = (typeof navigator !== 'undefined' && navigator.userAgent) || '';
      const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua);
      const hasVeWorldExtension =
        typeof window !== 'undefined' &&
        (window.veworld || window.vechain);

      // Für Desktop-Upgrades benötigen wir die VeWorld Extension
      if (!isMobile && !hasVeWorldExtension) {
        const installUrl = 'https://www.veworld.net/';
        const msgDe =
          'Für dieses Upgrade wird die VeWorld Browser Extension benötigt. Bitte installieren Sie die Extension und versuchen Sie es erneut.';
        const msgEn =
          'The VeWorld browser extension is required for this upgrade. Please install the extension and try again.';

        toast.error(language === 'en' ? msgEn : msgDe, {
          position: "top-right",
          autoClose: 5000
        });

        // Öffne Installationsseite nach 6 Sekunden (gibt User Zeit zum Lesen)
        setTimeout(() => {
          try {
            window.open(installUrl, '_blank', 'noopener,noreferrer');
          } catch (openErr) {
                      }
        }, 6000);

        return; // Flow abbrechen
      }

      // ✅ Normalisiere beide Pläne vor dem API-Call (lowercase IDs)
      const originalPlan = normalizePlanId(upgradeDatasetData.plan);
      const targetPlan = normalizePlanId(plan); // aktueller Auswahl-Plan im UI
      const yearNumber = Number(year);

      
      // 1) Wallets fürs Upgrade sammeln (ähnlich handleAddWalletsToSet)
      const existingMainWallets = new Set(
        (upgradeDatasetData.existingWallets || [])
          .map(w => w.main_wallet?.toLowerCase())
          .filter(Boolean)
      );

      const walletsForSet = [...analyzedWallets];

      if (
        walletData &&
        wallet &&
        !analyzedWallets.some(w => w.wallet.toLowerCase() === wallet.toLowerCase())
      ) {
        walletsForSet.push({
          wallet: wallet,
          delegateWallet: delegateWallet || null,
          mainTxCount: walletData?.mainWalletTxCount || 0,
          veDelegateTxCount: walletData?.delegateWalletTxCount || 0,
          vet: walletData?.vet?.transactions || 0,
          vtho: walletData?.vtho?.transactions || 0,
          b3tr: walletData?.b3tr?.transactions || 0,
          nft: walletData?.nft?.transactions || 0
        });
      }

      const newWallets = walletsForSet.filter(
        w => !existingMainWallets.has((w.wallet || '').toLowerCase())
      );

      if (newWallets.length === 0) {
        toast.info(language === 'en'
          ? 'No new wallets to upgrade-add.'
          : 'Keine neuen Wallets für das Upgrade gefunden.');
        return;
      }

      // Wallet-Paare & wallet_pairs lokal vorbereiten (GENAU wie im normalen Kauf!)
      const walletPairs = newWallets.map((w) => {
        const pair = {
          main_wallet: (w.wallet || '').toLowerCase(),
          delegate_wallet: w.delegateWallet ? w.delegateWallet.toLowerCase() : null,
          label: w.label || null,
          chain: 'vechain',
        };
        if (w.mainTxCount != null) pair.main_tx_count = w.mainTxCount;
        if (w.veDelegateTxCount != null) pair.delegate_tx_count = w.veDelegateTxCount;
        if (w.vet != null) pair.vet_amount = w.vet;
        if (w.vtho != null) pair.vtho_amount = w.vtho;
        if (w.b3tr != null) pair.b3tr_amount = w.b3tr;
        if (w.nft != null) pair.nft_count = w.nft;
        if (w.appContext) pair.app_context = w.appContext;
        return pair;
      });

      // Für PaymentSuccess / VeWorld-Monitor
      localStorage.setItem('analyzedWallets', JSON.stringify(newWallets));
      localStorage.setItem('wallet_pairs', JSON.stringify(walletPairs));

      const token = selectedToken || 'vet';

      // 2) Backend-Upgrade-Prepare aufrufen
      const upgradeData = await veWorldPaymentAPI.prepareUpgradeTransaction(
        originalPlan,
        targetPlan,
        yearNumber,
        token
      );

      const priceEUR = upgradeData.priceEUR;
      const tokenPriceEUR = upgradeData.tokenPriceEUR;
      const tokenAmount = upgradeData.expectedCryptoAmount;

      
      // 3) TransactionData für Modal bauen (analog handleAutoTransaction)
      const walletAddresses = walletPairs.map(pair => ({
        main_wallet: pair.main_wallet,
        delegate_wallet: pair.delegate_wallet || null,
        label: pair.label || null,
        chain: pair.chain || 'vechain',
        main_tx_count: Number(pair.main_tx_count || 0),
        delegate_tx_count: Number(pair.delegate_tx_count || 0),
        vet_amount: Number(pair.vet_amount || 0),
        vtho_amount: Number(pair.vtho_amount || 0),
        b3tr_amount: Number(pair.b3tr_amount || 0),
        nft_count: Number(pair.nft_count || 0)
      }));

      // GPS-Ergebnis wie im normalen Flow holen
      let geoResult = null;
      try {
        geoResult = await getGpsCountryOrThrow();
      } catch (gpsErr) {
                return;
      }

      const upgradeResidenceCountry = resolveResidenceCountryForApi(country);
      if (!upgradeResidenceCountry) {
        toast.error(
          language === 'en'
            ? 'Please select your tax country before upgrading.'
            : 'Bitte wähle vor dem Upgrade dein Steuerland aus.'
        );
        return;
      }

      const transactionData = {
        token,
        tokenSymbol: token.toUpperCase(),
        value: tokenAmount.toFixed(7),
        priceEUR,
        tokenPriceEUR,
        to: getVeWorldTargetAddress(), // 🚀 FIX: Nutzt Hostname/ENV und gibt in Staging die Staging-Wallet zurück
        plan: targetPlan,
        year: yearNumber,
        walletCount: walletAddresses.length,
        walletAddresses,
        residenceCountry: upgradeResidenceCountry,
        geoResult,
        // 🚀 NEU: Upgrade-Informationen
        isUpgrade: true,
        upgradeFromPlan: originalPlan,
        upgradeBasePurchaseId: upgradeDatasetData?.id || upgradeDatasetData?.datasetId || null // 👈 WICHTIG
      };

      setAutoTransactionData(transactionData);
      setShowAutoTransactionModal(true);

          } catch (err) {
            toast.error(language === 'en'
        ? 'Upgrade could not be prepared.'
        : 'Upgrade konnte nicht vorbereitet werden.');
    }
  };

  // 🚀 NEUE FUNKTION: VeWorld Wallet öffnen und Transaktion starten
  const handleStartVeWorldTransaction = async () => {
    try {
      if (!autoTransactionData) return;
      
            
      // 🆕 NEU: Prüfen, ob VeWorld Browser Extension installiert ist
      // Nur relevant für Desktop-User, die NICHT über WalletConnect kommen
      const ua = (typeof navigator !== 'undefined' && navigator.userAgent) || '';
      const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua);

      const hasVeWorldExtension =
        typeof window !== 'undefined' &&
        (window.veworld || window.vechain);

      // source kommt aus useWallet() oben in App():
      // const { wallet, chainId, source, setSource } = useWallet();
      // Prüfe: Desktop + kein WalletConnect + keine Extension
      // (source !== 'wallet-connect' funktioniert auch wenn source undefined ist)
      if (!isMobile && source !== 'wallet-connect' && !hasVeWorldExtension) {
        // Offizielle VeWorld Download-Seite (Desktop + Browser Extensions)
        const installUrl = 'https://www.veworld.net/';

        const msgDe =
          'VeWorld Browser Extension wurde in deinem Browser nicht gefunden.\n' +
          'Bitte installiere zuerst die VeWorld Browser Extension (es wurde ein neuer Tab geöffnet) ' +
          'und starte danach die Transaktion erneut.';

        const msgEn =
          'VeWorld browser extension was not detected in your browser.\n' +
          'Please install the VeWorld browser extension first (a new tab has been opened) ' +
          'and then start the transaction again.';

        setAutoTransactionError(language === 'de' ? msgDe : msgEn);

        try {
          // Direkt zur Installationsseite leiten (Neuer Tab)
          window.open(installUrl, '_blank', 'noopener,noreferrer');
        } catch (openErr) {
                  }

        // 🔚 WICHTIG: Flow hier abbrechen – keine Pending Order, keine Transaktion
        return;
      }
      
      // 🚀 WICHTIG: Erstelle Pending Order erst JETZT beim Klick auf "Transaktion starten"
            
      // 🚀 WICHTIG: transactionDataWithPaymentId außerhalb des try Blocks definieren (für Verwendung danach)
      let transactionDataWithPaymentId = { ...autoTransactionData };
      
      try {
        // Wallet-Adressen: bevorzugt aus autoTransactionData (Flow 2 => []), sonst localStorage Fallback (Flow 1)
        // 🚀 FIX: Bei Plan-Upgrade (Flow 2) KEIN Fallback auf localStorage!
        let walletAddresses = [];
        if (autoTransactionData?.isUpgrade) {
          // Flow 2: Reines Plan-Upgrade - KEINE Wallets
          walletAddresses = Array.isArray(autoTransactionData?.walletAddresses) 
            ? autoTransactionData.walletAddresses 
            : [];
        } else {
          // Flow 1: Add Wallet + Upgrade - Wallets aus autoTransactionData oder localStorage
          walletAddresses = Array.isArray(autoTransactionData?.walletAddresses)
            ? autoTransactionData.walletAddresses
            : (JSON.parse(localStorage.getItem('wallet_pairs') || '[]')).map(pair => ({
                main_wallet: pair.main_wallet,
                delegate_wallet: pair.delegate_wallet ?? null,
                label: pair.label ?? null,
                chain: pair.chain || 'vechain',
                main_tx_count: Number(pair.main_tx_count || 0),
                delegate_tx_count: Number(pair.delegate_tx_count || 0),
                vet_amount: Number(pair.vet_amount || 0),
                vtho_amount: Number(pair.vtho_amount || 0),
                b3tr_amount: Number(pair.b3tr_amount || 0),
                nft_count: Number(pair.nft_count || 0)
              }));
        }
        
      if (!autoTransactionData.residenceCountry) {
        throw new Error('Missing residence country for pending order.');
      }

                const pendingOrderResponse = await fetch(apiUrl('/api/veworld/create-pending-order'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            plan: autoTransactionData.plan,
            year: autoTransactionData.year,
            walletCount: walletAddresses.length,
            priceEUR: autoTransactionData.priceEUR,
            preferredToken: autoTransactionData.token,
            paymentAmounts: {
              [autoTransactionData.token]: autoTransactionData.value
            },
            walletAddresses: walletAddresses,
            residenceCountry: autoTransactionData.residenceCountry,
            geoResult: autoTransactionData.geoResult || null,
            // 🚀 NEU: Upgrade-Informationen
            isUpgrade: !!autoTransactionData.isUpgrade,
            upgradeFromPlan: autoTransactionData.upgradeFromPlan || null,
            upgradeToPlan: autoTransactionData.plan || null,
            upgradeBasePurchaseId: autoTransactionData.upgradeBasePurchaseId || null
          }),
          credentials: 'include'
        });

                        
        let paymentId = null;
        if (pendingOrderResponse.ok) {
          const pendingOrderData = await pendingOrderResponse.json();
                    paymentId = pendingOrderData.paymentId;
          const serverCryptoAmount =
            pendingOrderData.expectedCryptoAmount ??
            pendingOrderData.paymentAmounts?.[autoTransactionData.token];
                                        
          // 🚀 Failsafe: paymentId zusätzlich im localStorage ablegen
          localStorage.setItem('currentPaymentId', String(paymentId));
                    
          // 🚀 WICHTIG: paymentId direkt in transactionDataWithPaymentId schreiben (für sofortige Verwendung)
          // WICHTIG: Verwende autoTransactionData direkt, nicht den State nach setAutoTransactionData!
          transactionDataWithPaymentId = {
            ...autoTransactionData,
            paymentId: paymentId,
            ...(serverCryptoAmount != null
              ? { value: String(serverCryptoAmount) }
              : {}),
          };
          
                              
          // 🚀 WICHTIG: Speichere paymentId auch in autoTransactionData State für späteren Gebrauch
          setAutoTransactionData({
            ...autoTransactionData,
            paymentId: paymentId,
            ...(serverCryptoAmount != null
              ? { value: String(serverCryptoAmount) }
              : {}),
          });
          
          // Setze started_at für präziseres Zeitfenster
          try {
            await fetch(apiUrl('/api/veworld/start-pending-order'), {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({ paymentId }),
              credentials: 'include'
            });
                      } catch (startError) {
                      }
        } else {
          const errorText = await pendingOrderResponse.text();
          let errMsg =
            language === 'de'
              ? 'Bestellung konnte nicht erstellt werden.'
              : 'Could not create order.';
          try {
            const errJson = JSON.parse(errorText);
            if (errJson.error === 'PRICE_MISMATCH') {
              errMsg =
                language === 'de'
                  ? 'Der Preis hat sich geändert. Bitte Seite neu laden und erneut versuchen.'
                  : 'Price has changed. Please reload the page and try again.';
            } else if (errJson.message) {
              errMsg = errJson.message;
            }
          } catch {
            /* ignore parse errors */
          }
          setAutoTransactionError(errMsg);
          showErrorToast(errMsg);
          veWorldFlowActiveRef.current = false;
          return;
        }
        
      } catch (pendingError) {
        const errMsg =
          language === 'de'
            ? `Bestellung konnte nicht erstellt werden: ${pendingOrderError?.message || 'Unbekannter Fehler'}`
            : `Could not create order: ${pendingOrderError?.message || 'Unknown error'}`;
        setAutoTransactionError(errMsg);
        showErrorToast(errMsg);
        veWorldFlowActiveRef.current = false;
        return;
      }
      
      // 🎯 WICHTIG: Event Listener für Extension-Schließung hinzufügen
      const handleExtensionClose = async () => {
        // ❗ nichts tun, wenn gar kein VeWorld-Flow aktiv ist
        if (!veWorldFlowActiveRef.current) {
                    return;
        }

        
        // Listener IMMER sauber entfernen
        document.removeEventListener('visibilitychange', handleVisibilityChange);
        window.removeEventListener('focus', handleFocusChange);
        // ❌ diese beiden gab es zuvor doppelt (entfernt)
        // window.removeEventListener('pageshow', handlePageShow);
        // window.removeEventListener('focus', handlePageFocus);

        // Polling stoppen
        if (checkExtensionStatus) {
          clearInterval(checkExtensionStatus);
        }

        // Flags zurücksetzen
        veWorldFlowActiveRef.current = false;

        // Modal/State zurücksetzen
        setShowAutoTransactionModal(false);
        setAutoTransactionData(null);
        setAutoTransactionSuccess(false);
        setAutoTransactionError(null);

        // ✅ Nur wenn wir wirklich erfolgreich gekauft haben → neu laden
        if (reloadOnCloseRef.current) {
          // 🚀 WICHTIG: Setze Success-Flag für Toast nach Neuladung
          // Berechne korrekte Wallet-Anzahl aus localStorage (Main + VeDelegate)
          const analyzedWallets = JSON.parse(localStorage.getItem('analyzedWallets') || '[]');
          let totalWalletCount = analyzedWallets.length; // Main Wallets
          
          // Zähle auch VeDelegate-Wallets mit
          analyzedWallets.forEach(wallet => {
            if (wallet.delegateWallet) {
              totalWalletCount++; // VeDelegate-Wallet hinzufügen
            }
          });
          
          // 🚀 NEU: Prüfe ob es ein Upgrade ist
          const isUpgrade = !!autoTransactionData?.isUpgrade;
          
          let message;
          if (isUpgrade) {
            // Upgrade-spezifische Nachricht (gleiche Sprachlogik wie übrige Toasts: en vs. de)
            const planDisplay = autoTransactionData?.plan ? formatPlanDisplay(autoTransactionData.plan) : null;
            message = language === 'en'
              ? `Upgrade successful! Plan has been updated to ${planDisplay || 'new plan'}.`
              : `Upgrade erfolgreich! Plan wurde auf ${planDisplay || 'neuen Plan'} aktualisiert.`;
          } else {
            message = language === 'en'
              ? `${totalWalletCount} wallet${totalWalletCount > 1 ? 's' : ''} were successfully purchased! Calculation can be started under "My Wallets".`
              : `${totalWalletCount} Wallet${totalWalletCount > 1 ? 's' : ''} wurden erfolgreich gekauft! Berechnung kann unter "Meine Wallets" gestartet werden.`;
          }
          
          localStorage.setItem(
            'purchaseSuccess',
            JSON.stringify({
              success: true,
              message: message,
              timestamp: Date.now(),
              isUpgrade: isUpgrade,
              year: autoTransactionData?.year || year || null,
              plan: autoTransactionData?.plan || plan || null,
            }),
          );
          
          // 🚀 NEU: Bei Upgrade: Nur Daten neu laden, kein vollständiger Reload
          if (isUpgrade) {
                        await reloadDatasetsAndPlanInfo();
            
            // Zeige Toast
            toast.success(message, {
              position: "top-right",
              autoClose: 5000
            });
          } else {
            // Bei normalem Kauf: Vollständiger Reload (wie bisher)
                        window.location.reload();
          }
        }
        
        // Reset reload flag (nach möglichem Reload)
        reloadOnCloseRef.current = false;
      };

      // 🚀 FIX: Listener nur aktivieren wenn VeWorld-Flow startet
      veWorldFlowActiveRef.current = true;
      reloadOnCloseRef.current = false;

      // 🚀 Event Listener für Visibility Change (wenn Extension geöffnet wird)
      const handleVisibilityChange = () => {
        if (!veWorldFlowActiveRef.current) return; // 🚀 FIX: Nur reagieren wenn Flow aktiv
        
        if (document.hidden) {
                    // Extension wurde geöffnet - warte auf Rückkehr
        } else {
                    // App ist wieder sichtbar - Extension wurde geschlossen
          // Keine Verzögerung mehr - sofort reagieren
          handleExtensionClose();
        }
      };

      // 🚀 Event Listener für Focus Change (wenn Extension geschlossen wird)
      const handleFocusChange = () => {
        if (!veWorldFlowActiveRef.current) return; // 🚀 FIX: Nur reagieren wenn Flow aktiv
        
        if (document.hasFocus()) {
                    // App hat wieder den Fokus - Extension wurde geschlossen
          // Keine Verzögerung mehr - sofort reagieren
          handleExtensionClose();
        }
      };

      // 🚀 FIX: Nur die zwei nötigen Event Listener (pageshow und doppeltes focus entfernt)
      document.addEventListener('visibilitychange', handleVisibilityChange);
      window.addEventListener('focus', handleFocusChange);
      
      // 🚀 Zusätzlich: Polling für schnelle Erkennung
      let isExtensionOpen = false;
      const checkExtensionStatus = setInterval(() => {
        if (!veWorldFlowActiveRef.current) {
          // 🚀 FIX: Flow nicht aktiv - Polling stoppen
          clearInterval(checkExtensionStatus);
          return;
        }
        
        if (document.hidden) {
          isExtensionOpen = true;
        } else if (isExtensionOpen) {
                    isExtensionOpen = false;
          clearInterval(checkExtensionStatus);
          handleExtensionClose();
        }
      }, 100); // Alle 100ms prüfen
      
      // 🎯 WICHTIG: Verwende die ursprüngliche veWorldPaymentAPI für die Transaktion!
      try {
                
        // 🚀 WICHTIG: Importiere veWorldPaymentAPI dynamisch
        const { veWorldPaymentAPI } = await import('./api');
        
        // 🚀 WICHTIG: Verwende transactionDataWithPaymentId (enthält paymentId) statt autoTransactionData
        const transactionDataToUse = transactionDataWithPaymentId || autoTransactionData;
        
        // 🚀 KRITISCH: Debug-Logs um zu sehen, was übergeben wird
                                                                
        // 🚀 VeWorld Wallet öffnen und Transaktion starten
        const result = await veWorldPaymentAPI.openVeWorldTransaction(transactionDataToUse);
        
        if (result.method === 'direct') {
          // Direkte VeWorld Extension Transaktion
                    setAutoTransactionSuccess(true);
          
          // 🚀 FIX: Reload nur bei erfolgreichen direkten Transaktionen
          reloadOnCloseRef.current = true;
          
          // 🎯 WICHTIG: Bei direkten Transaktionen warten wir auf Extension-Schließung
          // Der Event Listener wird den Modal automatisch schließen und App neu laden
        } else if (result.method === 'walletConnect') {
          // WalletConnect Transaktion
                    setAutoTransactionSuccess(true);
          
          // 🚀 FIX: Reload nur bei erfolgreichen WalletConnect Transaktionen
          reloadOnCloseRef.current = true;
          
          // 🎯 WICHTIG: Bei WalletConnect warten wir auch auf Extension-Schließung
          // Der Event Listener wird den Modal automatisch schließen
        } else if (result.method === 'deepLink') {
          // VeWorld Mobile App Deep Link gesendet
                    setAutoTransactionSuccess(true);
          
          // 🚀 FIX: Flow beenden bei Deep Links (kein Reload erforderlich)
          veWorldFlowActiveRef.current = false;
          
          // 🎯 Bei Deep Links schließen wir das Modal nach 3 Sekunden
          setTimeout(() => {
            setShowAutoTransactionModal(false);
            setAutoTransactionData(null);
            setAutoTransactionSuccess(false);
            // Event Listener entfernen
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            window.removeEventListener('focus', handleFocusChange);
          }, 3000);
        } else if (result.method === 'website') {
          // VeWorld Website geöffnet
                    setAutoTransactionSuccess(true);
          
          // 🚀 FIX: Flow beenden bei Website (kein Reload erforderlich)
          veWorldFlowActiveRef.current = false;
          
          // 🎯 Bei Website-Öffnung schließen wir das Modal nach 3 Sekunden
          setTimeout(() => {
            setShowAutoTransactionModal(false);
            setAutoTransactionData(null);
            setAutoTransactionSuccess(false);
            // Event Listener entfernen
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            window.removeEventListener('focus', handleFocusChange);
          }, 3000);
        } else if (result.method === 'qrCode') {
          // QR Code für Desktop angezeigt
                    setAutoTransactionSuccess(true);
          // QR Code Daten für Anzeige setzen
          setAutoTransactionData({
            ...autoTransactionData,
            method: 'qrCode',
            qrCodeData: result.uri,
            message: result.message
          });
          
          // 🚀 FIX: Flow beenden bei QR-Code (kein Reload erforderlich)
          veWorldFlowActiveRef.current = false;
          
          // 🎯 Bei QR-Code bleibt das Modal offen - Event Listener entfernen
          document.removeEventListener('visibilitychange', handleVisibilityChange);
          window.removeEventListener('focus', handleFocusChange);
        } else if (result.method === 'error') {
          // Fehler bei VeWorld-Integration
                    const errorMessage = language === 'de' 
            ? `VeWorld Fehler: ${result.message}`
            : `VeWorld Error: ${result.message}`;
          setAutoTransactionError(errorMessage);
          setAutoTransactionSuccess(false);
          
          // 🚀 FIX: Flow beenden bei Fehlern (kein Reload erforderlich)
          veWorldFlowActiveRef.current = false;
          
          // 🎯 Bei Fehlern schließen wir das Modal nach 3 Sekunden
          setTimeout(() => {
            setShowAutoTransactionModal(false);
            setAutoTransactionData(null);
            setAutoTransactionSuccess(false);
            setAutoTransactionError(null);
            // Event Listener entfernen
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            window.removeEventListener('focus', handleFocusChange);
          }, 3000);
        } else if (result.method === 'manual') {
          // Manuelle Zahlungsanleitung
                    setAutoTransactionSuccess(true);
          
          // 🚀 FIX: Flow beenden bei manuellen Zahlungen (kein Reload erforderlich)
          veWorldFlowActiveRef.current = false;
          
          // 🎯 Bei manuellen Zahlungen bleibt das Modal offen - Event Listener entfernen
          document.removeEventListener('visibilitychange', handleVisibilityChange);
          window.removeEventListener('focus', handleFocusChange);
        } else {
          // Direkte Transaktion gestartet
                    setAutoTransactionSuccess(true);
          
          // 🚀 FIX: Reload auch bei erfolgreichen direkten Transaktionen (Fallback)
          reloadOnCloseRef.current = true;
          
          // 🎯 WICHTIG: Bei direkten Transaktionen warten wir auf Extension-Schließung
          // Der Event Listener wird den Modal automatisch schließen
        }
        
        // 🎯 Modal NICHT automatisch schließen - User muss es manuell schließen
        // Nur bei direkten Transaktionen automatisch schließen
        if (result.method === 'direct' || result.method === 'walletConnect') {
          setTimeout(() => {
            setShowAutoTransactionModal(false);
            setAutoTransactionData(null);
            setAutoTransactionSuccess(false);
          }, 3000);
        }
        // Bei QR-Code und manuellen Zahlungen bleibt das Modal offen
        
      } catch (apiError) {
                
        // 🎯 Event Listener entfernen bei Fehlern
        document.removeEventListener('visibilitychange', handleVisibilityChange);
        window.removeEventListener('focus', handleFocusChange);
        
        // 🎯 Fallback: Direkte DAppKit-Integration für VeWorld
        try {
          // 🚀 1. VeWorld als Quelle setzen
                    setSource('veworld');
          
          // 🚀 2. Mit VeWorld verbinden
                    const connectionResult = await connect();
          
          if (!connectionResult?.address) {
            throw new Error('VeWorld Wallet konnte nicht verbunden werden');
          }
          
                    
          // 🚀 3. Transaktion anfordern
                    
          let clauses;
          
          if (autoTransactionData.token === 'vet') {
            // VET-Transaktion
            clauses = [{
              to: autoTransactionData.to,
              value: autoTransactionData.value,
              data: '0x'
            }];
          } else if (autoTransactionData.token === 'b3tr' || autoTransactionData.token === 'vtho') {
            // Token-Transaktion (ERC-20)
            // 🎯 WICHTIG: Für B3TR und VTHO müssen wir die Token-Contract-Adresse verwenden
            const tokenAddress = autoTransactionData.token === 'b3tr' 
              ? '0x0000000000000000000000000000000000000000' // B3TR Contract (Beispiel)
              : '0x0000000000000000000000000000000000000000'; // VTHO Contract (Beispiel)
            
            // 🚀 ERC-20 Transfer ABI
            const erc20Transfer = [
              { name: 'transfer', type: 'function', stateMutability: 'nonpayable',
                inputs: [{ name: 'to', type: 'address' }, { name: 'amount', type: 'uint256' }], 
                outputs: [{ type: 'bool' }] }
            ];
            
            // 🚀 Encode function data
            const functionData = encodeFunctionData({ 
              abi: erc20Transfer, 
              args: [autoTransactionData.to, autoTransactionData.value] 
            });
            
            clauses = [{
              to: tokenAddress,
              value: '0x0',
              data: functionData
            }];
          }
          
          if (!clauses) {
            throw new Error('Transaktionsdaten konnten nicht erstellt werden');
          }
          
                    
          // 🚀 Transaktion anfordern
          const txResult = await requestTransaction({ 
            clauses, 
            comment: `v3tax ${autoTransactionData.token.toUpperCase()} purchase` 
          });
          
                    setAutoTransactionSuccess(true);
          
          // 🚀 WICHTIG: Versuche Extension automatisch zu schließen
          try {
            // Versuche die Extension zu schließen, indem wir die Seite fokussieren
            window.focus();
            
            // Versuche die Extension zu schließen, indem wir die Seite aktivieren
            if (document.hidden) {
              document.dispatchEvent(new Event('visibilitychange'));
            }
            
            // 🎯 NEU: Versuche Extension über DAppKit zu schließen
            if (window.connex && window.connex.thor) {
              try {
                // Versuche die Extension zu schließen, indem wir die Transaktion als erfolgreich markieren
                                
                // Versuche die Extension zu schließen, indem wir die Transaktion als erfolgreich markieren
                // Das sollte die Extension automatisch schließen
                setTimeout(() => {
                                  }, 1000);
              } catch (dappKitError) {
                              }
            }
            
                      } catch (error) {
                      }
          
          // 🚀 FIX: Flow beenden bei DAppKit-Transaktionen (kein Reload erforderlich, da direkt im setTimeout)
          veWorldFlowActiveRef.current = false;
          
          // 🎯 Modal nach 3 Sekunden schließen und App neu laden
          setTimeout(() => {
            setShowAutoTransactionModal(false);
            setAutoTransactionData(null);
            setAutoTransactionSuccess(false);
            // Event Listener entfernen
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            window.removeEventListener('focus', handleFocusChange);
            
            // 🚀 WICHTIG: Setze Success-Flag für Toast nach Neuladung
            // Berechne korrekte Wallet-Anzahl aus localStorage (Main + VeDelegate)
            const analyzedWallets = JSON.parse(localStorage.getItem('analyzedWallets') || '[]');
            let totalWalletCount = analyzedWallets.length; // Main Wallets
            
            // Zähle auch VeDelegate-Wallets mit
            analyzedWallets.forEach(wallet => {
              if (wallet.delegateWallet) {
                totalWalletCount++; // VeDelegate-Wallet hinzufügen
              }
            });
            let message;
            if (language === 'de') {
              message = `${totalWalletCount} Wallet${totalWalletCount > 1 ? 's' : ''} wurden erfolgreich gekauft! Berechnung kann unter "Meine Wallets" gestartet werden.`;
            } else {
              message = `${totalWalletCount} wallet${totalWalletCount > 1 ? 's' : ''} were successfully purchased! Calculation can be started under "My Wallets".`;
            }
            
            localStorage.setItem('purchaseSuccess', JSON.stringify({
              success: true,
              message: message,
              timestamp: Date.now()
            }));
            
            // 🚀 WICHTIG: App neu laden nach erfolgreichem VeWorld-Kauf (wie bei Stripe!)
                        window.location.reload();
          }, 3000);
          
        } catch (dappKitError) {
                    
          // 🎯 Fallback: Zeige Fehlermeldung
          const errorMessage = language === 'de' 
            ? `VeWorld Transaktion fehlgeschlagen: ${dappKitError.message}`
            : `VeWorld Transaction failed: ${dappKitError.message}`;
          setAutoTransactionError(errorMessage);
          setAutoTransactionSuccess(false);
          
          // 🎯 Modal nach 3 Sekunden schließen
          setTimeout(() => {
            setShowAutoTransactionModal(false);
            setAutoTransactionData(null);
            setAutoTransactionSuccess(false);
            setAutoTransactionError(null);
            // Event Listener entfernen
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            window.removeEventListener('focus', handleFocusChange);
          }, 3000);
        }
      }
      
    } catch (error) {
            const errorMessage = language === 'de' 
        ? `VeWorld Transaktion fehlgeschlagen: ${error.message}`
        : `VeWorld Transaction failed: ${error.message}`;
      setAutoTransactionError(errorMessage);
    }
  };
  
  // 🎉 Show notification when user logs in (deaktiviert - wird jetzt manuell getriggert)
  // useEffect(() => {
  //   if (isAuthenticated && user) {
  //     setShowWalletConnectedNotification(true);
  //     // Auto-hide after 5 seconds (länger)
  //     const timer = setTimeout(() => {
  //       setShowWalletConnectedNotification(false);
  //     }, 5000);
  //     return () => clearTimeout(timer);
  //   }
  // }, [isAuthenticated, user]);
  
  const [supportForm, setSupportForm] = useState({
    email: '',
    category: '',
    description: '',
    urgent: false,
    walletAddress: '',
    consent: false,
    calculationErrorConsent: false
  });

  const handleHelpModalClose = useCallback(() => {
    const closeHandler =
      typeof window !== 'undefined' ? window._supportModalCloseHandler : null;

    if (typeof closeHandler === 'function') {
      closeHandler();
      return;
    }

    setShowHelpModal(false);
  }, []);

  useEffect(() => {
    return () => {
      if (typeof window !== 'undefined') {
        window._supportModalCloseHandler = null;
      }
    };
  }, []);

  // Draggable modal states
  // 🚀 Drag Hook für Plan Modal
  const dragPlan = useDraggable();
  
  const [delegateCandidates, setDelegateCandidates] = useState([]);
  const [buttonMode, setButtonMode] = useState("analyze"); // "analyze", "buy", "dashboard", "addWalletToSet", ...
  const [buyPreviewMode, setBuyPreviewMode] = useState("preview"); // "buy" | "preview"
  const [upgradeMode, setUpgradeMode] = useState(false); // 🚀 Flag für Upgrade-Modus
  const [ctaNudge, setCtaNudge] = useState(false); // ✅ NEU: Nudge-Animation für CTA-Button
  const [buyPulse, setBuyPulse] = useState(false); // ✅ NUR für "Freischalten → Return": BUY Button Pulse
  const ctaRef = useRef(null); // ✅ NEU: Ref für CTA-Button (Premium-Ankunft)

  // ✅ Toggle immer anzeigen (aber ggf. disabled)
  const showBuyPreviewToggle = true;
  
  const [isNewAnalysis, setIsNewAnalysis] = useState(false); // 🚀 Flag für neue Analyse
  const [upgradeDatasetData, setUpgradeDatasetData] = useState(null); // 🚀 Dataset-Daten für Upgrade-Modus
  const [isUpgradeAnalyzing, setIsUpgradeAnalyzing] = useState(false); // 🚀 Loading-State für Upgrade-Modus

  const location = useLocation();
  const initialReturnNavigationRef = useRef(null);
  if (initialReturnNavigationRef.current === null) {
    const navState = location?.state || {};
    let isReturnNav = !!(navState?.restoreDraft || navState?.skipLandingIntro);
    if (!isReturnNav) {
      try {
        isReturnNav = !!sessionStorage.getItem("v3tax:return_intent");
      } catch (_) {}
    }
    initialReturnNavigationRef.current = isReturnNav;
  }
  const isReturnMount = initialReturnNavigationRef.current;
  
  // 🚀 Initialisierung: Button-Mode beim Start auf "analyze" setzen (aber nicht bei Restore-Return)
  useEffect(() => {
    const navState = location?.state || {};
    const hasReturnIntent = !!sessionStorage.getItem("v3tax:return_intent");
    const isRestoreReturn = !!navState.restoreDraft || hasReturnIntent;

    if (!isRestoreReturn) {
      setButtonMode("analyze");
          } else {
          }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Nur beim Mount
  const [wallet, setWallet] = useState("");
  const [submittedWallet, setSubmittedWallet] = useState("");
  const [walletData, setWalletData] = useState(null);
  const [error, setError] = useState("");
  const [country, setCountry] = useState(""); // Kein Standard, User muss wählen
  const [paymentMethod, setPaymentMethod] = useState("");
  const [year, setYear] = useState("");
  const [plan, setPlan] = useState("");

  // ✅ Free-Year (2024) robust erkennen (auch wenn year als Number kommt, z.B. Restore/Upgrade-State)
  const isFreeYear = String(year) === "2024";

  const [landingIntroReady, setLandingIntroReady] = useState(isReturnMount);
  const [landingReturnPhase, setLandingReturnPhase] = useState(
    isReturnMount ? "hold" : "idle"
  );

  const landingReturnRafARef = useRef(0);
  const landingReturnRafBRef = useRef(0);
  const landingReturnTimerRef = useRef(0);

  const triggerLandingReturnReveal = useCallback(() => {
    if (typeof window === "undefined") {
      setLandingReturnPhase("idle");
      return;
    }

    window.cancelAnimationFrame(landingReturnRafARef.current);
    window.cancelAnimationFrame(landingReturnRafBRef.current);
    window.clearTimeout(landingReturnTimerRef.current);

    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) {
      setLandingReturnPhase("idle");
      return;
    }

    setLandingReturnPhase("hold");

    landingReturnRafARef.current = window.requestAnimationFrame(() => {
      landingReturnRafBRef.current = window.requestAnimationFrame(() => {
        setLandingReturnPhase("reveal");

        landingReturnTimerRef.current = window.setTimeout(() => {
          setLandingReturnPhase("idle");
        }, 180);
      });
    });
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") {
      setLandingIntroReady(true);
      setLandingReturnPhase("idle");
      return;
    }

    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) {
      setLandingIntroReady(true);
      setLandingReturnPhase("idle");
      return;
    }

    if (isReturnMount) {
      setLandingIntroReady(true);
      return;
    }

    let rafA = 0;
    let rafB = 0;

    setLandingIntroReady(false);

    rafA = window.requestAnimationFrame(() => {
      rafB = window.requestAnimationFrame(() => {
        setLandingIntroReady(true);
      });
    });

    return () => {
      window.cancelAnimationFrame(rafA);
      window.cancelAnimationFrame(rafB);
    };
  }, [isReturnMount]);

  useEffect(() => {
    return () => {
      if (typeof window === "undefined") return;
      window.cancelAnimationFrame(landingReturnRafARef.current);
      window.cancelAnimationFrame(landingReturnRafBRef.current);
      window.clearTimeout(landingReturnTimerRef.current);
    };
  }, []);

  const getLandingIntroClass = (extra = "") => {
    if (isReturnMount) return extra.trim();
    return `landing-intro-stage ${landingIntroReady ? "landing-intro-stage--visible" : ""} ${extra}`.trim();
  };

  const landingReturnShellClass =
    landingReturnPhase === "hold"
      ? "landing-return-shell landing-return-shell--hold"
      : landingReturnPhase === "reveal"
        ? "landing-return-shell landing-return-shell--reveal"
        : "";

  // ✅ NEU: Draft aus sessionStorage wiederherstellen (mit Intent-Fallback)
  useEffect(() => {
    const navState = location?.state || {};
    const intent = navState?.restoreDraft
      ? (navState?.openBuy ? "buy" : "back")
      : (sessionStorage.getItem("v3tax:return_intent") || "");

    const shouldRestore = navState?.restoreDraft || !!intent;
    if (!shouldRestore) {
            return;
    }

    const raw = sessionStorage.getItem("v3tax:draft_wallet_set");
    if (!raw) {
      if (isReturnMount) {
        triggerLandingReturnReveal();
      }
      return;
    }

    try {
      const draft = JSON.parse(raw);
      const wallets = Array.isArray(draft?.analyzedWallets) ? draft.analyzedWallets : [];
      
      
      // ✅ FIX: Jahr-Reset-Effekt beim Draft-Restore unterdrücken (muss VOR setYear passieren)
      try {
        sessionStorage.setItem("v3tax:suppress_year_reset", "1");
      } catch (_) {}

      setWallet(draft?.wallet || "");
      setDelegateWallet(draft?.delegateWallet || null);
      if (draft?.year) setYear(String(draft.year));
      if (draft?.plan) setPlan(normalizePlanId(draft.plan));
      if (draft?.country) setCountry(draft.country);
      if (draft?.paymentMethod) setPaymentMethod(draft.paymentMethod);

      setAnalyzedWallets(wallets);
      
      // ✅ FIX: frozenTxTotal/globalTotals sauber wiederherstellen (sonst TX-LIMIT = 0/2500)
      const computedFrozenTx = wallets.reduce((sum, w) => {
        const main = Number(w.mainTxCount ?? w.main_tx_count ?? 0);
        const del  = Number(w.veDelegateTxCount ?? w.delegate_tx_count ?? w.delegateTxCount ?? 0);
        return sum + main + del;
      }, 0);

      const computedTotals = wallets.reduce((acc, w) => ({
        vet:  acc.vet  + Number(w.vet  ?? 0),
        vtho: acc.vtho + Number(w.vtho ?? 0),
        b3tr: acc.b3tr + Number(w.b3tr ?? 0),
        nft:  acc.nft  + Number(w.nft  ?? 0),
      }), { vet: 0, vtho: 0, b3tr: 0, nft: 0 });

      // Draft bevorzugen, fallback = computed
      const restoredFrozenTx = (typeof draft?.frozenTxTotal === "number") ? draft.frozenTxTotal : computedFrozenTx;
      const restoredTotals   = (draft?.globalTotals && typeof draft.globalTotals === "object") ? draft.globalTotals : computedTotals;

      setFrozenTxTotal(restoredFrozenTx);
      setGlobalTotals(restoredTotals);

      // optional aber stabil: totalTransactionCount sofort korrekt setzen (wird danach eh vom Effect gepflegt)
      setTotalTransactionCount(restoredFrozenTx);

      setButtonMode("buy");
      setError("");

      // ✅ Optional: localStorage für sofortige UI-Konsistenz
      try {
        localStorage.setItem("analyzedWallets", JSON.stringify(wallets));

        // ✅ Wichtig für Buy-/Payment-/Monitoring-Flow: wallet_pairs konsistent halten
        const walletPairs = wallets.map((w) => ({
          main_wallet: w.wallet,
          delegate_wallet: w.delegateWallet || null,
          label: w.label || null,
          chain: "vechain",
          main_tx_count: w.mainTxCount || 0,
          delegate_tx_count: w.veDelegateTxCount || 0,
          vet_amount: w.vet || 0,
          vtho_amount: w.vtho || 0,
          b3tr_amount: w.b3tr || 0,
          nft_count: w.nft || 0,
        }));
        localStorage.setItem("wallet_pairs", JSON.stringify(walletPairs));
      } catch (_) {}

      // Default: so wie vor Preview
      setBuyPreviewMode(draft?.buyPreviewMode || "preview");

      const openBuy = navState?.openBuy || intent === "buy";
      const scrollToCta = navState?.scrollToCta || openBuy;

      if (openBuy) {
        // ✅ "Freischalten": Toggle auf "Kaufen" (ohne Payment-Method Default!)
        setBuyPreviewMode("buy");
        // ❌ KEIN Auto-Set: User muss Payment-Method selbst wählen
        // ✅ Force-CTA auf BUY setzen (verhindert Überschreibung durch andere Effects)
        setForceCta('buy');
      } else {
        // ✅ "Zurück": Force-CTA löschen
        setForceCta(null);
      }
      // ✅ "Zurück": Toggle bleibt auf "Vorschau" (wird oben schon gesetzt)

      if (scrollToCta && openBuy) {
        // ✅ "Freischalten": Auto-Scroll zum CTA (Morph bleibt wie gehabt)
        setTimeout(() => {
          ctaRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
          const buttonElement = ctaRef.current?.querySelector('[data-cta-button]');
          buttonElement?.focus?.();

          // ✅ NUR nach Freischalten: BUY-Button pulsen (mehrfach)
          let shouldPulse = false;
          try {
            shouldPulse = sessionStorage.getItem("v3tax:buy_pulse") === "1";
            if (shouldPulse) sessionStorage.removeItem("v3tax:buy_pulse");
          } catch (_) {}

          if (shouldPulse) {
            setBuyPulse(true);
            setTimeout(() => setBuyPulse(false), 2800); // ~3 Pulse
          }
        }, 80);
      }
      // ✅ "Zurück": kein Auto-Scroll (User ist wieder "im Builder")

      if (isReturnMount) {
        triggerLandingReturnReveal();
      }

      sessionStorage.removeItem("v3tax:return_intent");
      
      // Router-State leeren (kein Re-Run) - ABER: Warte kurz, damit State gesetzt ist
      if (navState?.restoreDraft) {
        setTimeout(() => {
          navigate(location.pathname, { replace: true, state: {} });
                  }, 100);
      }
    } catch (e) {
      if (isReturnMount) {
        triggerLandingReturnReveal();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location?.key]);

  useEffect(() => {
    if (!location.state?.fromAnalysisCrash) return;

    if (location.state?.year) {
      setYear(String(location.state.year));
    }

    if (location.state?.selectedCountry) {
      setCountry(String(location.state.selectedCountry).toUpperCase());
    }

    if (location.state?.openMyWalletsDashboard) {
      setShowMyWalletsDashboard(true);
    }
  }, [location.state]);

  const [currentPaymentId, setCurrentPaymentId] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const [transactionCount, setTransactionCount] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const isBuyCtaVisible = buttonMode === "buy";
  const isToggleInteractive = isBuyCtaVisible && !upgradeMode && !(isAnalyzing || isUpgradeAnalyzing);

  // 📊 Compare-Plans Modal (inline in App.jsx): Early-Adopter Banner helpers
  const paidPlanIds = ['standard', 'pro', 'elite', 'ultimate'];
  const earlyAdopterLaunchPricesEUR = Object.fromEntries(
    paidPlanIds.map((id) => [id, getPlanPriceEUR(id, { earlyAdopter: true })])
  );
  const earlyAdopterRegularPricesEUR = Object.fromEntries(
    paidPlanIds.map((id) => [id, getPlanPriceEUR(id, { earlyAdopter: false })])
  );

  const formatPrice = (value) => {
    const n = Number(value);
    if (!Number.isFinite(n)) return String(value ?? "");
    try {
      return new Intl.NumberFormat(language === "de" ? "de-DE" : "en-US", {
        style: "currency",
        currency: "EUR",
      }).format(n);
    } catch {
      return `${n.toFixed(2)} €`;
    }
  };

  const regularVals = Object.values(earlyAdopterRegularPricesEUR).filter((n) => Number.isFinite(Number(n)));
  const lowestRegularPaidPrice = regularVals.length ? Math.min(...regularVals) : 0;
  const highestRegularPaidPrice = regularVals.length ? Math.max(...regularVals) : 0;

  const maxEarlyAdopterDiscount = (() => {
    let max = 0;
    for (const [planId, regular] of Object.entries(earlyAdopterRegularPricesEUR)) {
      const launch = earlyAdopterLaunchPricesEUR[planId];
      const r = Number(regular);
      const l = Number(launch);
      if (!Number.isFinite(r) || !Number.isFinite(l) || r <= 0) continue;
      const pct = ((r - l) / r) * 100;
      if (pct > max) max = pct;
    }
    return Math.round(max);
  })();
  
  const [delegateWallet, setDelegateWallet] = useState(null);
  const [step, setStep] = useState("landing");
  const navigate = useNavigate();

  // ✅ KORREKTE ABGELEITETE WERTE
  const mainWallet = submittedWallet;
  const mainTxCount = walletData?.mainWalletTxCount || 0;
  const veDelegateWallet = delegateWallet || null;
  const veDelegateTxCount = walletData?.delegateWalletTxCount || 0;
  const [manualVeDelegateMode, setManualVeDelegateMode] = useState(false);
  const [manualVeDelegateInput, setManualVeDelegateInput] = useState("");
  const [totalTransactionCount, setTotalTransactionCount] = useState(null);
  // 🚀 WICHTIG: analyzedWallets wird NICHT aus localStorage geladen beim Start
  // Beim Neuladen der Seite soll alles zurückgesetzt werden
  const [analyzedWallets, setAnalyzedWallets] = useState([]);


  // ✅ NEU: Hint nach Rückkehr von Preview-Buy (verschwindet nach 4s)
  const [showPreviewBuyHint, setShowPreviewBuyHint] = useState(false);
  useEffect(() => {
    const isPreviewBuyReturn =
      location.state?.source === "preview-buy" ||
      location.state?.openBuy === true ||
      location.state?.intent === "buy";

    if (isPreviewBuyReturn) {
      setShowPreviewBuyHint(true);
      const timer = setTimeout(() => setShowPreviewBuyHint(false), 4000);
      return () => clearTimeout(timer);
    }
  }, [location?.key]);

  const [showNextWalletHint, setShowNextWalletHint] = useState(false);
  const [isDuplicateWallet, setIsDuplicateWallet] = useState(false);
  const [formIncomplete, setFormIncomplete] = useState(false);
  
  // 🎯 Flag um zu markieren dass gekaufte Wallets geladen wurden
  const [isPurchasedWalletLoaded, setIsPurchasedWalletLoaded] = useState(false);
  
  // 🎨 State für Animations-Remount der Transaktionsübersicht
  const [txAnimKey, setTxAnimKey] = useState(0);
  
  // 🚀 Custom Dropdown States für blaue Hover-Effekte
  const [showPlanDropdown, setShowPlanDropdown] = useState(false);
  const [showYearDropdown, setShowYearDropdown] = useState(false);
  const [showPaymentDropdown, setShowPaymentDropdown] = useState(false);
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  const isLandingFormDropdownOpen =
    showPlanDropdown ||
    showYearDropdown ||
    showPaymentDropdown ||
    showCountryDropdown;

  // 🚀 Krypto-Checkout States
  // 🆕 NEU: Aktueller Plan-Info (priorisiere latest paid plan über effectivePlanForYear)
  const selectedYearStr = year || new Date().getFullYear().toString();
  const effectivePlanForYear = effectivePlanByYear[selectedYearStr];
  
  // 🚀 FIX: planToUse so berechnen, dass "free" NICHT alles übersteuert und "latest purchase" gewinnt
  const latestPlanId    = normalizePlanId(latestPlan?.plan);
  const userPlanId      = normalizePlanId(user?.plan);
  const effectivePlanId = normalizePlanId(effectivePlanForYear);
  
  // Header/Wallet-Modal: IMMER latest paid plan anzeigen (oder free wenn wirklich nix bezahlt)
  const planToUse =
    latestPlanId !== 'free' ? latestPlanId :
    userPlanId   !== 'free' ? userPlanId   :
    effectivePlanId !== 'free' ? effectivePlanId :
    'free';
  
  const currentPlanInfo = getPlanInfo(planToUse, language);

  const autoModalPlanId = normalizePlanId(autoTransactionData?.plan || plan || 'free');
  const autoModalPlanName = formatPlanDisplay(autoModalPlanId);

  const autoModalTokenSymbol =
    autoTransactionData?.tokenSymbol?.toUpperCase() ||
    autoTransactionData?.token?.toUpperCase() ||
    'TOKEN';

  const autoModalAmount = autoTransactionData?.value
    ? Number(autoTransactionData.value).toFixed(7)
    : '0.0000000';

  const autoModalTitle = autoTransactionData?.isUpgrade
    ? (language === 'en' ? 'Plan upgrade via VeWorld' : 'Plan-Upgrade via VeWorld')
    : (language === 'en' ? 'Secure VeWorld payment' : 'Sichere VeWorld-Zahlung');

  const autoModalSubtitle = autoTransactionData?.isUpgrade
    ? `${language === 'en' ? 'Upgrade to' : 'Upgrade auf'} ${autoModalPlanName} • ${autoTransactionData?.year || year}`
    : `${autoModalPlanName} • ${autoTransactionData?.year || year}`;

  const autoModalTokenKey = (
    autoTransactionData?.token ||
    autoTransactionData?.tokenSymbol ||
    selectedToken ||
    paymentMethod ||
    'vet'
  ).toLowerCase();

  const autoModalTokenMeta = {
    vet: {
      label: 'VET',
      icon: '/vet-icon.png',
      pillClass: 'border-indigo-200/80 bg-indigo-50/80',
      iconWrapClass: 'border-indigo-200/80 bg-white',
      pillHoverClass: 'hover:border-indigo-300 hover:bg-indigo-50 hover:shadow-indigo-200/60',
    },
    b3tr: {
      label: 'B3TR',
      icon: '/b3tr-icon.png',
      pillClass: 'border-emerald-200/80 bg-emerald-50/80',
      iconWrapClass: 'border-emerald-200/80 bg-white',
      pillHoverClass: 'hover:border-emerald-300 hover:bg-emerald-50 hover:shadow-emerald-200/60',
    },
    vtho: {
      label: 'VTHO',
      icon: '/vtho-icon.png',
      pillClass: 'border-violet-200/80 bg-violet-50/80',
      iconWrapClass: 'border-violet-200/80 bg-white',
      pillHoverClass: 'hover:border-violet-300 hover:bg-violet-50 hover:shadow-violet-200/60',
    },
  }[autoModalTokenKey] || {
    label: autoModalTokenSymbol,
    icon: null,
    pillClass: 'border-slate-200 bg-slate-50',
    iconWrapClass: 'border-slate-200 bg-white',
    pillHoverClass: 'hover:border-slate-300 hover:bg-slate-50 hover:shadow-slate-200/60',
  };

  const autoModalVeWorldLogo = '/veworld.png';

  // ✅ Preview→Unlock Rückkehr: Force-CTA Flag
  const getForceCta = () => {
    try { return sessionStorage.getItem('v3tax:force_cta'); } catch (_) { return null; }
  };
  const setForceCta = (value) => {
    try {
      if (value) sessionStorage.setItem('v3tax:force_cta', String(value));
      else sessionStorage.removeItem('v3tax:force_cta');
    } catch (_) {}
  };
  
  // ✅ WICHTIG: KEINE Plan-Vorauswahl beim Start (egal ob eingeloggt oder nicht)
  // Plan wird nur gesetzt durch:
  // - Restore Draft (setPlan(draft.plan))
  // - User Auswahl im Dropdown
  // (bewusst kein Auto-Set aus user/latestPlan/effectivePlan)
  // 
  // Normale Rechnungs-/Planlogik (planToUse für Anzeige/Entitlements) bleibt unberührt,
  // weil die arbeitet separat über effectivePlanForYear || latestPlan || user.plan || 'free'
  
  // 🚀 DEBUG: Zeige was geladen wird
    const [showCryptoCheckout, setShowCryptoCheckout] = useState(false);
  const [cryptoCheckoutData, setCryptoCheckoutData] = useState(null);
  const [sessionTimer, setSessionTimer] = useState(null);
  const [timeRemaining, setTimeRemaining] = useState(0);
  
  // 🧹 Cleanup beim Unmount der Komponente
  useEffect(() => {
    return () => {
      // 🚨 WICHTIG: Timer stoppen beim Unmount
      if (sessionTimer) {
        clearInterval(sessionTimer);
      }
    };
  }, [sessionTimer]);

  const anyModalOpen =
    showModal ||
    showHelpModal ||
    showLegalModal ||
    showFunctionModal ||
    showRoadmap ||
    showWalletModal ||
    showMyWalletsDashboard ||
    showPlanModal ||
    showPreviewImports ||
    showAutoTransactionModal;

  const shouldLockScroll = anyModalOpen || isMobileMenuOpen;

  // Scroll-Lock (ein Effekt): Modals + Mobile-Menü — body + html, ohne paddingRight (`scrollbar-gutter: stable` in index.css).
  useEffect(() => {
    const body = document.body;
    const html = document.documentElement;
    const prevBodyOverflow = body.style.overflow;
    const prevHtmlOverflow = html.style.overflow;

    if (shouldLockScroll) {
      body.style.overflow = 'hidden';
      html.style.overflow = 'hidden';
    } else {
      body.style.overflow = '';
      html.style.overflow = '';
    }

    return () => {
      body.style.overflow = prevBodyOverflow;
      html.style.overflow = prevHtmlOverflow;
    };
  }, [shouldLockScroll]);

  // Schließe Dropdowns wenn Modal offen ist
  useEffect(() => {
    if (!anyModalOpen) return;
    setShowDropdown(false);
    setShowUserDropdown(false);
    setIsMobileMenuOpen(false);
  }, [anyModalOpen]);

  useEffect(() => {
    if (!anyModalOpen) return;

    const onKeyDown = (e) => {
      if (e.key !== 'Escape') return;

      if (showHelpModal) {
        handleHelpModalClose();
        return;
      }

      setShowModal(false);
      setShowLegalModal(false);
      setShowFunctionModal(false);
      setShowRoadmap(false);
      setShowWalletModal(false);
      setShowMyWalletsDashboard(false);
      setShowPlanModal(false);
      setShowPreviewImports(false);
      setShowAutoTransactionModal(false);
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [anyModalOpen, showHelpModal, handleHelpModalClose]);

  // 🚀 WICHTIG: Beim Neuladen der Seite alles zurücksetzen (aber nicht bei Restore-Return)
  useEffect(() => {
    const navState = location?.state || {};
    const hasReturnIntent = !!sessionStorage.getItem("v3tax:return_intent");
    const isRestoreReturn = !!navState.restoreDraft || hasReturnIntent;

    const purchaseSuccess = localStorage.getItem("purchaseSuccess");

    // ✅ Nur beim echten Hard-Reload/normalem Einstieg resetten
    if (!purchaseSuccess && !isRestoreReturn) {
      localStorage.removeItem("analyzedWallets");
          } else {
          }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Nur beim Mount

  // 🎉 Purchase Success Toast + wallet_pairs Hydration nach erfolgreichem Kauf
  useEffect(() => {
    const run = async () => {
      const purchaseSuccess = localStorage.getItem('purchaseSuccess');
      if (!purchaseSuccess) return;

      try {
        const data = JSON.parse(purchaseSuccess);
        if (data.success) {
          // 💾 LS mit echten DB-Werten befüllen (falls Jahr bekannt)
          if (data.year) {
            await hydrateWalletPairsFromServer(data.year);
          }
          // 🎯 Richtigen Toast anzeigen
          showPurchaseSuccessToast(data.message);
        }
      } catch (error) {
              } finally {
        // localStorage immer leeren
        localStorage.removeItem('purchaseSuccess');
      }
    };

    // async helper ausführen (useEffect selbst darf nicht async sein)
    run();
  }, [hydrateWalletPairsFromServer]); // Nur beim Mount prüfen

  // 🚫 KEIN useEffect mehr - Button-Mode wird nur noch explizit gesetzt!
  // Das verhindert das "Flackern" zwischen verschiedenen Zuständen

  // 🚀 WICHTIG: Synchronisiere analyzedWallets mit localStorage
  useEffect(() => {
    if (analyzedWallets.length > 0) {
      localStorage.setItem('analyzedWallets', JSON.stringify(analyzedWallets));
          }
  }, [analyzedWallets]);

  // 🚀 Click-Outside Handler für Custom Dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.custom-dropdown')) {
        setShowPlanDropdown(false);
        setShowYearDropdown(false);
        setShowPaymentDropdown(false);
        setShowCountryDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // 🚀 Click-Outside-Handler für Language-Dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isLanguageDropdownOpen && !event.target.closest('.language-dropdown-container')) {
        setIsLanguageDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isLanguageDropdownOpen]);
  


  // 🔢 Globales technisches Limit: maximal 50 Wallets pro Set (für alle Pläne gleich)
  const MAX_WALLETS_PER_SET = 50;

  // 📊 Plan-Limits für Transaktionen (Wallet-Limits sind jetzt rein technisch)
  const getPlanLimits = (selectedPlan) => {
    const id = normalizePlanId(selectedPlan || latestPlan?.plan || user?.plan || "free");
    const limit = getPlanTxLimit(id);
    return { transactions: limit ?? null, unlimited: limit == null };
  };

  // 📊 Plan-Order für Vergleich (höhere Pläne haben höhere Order)
  const getPlanOrder = (planName) => {
    const id = normalizePlanId(planName);
    switch (id) {
      case "free": return 0;
      case "standard": return 1;
      case "pro": return 2;
      case "elite": return 3;
      case "ultimate": return 4;
      case "dev": return 99;
      default: return -1;
    }
  };

  // 📊 Aktuelle Plan-Limits basierend auf ausgewähltem Plan
  // ✅ Sicherer Plan: niemals '' erlauben
  const safePlan = plan || latestPlan?.plan || user?.plan || 'free';
  const currentPlanLimits = getPlanLimits(safePlan);
  
  // Tx-Limit für die Anzeige (basierend auf ausgewähltem Plan)
  const planLimit = currentPlanLimits.transactions;
  const planLimitUnlimited = currentPlanLimits.unlimited || planLimit == null;
  const planLimitLabel = planLimitUnlimited
    ? formatTxLimitDisplay(safePlan, language === 'en' ? 'en' : 'de')
    : planLimit;
  // 🔢 WalletLimit ist jetzt ein rein technisches Set-Limit, nicht planabhängig
  const walletLimit = MAX_WALLETS_PER_SET;
  
  // 🚨 Einheitlicher Bool für Limitüberschreitung (gleiche Quelle wie Plakette)
  const txExceededOnSelectedPlan =
    !planLimitUnlimited && planLimit != null && (totalTransactionCount ?? 0) > planLimit;
  
  // ✅ "selected plan" robust normalisieren (nach txExceededOnSelectedPlan, damit safePlan verfügbar ist)
  // ✅ Nur als Free betrachten, wenn explizit "free" gewählt wurde (nicht wenn leer!)
  const selectedPlanId = plan ? normalizePlanId(plan) : null;
  const isFreePlanSelected = selectedPlanId === "free";

  // ✅ Free-Plan darf nur speichern, wenn TX <= 100
  const canSaveFreePlan = isFreePlanSelected && !txExceededOnSelectedPlan;
  
  // ✅ Hilfs-Variable: Payment disabled bei 2024 ODER Free-Plan
  const isPaymentDisabled = isFreeYear || isFreePlanSelected;
  
  // 🚀 Upgrade-Modus: Ursprünglicher Plan und Limits aus Dataset
  // Nur im Upgrade-Modus relevant - für Filter & Button-Disabled-Logik
  const originalPlan = upgradeMode && upgradeDatasetData ? (upgradeDatasetData.plan || plan) : plan;
  const originalPlanOrder = getPlanOrder(originalPlan);
  const originalPlanLimits = getPlanLimits(originalPlan);
  const originalPlanLimit = originalPlanLimits.transactions;
  
  
  // ✅ Optional: Payment-Methode zurücksetzen, wenn Free-Plan oder 2024 aktiv
  useEffect(() => {
    if (isFreeYear || isFreePlanSelected) {
      setShowPaymentDropdown(false);
      setPaymentMethod("");
    }
  }, [isFreeYear, isFreePlanSelected]);

  // 📊 Aktuelle Wallet-Anzahl berechnen
  const getCurrentWalletCount = () => {
    // 🚀 Upgrade-Modus: Verwende Dataset walletCount als Basis
    if (upgradeMode && upgradeDatasetData) {
      let count = upgradeDatasetData.currentWalletCount || 0;
      
      // Addiere alle neu analysierten Wallets
      analyzedWallets.forEach(w => {
        count += 1; // Main Wallet
        if (w.delegateWallet) {
          count += 1; // VeDelegate Wallet
        }
      });
      
      // Aktive Wallets zählen (nur wenn sie noch nicht in analyzedWallets sind)
      if (walletData && submittedWallet && !analyzedWallets.some(w => w.wallet === submittedWallet)) {
        count += 1; // Main Wallet
        if (delegateWallet) {
          count += 1; // VeDelegate Wallet
        }
      }
      
            
      return count;
    }
    
    // Normaler Modus: Zähle wie bisher
    let count = 0;
    
    // Gespeicherte Wallets zählen (das sind die, die wir wirklich analysiert haben)
    analyzedWallets.forEach(entry => {
      count += 1; // Main Wallet
      if (entry.delegateWallet) {
        count += 1; // VeDelegate Wallet
      }
    });
    
    // Aktive Wallets zählen (nur wenn sie noch nicht in analyzedWallets sind)
    if (walletData && submittedWallet && !analyzedWallets.some(w => w.wallet === submittedWallet)) {
      count += 1; // Main Wallet
      if (delegateWallet) {
        count += 1; // VeDelegate Wallet
      }
    }
    
        
    return count;
  };

  const currentWalletCount = getCurrentWalletCount();

  // ✅ NEU: CTA-Variant zentral berechnen (damit Morph immer triggert)
  // WICHTIG: Muss nach allen State-Deklarationen sein (isAnalyzing, analyzedWallets, currentWalletCount, etc.)
  const ctaVariant = (() => {
    if (isAnalyzing || isUpgradeAnalyzing) return "loading";
    if (currentWalletCount >= MAX_WALLETS_PER_SET) return "limit";
    if (buttonMode === "addWalletToSet") return "add";
    if (buttonMode === "upgrade") return "upgrade";

    // ButtonMode analyze bleibt eigener Zustand
    if (buttonMode === "analyze") return "analyze";

    // Buy-Mode kann: analyze (wenn noch nix analysiert), buy oder preview
    if (buttonMode === "buy") {
      const hasAnalysis = !(analyzedWallets.length === 0 && !walletData);
      if (!hasAnalysis) return "analyze";
      return buyPreviewMode === "preview" ? "preview" : "buy";
    }

    if (buttonMode === "dashboard") return "dashboard";
    return "analyze";
  })();

  // ✅ CTA-Nudge nur auslösen, wenn Buy/Preview CTA wirklich aktiv ist
  useEffect(() => {
    if (!isBuyCtaVisible) return;
    setCtaNudge(true);
    const t = setTimeout(() => setCtaNudge(false), 220);
    return () => clearTimeout(t);
  }, [buyPreviewMode, isBuyCtaVisible]);

  // 🚀 Upgrade-Flow: Ist das Set mit dem URSPRÜNGLICHEN Plan schon über Limit?
  // (wichtig: nur dieser Wert entscheidet, ob Plan/Zahlungsmethode freigegeben werden)
  const upgradeRequiredOnOriginalPlan =
    upgradeMode &&
    upgradeDatasetData &&
    (totalTransactionCount !== null &&
      originalPlanLimit != null &&
      totalTransactionCount > originalPlanLimit);

  // 🔍 Prüfe ob Wallet gekauft ist
  // 🚀 Prüfe ob Wallet gekauft ist und lade VeDelegate Wallet
  const checkIfWalletIsPurchased = async (walletAddress, selectedYear) => {
    if (!walletAddress || !selectedYear || !isAuthenticated) return false;
    
    try {
      // 🚀 WICHTIG: IMMER Backend-API prüfen, auch bei lokaler Auth!
      try {
        // 🎯 1. Prüfe Stripe-Käufe (alte Route)
        const stripeResponse = await fetch(apiUrl(`/api/auth/user/wallets/${selectedYear}`), {
          credentials: 'include'
        });
        
        let hasAccess = false;
        
        if (stripeResponse.ok) {
          const stripeData = await stripeResponse.json();
          
          // 🎯 Prüfe ob die Wallet in den Stripe-Käufen enthalten ist
          hasAccess = stripeData.purchasedWallets.some(wallet => 
            wallet.address.toLowerCase() === walletAddress.toLowerCase()
          );
          
          if (hasAccess) {
                        return true;
          }
        }
        
        // 🎯 2. Prüfe VeWorld-Käufe (neue Route)
        try {
                    const veWorldResponse = await fetch(apiUrl(`/api/veworld/user-purchases`), {
            credentials: 'include'
          });
          
          if (veWorldResponse.ok) {
            const veWorldData = await veWorldResponse.json();
                        
            // 🎯 Prüfe ob die Wallet in den VeWorld-Käufen für das richtige Jahr enthalten ist
            hasAccess = veWorldData.purchases.some(purchase => {
                            
              // ✅ Year robust vergleichen (String-Vergleich)
              if (String(purchase.year) !== String(selectedYear)) {
                                return false;
              }

              // ✅ Pending/Unpaid Orders nicht als gekauft werten
              const statusRaw =
                purchase?.status ??
                purchase?.paymentStatus ??
                purchase?.payment_status ??
                purchase?.state ??
                purchase?.payment_state ??
                '';
              const status = String(statusRaw || '').toLowerCase();

              const isExplicitlyUnpaid = purchase?.isPaid === false || purchase?.paid === false;
              const isFinalStatus =
                !status ||
                ['paid','success','succeeded','completed','confirmed','settled'].includes(status);

              if (isExplicitlyUnpaid || !isFinalStatus) {
                                return false;
              }

              return purchase.wallets && purchase.wallets.some(wallet => {
                const mainMatch = wallet.mainWallet && wallet.mainWallet.toLowerCase() === walletAddress.toLowerCase();
                const delegateMatch = wallet.delegateWallet && wallet.delegateWallet.toLowerCase() === walletAddress.toLowerCase();
                                return mainMatch || delegateMatch;
              });
            });
            
            if (hasAccess) {
                            return true;
            }
          } else {
                      }
        } catch (veWorldError) {
                  }
        
                return false;
        
      } catch (backendError) {
                
        // 🚀 Fallback: Lokale Authentifizierung prüfen
        const localAuth = localStorage.getItem('v3tax_auth');
        if (localAuth) {
          try {
            const authData = JSON.parse(localAuth);
            if (authData.authenticated && authData.walletAddress) {
                            
              // 🎯 Für lokale Auth: Wallets sind standardmäßig nicht gekauft
                            return false;
            }
          } catch (parseError) {
                      }
        }
        
        return false; // Lokale Auth: Wallet nicht gekauft
      }
    } catch (error) {
          }
    
    return false;
  };

  // 🚨 ABSOLUTER SCHUTZ: Prüfe ob Wallet im Dashboard des Users vorhanden ist
  const checkIfWalletInUserDashboard = async (walletAddress, selectedYear) => {
    if (!walletAddress || !selectedYear || !isAuthenticated) return false;
    
    try {
      // 🎯 Direkt die gekauften Wallets für das Jahr laden
      const response = await fetch(apiUrl(`/api/auth/user/wallets/${selectedYear}`), {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        
        // 🎯 Prüfe ob die Wallet in den gekauften Wallets enthalten ist
        const hasAccess = data.purchasedWallets.some(wallet => 
          wallet.address.toLowerCase() === walletAddress.toLowerCase()
        );
        
        // 🔍 ABSOLUTER SCHUTZ: Wallet im Dashboard (ENTFERNT - zu viel Output)
        
        return hasAccess;
      }
    } catch (error) {
          }
    
    return false;
  };

  // 🚀 Lade automatisch VeDelegate Wallet für gekaufte Main Wallet
  const loadVeDelegateWalletForPurchasedWallet = async (mainWallet, year, purchasedWallets) => {
    try {
      // 🔍 Suche nach VeDelegate Wallet für gekaufte Main Wallet (ENTFERNT - zu viel Output)
      
      // 🎯 JETZT RICHTIG: Suche nach Main Wallet und finde die dazugehörige Delegate Wallet
      const mainWalletInfo = purchasedWallets.find(wallet => 
        wallet.address.toLowerCase() === mainWallet.toLowerCase()
      );
      
      if (mainWalletInfo) {
                
        // 🎯 Suche nach der dazugehörigen Delegate Wallet (flexibler Ansatz)
        let delegateWalletInfo = purchasedWallets.find(wallet => 
          wallet.type === 'delegate' && 
          wallet.plan === mainWalletInfo.plan
        );
        
        // 🎯 Fallback: Wenn keine Delegate Wallet mit gleichem Plan gefunden wird, 
        // suche nach der ersten verfügbaren Delegate Wallet
        if (!delegateWalletInfo) {
          delegateWalletInfo = purchasedWallets.find(wallet => 
            wallet.type === 'delegate'
          );
          if (delegateWalletInfo) {
                      }
        }
        
        if (delegateWalletInfo) {
                    
          // 🎯 Automatisch VeDelegate Wallet setzen
          setDelegateWallet(delegateWalletInfo.address);
          
          // ❌ KEIN Auto-Set von Plan/Jahr: User muss selbst wählen
          // Nur VeDelegate Wallet wird gesetzt, alles andere bleibt leer
          
                    
          return; // VeDelegate gefunden und gesetzt
        } else {
                  }
      } else {
              }
      
            
    } catch (error) {
          }
  };

  // 🚀 Zusätzlicher useEffect für delegateWallet Änderungen
  useEffect(() => {
    // ⛔️ Im Upgrade-Modus NICHT in die normale Button-Logik eingreifen
    if (upgradeMode && upgradeDatasetData) {
      return;
    }

    // ✅ WICHTIG: Beim Return aus "Freischalten" (Preview → App) darf NICHT auf Calculate umgeschaltet werden
    const isPreviewBuyReturn =
      location?.state?.source === "preview-buy" || !!location?.state?.openBuy;
    if (isPreviewBuyReturn) {
            return;
    }

    // ✅ Force-CTA Check: Wenn Force-Buy aktiv ist, nicht auf Calculate switchen
    if (getForceCta() === 'buy') {
            return;
    }
    
    if (delegateWallet && wallet && year && isAuthenticated) {
      // Wenn VeDelegate Wallet gesetzt ist, prüfe nochmal den Button-Modus
      const updateButtonMode = async () => {
        const isPurchased = await checkIfWalletIsPurchased(wallet, year);
        if (isPurchased) {
          if (isAdminWallet) {
            setButtonMode("analyze");
                        return;
          }
          setButtonMode("dashboard");
                  }
      };
      
      updateButtonMode();
    }
  }, [
    delegateWallet,
    wallet,
    year,
    isAuthenticated,
    upgradeMode,
    upgradeDatasetData,
    location?.state?.source,
    location?.state?.openBuy,
  ]);

  // ⚠️ WICHTIG: useDraggable() liefert pro Render ein neues Object zurück.
  // Wenn man dragPlan in die Dependency-Liste packt, läuft der Effekt bei jeder Bewegung
  // erneut und setzt die Position permanent zurück → Modal "nicht verschiebbar" / Flackern.
  const { setPos: setPlanPos } = dragPlan;

  useEffect(() => {
    if (showModal) setPlanPos({ x: 0, y: 0 });
  }, [showModal, setPlanPos]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showDropdown && !event.target.closest('.dropdown-container')) {
        setShowDropdown(false);
      }
      // Legal & Help Modals haben eigenes Backdrop-onClick-Handling - hier nicht schließen!
      if (showFunctionModal && !event.target.closest('.function-modal-container')) {
        setShowFunctionModal(false);
      }
      // RoadmapModal hat eigenes Click-Outside-Handling
      if (showModal && !event.target.closest('.plan-modal-container')) {
        setShowModal(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showDropdown, showFunctionModal, showModal]);

  useEffect(() => {
    const handleScroll = () => {
      if (scrollRafRef.current !== null) return;
      scrollRafRef.current = window.requestAnimationFrame(() => {
        scrollRafRef.current = null;
        const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
        const next = scrollTop > 300;
        if (showScrollToTopRef.current !== next) {
          showScrollToTopRef.current = next;
          setShowScrollToTop(next);
        }
      });
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (scrollRafRef.current !== null) {
        window.cancelAnimationFrame(scrollRafRef.current);
        scrollRafRef.current = null;
      }
    };
  }, []);

  // 🌐 Automatische Sprachauswahl basierend auf Land - ENTFERNT
  // Die Sprache wird nur noch manuell über den Header-Button geändert

  // 🔄 Ref für vorheriges Jahr (um nur bei echtem Jahrwechsel zu resetten)
  const prevYearRef = useRef(null);

  // 🔄 Jahrwechsel-Logik: nur resetten, wenn bereits analysiert wurde
  useEffect(() => {
    // Im Upgrade-Modus nie resetten
    if (upgradeMode && upgradeDatasetData) {
            return;
    }

    // ✅ FIX: Draft-Restore darf keinen "Jahrwechsel-Reset" auslösen
    try {
      const suppress = sessionStorage.getItem("v3tax:suppress_year_reset");
      if (suppress) {
        // WICHTIG: Beim Mount kann das Flag schon gesetzt sein, bevor year aus dem Draft im State angekommen ist.
        // Deshalb erst konsumieren, wenn year wirklich gesetzt ist (nicht leer).
        if (year) {
          prevYearRef.current = year;
          sessionStorage.removeItem("v3tax:suppress_year_reset");
                  } else {
                  }
        return;
      }
    } catch (_) {}

    // Erstinitialisierung: Jahr merken, aber NICHT resetten
    if (prevYearRef.current === null) {
      prevYearRef.current = year;
            return;
    }

    // Wenn Jahr unverändert, nichts tun
    if (prevYearRef.current === year) {
      return;
    }

    // ---- ab hier: echtes Jahr hat sich geändert (z.B. 2024 → 2025) ----
    const previousYear = prevYearRef.current;
        prevYearRef.current = year;

    // ➜ Noch keine Analyse gelaufen? (Eingabe nicht leeren)
    const noAnalysisYet =
      (analyzedWallets.length === 0) &&
      !walletData &&
      (totalTransactionCount === 0 || totalTransactionCount === null) &&
      !isPurchasedWalletLoaded;

    if (noAnalysisYet) {
      // Button sicherheitshalber auf Analyze, aber Eingaben behalten
      setButtonMode("analyze");
            return;
    }

    // ➜ Jahrwechsel NACH Analyse: harter Reset
    setWallet("");
    setSubmittedWallet("");
    setDelegateWallet(null);
    setDelegateCandidates([]);
    setManualVeDelegateMode(false);

    setWalletData(null);
    setAnalyzedWallets([]);
    localStorage.removeItem("analyzedWallets");
    setIsPurchasedWalletLoaded(false);

    setGlobalTotals({ vet: 0, vtho: 0, b3tr: 0, nft: 0 });
    setFrozenTxTotal(0);
    setTotalTransactionCount(0);

    setShowNextWalletHint(false);
    setIsNewAnalysis(true);
    setIsUpgradeAnalyzing(false);
    setTransactionCount(null);
    setButtonMode("analyze");

      }, [year, upgradeMode, upgradeDatasetData]);

  // 🚀 Upgrade-Modus: Initialisiere aus Navigation State
  useEffect(() => {
    if (location.state?.upgradeMode) {
      const upgradeData = location.state;
            
      // Setze Upgrade-Modus State ZUERST
      setUpgradeMode(true);
      setUpgradeDatasetData(upgradeData);
      
      // Initialisiere Jahr
      if (upgradeData.year) {
        setYear(upgradeData.year);
      }
      
      // Initialisiere Plan
      if (upgradeData.plan) {
        setPlan(normalizePlanId(upgradeData.plan));
      }
      
      // 🚀 WICHTIG: analyzedWallets NICHT initialisieren - bleibt leer!
      // Nur die Status-Anzeigen werden korrekt gesetzt, die Wallets werden nur bei neuer Analyse hinzugefügt
      setAnalyzedWallets([]);
            
      // Zeige Hint
      setShowNextWalletHint(true);
      
      // Setze Button-Mode auf "analyze" - User muss erst eine Wallet analysieren
      setButtonMode("analyze");
            
      // 🚀 NEU: Analysiere alle Wallets im Set für aktuelle Transaktionszahlen
      // Wird nach Jahr-Setzeung ausgeführt
      setTimeout(() => {
        if (upgradeData.existingWallets && Array.isArray(upgradeData.existingWallets) && upgradeData.year) {
          analyzeAllWalletsInSet(
            upgradeData.existingWallets,
            upgradeData.year,
            upgradeData.datasetId
          );
        } else {
          // Fallback: Verwende die aus dem Dataset übergebenen Werte
          if (upgradeData.currentTransactionCount !== undefined) {
            const txCount = upgradeData.currentTransactionCount;
            setFrozenTxTotal(txCount);
            setTotalTransactionCount(txCount);
                      }
        }
      }, 0);
      
      // Lösche State aus location (einmalig) - nach kurzer Verzögerung um sicherzustellen, dass alles gesetzt ist
      setTimeout(() => {
        if (location.state) {
          window.history.replaceState({}, document.title, location.pathname);
        }
      }, 100);
      
          }
  }, [location.state]);

const [missingFields, setMissingFields] = useState({
  year: false,
  plan: false,
  payment: false,
  country: false,
});

// 🚀 ALTE FUNKTION ENTFERNT: Duplikatsprüfung erfolgt jetzt direkt in handleWalletChange
// Der useEffect wurde entfernt, da er redundant ist und möglicherweise Konflikte verursacht
// handleWalletChange prüft sofort beim Tippen und setzt isDuplicateWallet entsprechend


  // HIER einfügen:
const [globalTotals, setGlobalTotals] = useState({
  vet: 0,
  vtho: 0,
  b3tr: 0,
  nft: 0,
});
const [frozenTxTotal, setFrozenTxTotal] = useState(0);

  // ✅ NEU: Draft autosaven (wenn buttonMode === "buy" und analyzedWallets vorhanden)
  // WICHTIG: Muss NACH frozenTxTotal und globalTotals deklariert sein
  useEffect(() => {
    if (buttonMode !== "buy" || analyzedWallets.length === 0) return;

    const draft = {
      wallet,
      delegateWallet,
      year,
      plan,
      country,
      paymentMethod,      // ✅ wichtig (war vorher NICHT drin)
      analyzedWallets,
      buyPreviewMode,

      // ✅ WICHTIG für TX-LIMIT + Totals nach Preview-Return
      frozenTxTotal,
      globalTotals,
    };

    sessionStorage.setItem("v3tax:draft_wallet_set", JSON.stringify(draft));
  }, [
    buttonMode,
    wallet,
    delegateWallet,
    year,
    plan,
    country,
    paymentMethod,
    analyzedWallets,
    buyPreviewMode,
    frozenTxTotal,
    globalTotals,
  ]);

  // 🔢 Summen aller eingefrorenen Wallets + aktuell sichtbarer Wallet
  const hasGlobalTotals =
    ((globalTotals?.vet ?? 0) +
     (globalTotals?.vtho ?? 0) +
     (globalTotals?.b3tr ?? 0) +
     (globalTotals?.nft ?? 0)) > 0
    || (totalTransactionCount ?? 0) > 0
    || (frozenTxTotal ?? 0) > 0;

  const noData = (!walletData && analyzedWallets.length === 0 && !hasGlobalTotals);

  const displayTotals = noData
    ? { vet: 0, vtho: 0, b3tr: 0, nft: 0 }
    : {
        // 🚀 FIX: Delegate-Kategorien mit in die Totals einbeziehen
        vet:  (globalTotals?.vet  ?? 0)
             + (walletData?.vet?.transactions  ?? 0)
             + (walletData?.delegate?.vet      ?? 0),
        vtho: (globalTotals?.vtho ?? 0)
             + (walletData?.vtho?.transactions ?? 0)
             + (walletData?.delegate?.vtho     ?? 0),
        b3tr: (globalTotals?.b3tr ?? 0)
             + (walletData?.b3tr?.transactions ?? 0)
             + (walletData?.delegate?.b3tr     ?? 0),
        nft:  (globalTotals?.nft  ?? 0)
             + (walletData?.nft?.transactions  ?? 0)
             + (walletData?.delegate?.nft      ?? 0),
      };

const handleRemoveAnalyzedWallet = (walletToRemove) => {
  const walletEntry = analyzedWallets.find(w => w.wallet === walletToRemove);
  if (!walletEntry) return;

  setAnalyzedWallets(prev => prev.filter(w => w.wallet !== walletToRemove));

  // Token-Totals abziehen
  setGlobalTotals(prev => ({
    vet: prev.vet - (walletEntry.vet || 0),
    vtho: prev.vtho - (walletEntry.vtho || 0),
    b3tr: prev.b3tr - (walletEntry.b3tr || 0),
    nft: prev.nft - (walletEntry.nft || 0),
  }));

  // Transaktionssumme abziehen (Main + Delegate)
  setFrozenTxTotal(prev => prev - ((walletEntry.mainTxCount || 0) + (walletEntry.veDelegateTxCount || 0)));
};

function isWalletComboAlreadyFrozen(main, delegate) {
  return analyzedWallets.some(w =>
    w.wallet.toLowerCase() === (main || "").toLowerCase() &&
    (w.delegateWallet || "").toLowerCase() === (delegate || "").toLowerCase()
  );
}


  useEffect(() => {
  // 🚀 UPGRADE-MODUS: Nicht überschreiben, da totalTransactionCount bereits korrekt berechnet wurde
  if (upgradeMode && upgradeDatasetData) {
    return; // Im Upgrade-Modus nicht überschreiben
  }
  
  if (walletData) {
    const main = walletData.mainWalletTxCount ?? 0;
    const delegate = walletData.delegateWalletTxCount ?? 0;
    setTotalTransactionCount(main + delegate);
  }
}, [walletData, upgradeMode, upgradeDatasetData]);

useEffect(() => {
  // 🚀 UPGRADE-MODUS: Nicht überschreiben, da totalTransactionCount bereits korrekt berechnet wurde
  if (upgradeMode && upgradeDatasetData) {
    return; // Im Upgrade-Modus nicht überschreiben
  }
  
  // live-TX der aktuell angezeigten Wallet
  const liveTx =
    (walletData?.mainWalletTxCount ?? 0) +
    (walletData?.delegateWalletTxCount ?? 0);

  // Frozen + Live = Gesamt
  setTotalTransactionCount(frozenTxTotal + liveTx);
}, [walletData, frozenTxTotal, upgradeMode, upgradeDatasetData]);

  // 🚀 Upgrade-Modus: Analysiere alle Wallets im Set UND speichere Counts direkt in DB (updateOnly)
  const analyzeAllWalletsInSet = async (wallets, selectedYear, datasetIdParam) => {
    if (!wallets || !Array.isArray(wallets) || wallets.length === 0) {
            return;
    }

        setIsUpgradeAnalyzing(true);
    if (showNextWalletHint) setShowNextWalletHint(false);
    setError("");

    try {
      // 1) Alle zu prüfenden Adressen (Main + Delegate) sammeln
      const allWalletAddresses = [];
      wallets.forEach(pair => {
        const mainAddr = pair.main_wallet || pair.mainWallet;
        const delAddr  = pair.delegate_wallet || pair.delegateWallet;
        if (mainAddr) allWalletAddresses.push({ address: mainAddr, type: 'main' });
        if (delAddr)  allWalletAddresses.push({ address: delAddr, type: 'delegate' });
      });
      
      // 2) Für jede Adresse transaction-count + Breakdown holen
      const results = await Promise.all(allWalletAddresses.map(async (w) => {
        try {
          const r = await fetch(
            apiUrl(`/api/v2/transaction-count?wallet=${w.address}&year=${selectedYear}`),
            { credentials: 'include', signal: analyzeAbortRef.current?.signal }
          );
          const j = await r.json();
          return {
            address: (w.address || '').toLowerCase(),
            type: w.type,
            txCount: Number(j?.transactionCount || 0),
            vetTx:  Number(j?.vet?.transactions  || 0),
            vthoTx: Number(j?.vtho?.transactions || 0),
            b3trTx: Number(j?.b3tr?.transactions || 0),
            nftTx:  Number(j?.nft?.transactions  || 0),
          };
        } catch (e) {
                    return {
            address: (w.address || '').toLowerCase(),
            type: w.type,
            txCount: 0,
            vetTx: 0,
            vthoTx: 0,
            b3trTx: 0,
            nftTx: 0,
          };
        }
      }));

      // 3) Headline/Summen für die UI
      let totalMainTxCount = 0, totalDelegateTxCount = 0;
      let totalVetTx = 0, totalVthoTx = 0, totalB3trTx = 0, totalNftTx = 0;
      for (const r of results) {
        if (r.type === 'main') totalMainTxCount += r.txCount;
        else totalDelegateTxCount += r.txCount;
        totalVetTx  += r.vetTx;
        totalVthoTx += r.vthoTx;
        totalB3trTx += r.b3trTx;
        totalNftTx  += r.nftTx;
      }
      const totalTxCount = totalMainTxCount + totalDelegateTxCount;
      setFrozenTxTotal(totalTxCount);
      setTotalTransactionCount(totalTxCount);
      setGlobalTotals({ vet: totalVetTx, vtho: totalVthoTx, b3tr: totalB3trTx, nft: totalNftTx });

      // 4) ➕ Persist-Daten je Wallet-Paar aus den Resultaten bauen (snake_case wie Backend erwartet)
      const byAddr = new Map(results.map(r => [r.address, r]));
      const enriched = (wallets || []).map(pair => {
        const mainRaw = pair.main_wallet || pair.mainWallet || '';
        const delRaw  = pair.delegate_wallet || pair.delegateWallet || '';
        const mainKey = mainRaw.toLowerCase();
        const delKey  = delRaw.toLowerCase();
        const m = mainKey ? byAddr.get(mainKey) : null;
        const d = delKey  ? byAddr.get(delKey)  : null;

        return {
          main_wallet: pair.main_wallet || pair.mainWallet || null,
          delegate_wallet: pair.delegate_wallet || pair.delegateWallet || null,
          main_tx_count: Number(m?.txCount || 0),
          delegate_tx_count: Number(d?.txCount || 0),
          vet_amount:  Number((m?.vetTx  || 0) + (d?.vetTx  || 0)),
          vtho_amount: Number((m?.vthoTx || 0) + (d?.vthoTx || 0)),
          b3tr_amount: Number((m?.b3trTx || 0) + (d?.b3trTx || 0)),
          nft_count:   Number((m?.nftTx  || 0) + (d?.nftTx  || 0)),
          app_context: pair.app_context || pair.appContext || {},
        };
      });

      // 5) 💾 Direkt in DB schreiben (updateOnly)
      const dsId = datasetIdParam ?? upgradeDatasetData?.datasetId;
      if (!dsId) {
                toast.error(t('toast.datasetIdMissing', language));
        return;
      }

      
      const response = await fetch(apiUrl(`/api/datasets/${dsId}/add-wallets`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          walletPairs: enriched,
          updateOnly: true,            // nur aktualisieren, nichts neu anlegen
          source: 'auto-upgrade-sync'
        })
      });

      const result = await response.json().catch(() => null);

      if (!response.ok || !result?.success) {
                toast.error(language === 'en' ? 'Save failed' : 'Speichern fehlgeschlagen');
        return;
      }

      
      // UI/Cache updaten – localStorage mergen wie im Button-Flow
      const existing = JSON.parse(localStorage.getItem('wallet_pairs') || '[]');
      const merged = [...existing];
      for (const nw of enriched) {
        const key = (nw.main_wallet || '').toLowerCase();
        const i = merged.findIndex(p => (p.main_wallet || '').toLowerCase() === key);
        if (i >= 0) merged[i] = { ...merged[i], ...nw };
        else merged.push(nw);
      }
      localStorage.setItem('wallet_pairs', JSON.stringify(merged));

      // Dashboard neu laden & Feedback
      window.dispatchEvent(new CustomEvent('datasets:refresh', { detail: { datasetId: dsId } }));
      toast.success(language === 'en' ? 'Transactions updated.' : 'Transaktionen aktualisiert.');
    } catch (error) {
            setError("Fehler beim Aktualisieren des Wallet-Sets.");
    } finally {
      setIsUpgradeAnalyzing(false);
          }
  };

  const WalletIcon = Wallet;

// 🔍 Wird bei jeder Änderung des Wallet-Feldes aufgerufen - prüft sofort auf Duplikate
const handleWalletChange = async (e) => {
  const value = e.target.value.trim();

  setWallet(e.target.value); // Setze den Wert mit Leerzeichen, trimme nur für Prüfung
  setMissingFields(prev => ({ ...prev, wallet: false }));

  // Hinweis erstmal zurücksetzen – neue Eingabe, neuer Check
  setIsDuplicateWallet(false);

  // Bei leerem Feld oder noch unvollständiger Adresse: gar nicht prüfen
  if (!value || value.length < 10) {
    // 🚀 UPGRADE-MODUS: Button-Mode auf Analyze setzen, wenn Wallet leer wird
    if (upgradeMode && upgradeDatasetData) {
      if (!value && !walletData) {
        // Wallet wird leer → Button-Mode bleibt erhalten oder geht zu addWalletToSet/upgrade
        // Das wird von der Button-Logik bestimmt
      }
    }
    return;
  }

  // Optional: nur prüfen, wenn es wie eine VET-Adresse aussieht
  const isProbablyAddress = /^0x[a-fA-F0-9]{40}$/.test(value);
  if (!isProbablyAddress) {
    return;
  }

  const lower = value.toLowerCase();

  // 1️⃣ Im Upgrade-Modus: Prüfen, ob Wallet bereits im aktuellen Wallet-Set ist
  if (upgradeMode && upgradeDatasetData?.existingWallets?.length) {
    const existsInSet = upgradeDatasetData.existingWallets.some(w => {
      const mainWallet = (w.main_wallet || w.mainWallet || "").toLowerCase();
      const delegateWallet = (w.delegate_wallet || w.delegateWallet || "").toLowerCase();
      return mainWallet === lower || delegateWallet === lower;
    });

    if (existsInSet) {
            setIsDuplicateWallet(true);
      
      // 🚀 UPGRADE-MODUS: Button-Mode auf Analyze setzen (aber disabled)
      if (upgradeMode && upgradeDatasetData) {
        setButtonMode("analyze");
      }
      return; // Restliche Checks sparen wir uns
    }
  }

  // 2️⃣ Prüfen, ob Wallet bereits in analyzedWallets ist (aktuell analysierte Wallets)
  const existsInAnalyzed = analyzedWallets.some(w => {
    const mainWallet = (w.wallet || "").toLowerCase();
    const delegateWallet = (w.delegateWallet || "").toLowerCase();
    return mainWallet === lower || delegateWallet === lower;
  });

  if (existsInAnalyzed) {
        setIsDuplicateWallet(true);
    return;
  }

  // 3️⃣ ABSOLUTER SCHUTZ: Prüfen, ob Wallet im Dashboard / Kauf schon existiert
  // 🚀 WICHTIG: Diese Prüfung funktioniert sowohl im normalen Modus als auch im Upgrade-Modus
  // Sie prüft, ob die Wallet bereits in einem gekauften Set existiert
  if (isAuthenticated) {
    // Wenn Jahr vorhanden ist, prüfe für dieses spezifische Jahr
    if (year) {
      try {
        const alreadyInDashboard = await checkIfWalletInUserDashboard(value, year);
        if (alreadyInDashboard) {
                    setIsDuplicateWallet(true);
          return;
        }
      } catch (err) {
                // kein showErrorToast, UI soll nicht nerven beim Tippen
      }
    }
    // 🚀 ZUSÄTZLICH: Auch ohne Jahr prüfen (für alle Jahre) - falls Jahr noch nicht ausgewählt ist
    // Dies ist wichtig, damit der Hinweis auch erscheint, bevor der User ein Jahr auswählt
    // Aber nur wenn wir im normalen Modus sind (im Upgrade-Modus haben wir bereits existingWallets geprüft)
    if (!upgradeMode && !year) {
      // Prüfe für alle möglichen Jahre (2024, 2025, 2026)
      try {
        const yearsToCheck = ['2024', '2025', '2026'];
        for (const checkYear of yearsToCheck) {
          const alreadyInDashboard = await checkIfWalletInUserDashboard(value, checkYear);
          if (alreadyInDashboard) {
                        setIsDuplicateWallet(true);
            return;
          }
        }
      } catch (err) {
                // kein showErrorToast, UI soll nicht nerven beim Tippen
      }
    }
  }

  // 🚀 UPGRADE-MODUS: Button-Mode auf Analyze setzen, wenn neue Wallet eingegeben wird
  if (upgradeMode && upgradeDatasetData) {
    // Im Upgrade-Modus: Wenn User eine neue Wallet eingibt (noch nicht analysiert), Button auf "Analyze"
    // Nach der Analyse wird der Button automatisch auf "addWalletToSet" oder "upgrade" gesetzt
    if (value && !walletData) {
      setButtonMode("analyze");
          }
    return;
  }
  
  // 🚀 ADMIN-WALLET PRÜFUNG: Prüfe ob eingeloggte Wallet eine Admin-Wallet ist
  if (isAuthenticated && isAdminWallet) {
    // 🚀 ADMIN-WALLET: Immer Analyze Button anzeigen
    setButtonMode("analyze");
  } else {
    // 🎯 NORMALE USER: Prüfe Limits beim Wallet-Input
    // 🚨 WICHTIG: Verwende totalTransactionCount statt transactionCount für Konsistenz mit Plakette
    const transactionsExceeded = (totalTransactionCount ?? 0) > currentPlanLimits.transactions;
    // Nur noch für Logging interessant, kein Plan-Limit mehr:
    const walletsExceeded = currentWalletCount > walletLimit;
    
    // 🚨 Paywall: nur noch Transaktions-Limit relevant
    if (transactionsExceeded) {
      setButtonMode("analyze");
          } else {
      setButtonMode("analyze");
          }
  }
};

// 👑 Admin: Dataset erzeugen/wiederverwenden und wie Paid-User zur Full-Analysis navigieren
const handleAdminDirectAnalysis = async ({
  mainWallet,
  data,
  count,
  exchangeRows = [],
} = {}) => {
  if (!isAuthenticated || !user?.walletAddress) {
    setShowWalletModal(true);
    return;
  }

  const allForRun = collectAllWalletsForAnalysis();
  const walletPairs = allForRun.map((w) => ({
    mainWallet: w.wallet,
    delegateWallet: w.delegateWallet || null,
    mainTxCount: Number(w.mainTxCount || 0),
    delegateTxCount: Number(w.veDelegateTxCount || 0),
    vet: Number(w.vet || 0),
    vtho: Number(w.vtho || 0),
    b3tr: Number(w.b3tr || 0),
    nft: Number(w.nft || 0),
  }));

  if (walletPairs.length === 0 && mainWallet) {
    walletPairs.push({
      mainWallet,
      delegateWallet: data?.delegateWallet || delegateWallet || null,
      mainTxCount: Number(data?.mainWalletTxCount || count || 0),
      delegateTxCount: Number(data?.delegateWalletTxCount || data?.delegateTxCount || 0),
      vet: Number(data?.vet?.transactions || 0),
      vtho: Number(data?.vtho?.transactions || 0),
      b3tr: Number(data?.b3tr?.transactions || 0),
      nft: Number(data?.nft?.transactions || 0),
    });
  }

  if (walletPairs.length === 0) {
    throw new Error(
      language === "en"
        ? "Add at least one wallet to the set before starting the calculation."
        : "Bitte mindestens eine Wallet zum Set hinzufügen, bevor die Berechnung startet."
    );
  }

  const adminCountry = resolveResidenceCountryForApi(country);
  if (!adminCountry) {
    throw new Error(
      language === "en"
        ? "Please select a tax country (e.g. US or DE) before starting the admin analysis."
        : "Bitte zuerst ein Steuerland (z. B. US oder DE) auswählen, bevor die Admin-Analyse startet."
    );
  }

  const y = String(year || "2025");

  const response = await fetch(apiUrl("/api/admin/analysis-dataset"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({
      year: Number(y),
      selectedCountry: adminCountry,
      plan,
      walletPairs,
    }),
  });

  const json = await response.json().catch(() => null);

  if (!response.ok || !json?.success || !json?.datasetId) {
    throw new Error(json?.error || "Admin dataset could not be created");
  }

  try {
    localStorage.setItem("v3tax_preview_mode", "false");
    localStorage.removeItem("previewWallets");
  } catch (_) {}

  try {
    sessionStorage.setItem(
      "preview_exchange_imports",
      JSON.stringify(exchangeRows || [])
    );
  } catch (_) {}

  setButtonMode("analyze");
  navigate(
    `/analyze/${json.datasetId}?year=${encodeURIComponent(y)}&country=${encodeURIComponent(adminCountry)}`,
    {
    state: {
      datasetId: json.datasetId,
      wallet: allForRun[0]?.wallet || mainWallet || wallet,
      delegateWallet:
        allForRun[0]?.delegateWallet ||
        data?.delegateWallet ||
        delegateWallet ||
        null,
      year: y,
      plan,
      selectedCountry: adminCountry,
      isPurchased: true,
      isPreview: false,
      fromViewAnalysis: false,
      loadFromSnapshot: false,
      autorun: "calc",
      previewExchangeRows: exchangeRows || [],
    },
  }
  );
};

const handleAnalyzeClick = async (e) => {
  e.preventDefault();

  // 🧹 Wallet-Adresse trimmen (Leerzeichen am Anfang/Ende entfernen)
  const trimmedWallet = wallet.trim();

  // 🔶 Pflichtfelder prüfen (Jahr, Plan, Zahlungsmethode, Land)
  // 🚀 Upgrade-Modus: Nur Wallet-Adresse prüfen, andere Felder sind bereits gesetzt
  const requiresPayment = !isAdminWallet && !isPaymentDisabled;
  if (upgradeMode && upgradeDatasetData) {
    if (!trimmedWallet) {
      setMissingFields({
        wallet: true,
        year: false,
        plan: false,
        payment: false,
        country: false,
      });
      setFormIncomplete(true);
      return;
    }
  } else if (!trimmedWallet || !year || !plan || (requiresPayment && !paymentMethod) || !country) {
  setMissingFields({
    wallet: !trimmedWallet,
    year: !year,
    plan: !plan,
    payment: requiresPayment && !paymentMethod,
    country: !country,
  });
  setFormIncomplete(true); // optional, falls du z. B. noch Gesamtwarnung nutzen willst
  return;
}

// Falls alles ausgewählt ist: Warnungen zurücksetzen
// 🚀 Upgrade-Modus: Payment-Feld nur prüfen wenn Limit überschritten ist
if (upgradeMode && upgradeDatasetData) {
  setMissingFields({
    wallet: false,
    year: false,
    plan: false,
    payment: false, // Im Upgrade-Modus wird payment erst geprüft wenn Upgrade Button geklickt wird
    country: false,
  });
} else {
setMissingFields({
  wallet: false,
  year: false,
  plan: false,
  payment: requiresPayment && !paymentMethod,
  country: false,
});
}

  setFormIncomplete(false); // Frühere Warnung zurücksetzen
  setShowNextWalletHint(false); // Hinweis zuverlässig ausblenden
  setError(""); // Alte Fehler entfernen
  // 🚀 WICHTIG: setIsDuplicateWallet(false) entfernt - der Hinweis soll bleiben, solange die verbotene Wallet im Eingabefeld steht

  // 👉 Reset aller veDelegate-Zustände bei neuer Analyse
  setManualVeDelegateMode(false);
  setManualVeDelegateInput("");
  setDelegateWallet(null);
  setDelegateCandidates([]);

  const isValid = /^0x[a-fA-F0-9]{40}$/.test(trimmedWallet);
  if (!isValid) {
    setError("Bitte gib eine gültige Vechain-Adresse ein.");
    setSubmittedWallet("");
    setWalletData(null);
    return;
  }

  // 🚀 Technisches Limit: max. 50 Wallets pro Set
  const currentCount = getCurrentWalletCount();
  if (currentCount >= MAX_WALLETS_PER_SET) {
    toast.error(
      language === 'en'
        ? 'Maximum of 50 wallets per set reached. Please remove a wallet from the set if you want to analyse another one.'
        : 'Maximal 50 Wallets pro Set erreicht. Bitte entferne zuerst eine Wallet aus dem Set, wenn du eine weitere analysieren möchtest.'
    );
    // Analyse gar nicht erst starten
    setIsAnalyzing(false);
    setIsNewAnalysis(false);
    return;
  }

  setError("");
  setSubmittedWallet(trimmedWallet);
  // 🧹 Getrimmte Wallet-Adresse auch im Eingabefeld anzeigen
  setWallet(trimmedWallet);
  setTransactionCount(null);
  setWalletData(null);
  setIsAnalyzing(true);
  setIsNewAnalysis(true); // 🚀 Flag für neue Analyse setzen
  if (showNextWalletHint) setShowNextWalletHint(false); // ✅ Hint ausblenden beim Start der Analyse

  // 🚀 WICHTIG: Variablen GANZ AM ANFANG der Funktion deklarieren, damit sie im gesamten Scope verfügbar sind
  let validDelegate = null;
  let validTxCount = 0;
  let delegateTxData = null;

  // Alten Browser-Waiter entfernen (Backend-SingleFlight läuft weiter, wenn andere Waiter da sind)
  analyzeAbortRef.current?.abort();
  const analyzeController = new AbortController();
  analyzeAbortRef.current = analyzeController;
  const analyzeSignal = analyzeController.signal;

  try {
    const analysisFlowDebug =
      String(import.meta.env?.VITE_ANALYSIS_FLOW_DEBUG || '') === '1';
    const analysisFlowLog = (msg, payload) => {
      if (!analysisFlowDebug) return;
      console.log('[ANALYSIS_FLOW]', msg, payload);
    };
    const analysisFlowT0 = performance.now();

    const tMain = performance.now();
    const TX_COUNT_MAX_ATTEMPTS = 8;
    let data = null;
    let response = null;

    for (let attempt = 1; attempt <= TX_COUNT_MAX_ATTEMPTS; attempt += 1) {
      if (analyzeSignal.aborted) {
        throw analyzeSignal.reason instanceof Error
          ? analyzeSignal.reason
          : new DOMException('Aborted', 'AbortError');
      }

      try {
        response = await fetch(
          apiUrl(`/api/v2/transaction-count?wallet=${trimmedWallet}&year=${year}`),
          { credentials: 'include', signal: analyzeSignal }
        );
        try {
          data = await response.json();
        } catch {
          data = null;
        }
      } catch (fetchErr) {
        if (analyzeSignal.aborted || fetchErr?.name === 'AbortError') {
          throw fetchErr;
        }
        response = null;
        data = null;
        analysisFlowLog('transaction-count network error', {
          attempt,
          error: fetchErr?.message || String(fetchErr),
        });
      }

      const ok = Boolean(response?.ok && data && typeof data.transactionCount === 'number');
      if (ok) break;

      const rateLimited =
        response?.status === 429 ||
        data?.code === 'VECHAINSTATS_RATE_LIMIT' ||
        /rate.?limit|API-Limit/i.test(String(data?.error || data?.details || ''));
      const retryable =
        !response ||
        rateLimited ||
        response.status >= 500 ||
        response.status === 408 ||
        response.status === 504;

      if (!retryable || attempt >= TX_COUNT_MAX_ATTEMPTS) {
        const msg = rateLimited
          ? (language === 'en'
              ? 'VeChainStats is temporarily rate-limiting NFT sync. Please wait a moment and analyse again — progress is saved.'
              : 'VeChainStats limitiert den NFT-Abruf gerade kurz. Bitte kurz warten und erneut analysieren – der Fortschritt bleibt erhalten.')
          : (language === 'en'
              ? 'Could not load wallet transaction counts. Please try again.'
              : 'Wallet-Transaktionszahlen konnten nicht geladen werden. Bitte erneut versuchen.');
        setError(msg);
        toast.error(msg);
        return;
      }

      const waitMs = Math.min(30_000, 2_000 * attempt);
      analysisFlowLog('transaction-count retry', {
        attempt,
        status: response?.status,
        rateLimited,
        waitMs,
      });
      if (attempt === 1) {
        toast.info(
          language === 'en'
            ? 'Still syncing NFT transfers (API busy). Staying on Analyse…'
            : 'NFT-Transfers werden noch synchronisiert (API ausgelastet). Analyse läuft weiter…'
        );
      }
      await new Promise((resolve, reject) => {
        const timer = setTimeout(resolve, waitMs);
        const onAbort = () => {
          clearTimeout(timer);
          reject(
            analyzeSignal.reason instanceof Error
              ? analyzeSignal.reason
              : new DOMException('Aborted', 'AbortError')
          );
        };
        if (analyzeSignal.aborted) {
          onAbort();
          return;
        }
        analyzeSignal.addEventListener('abort', onAbort, { once: true });
      });
    }

    analysisFlowLog('transaction-count main', {
      ms: Math.round(performance.now() - tMain),
      ok: response?.ok,
      transactionCount: data?.transactionCount,
      code: data?.code,
    });

    const count = data?.transactionCount;
    setTransactionCount(count);

    // 👇 veDelegate-Erkennung (FIX: kein Year-Loop, kein falsches Fallback)
    try {
      const tDelegate = performance.now();
      // Länger als frühere 9s: Backend braucht bis ~VEDELEGATE_DETECT_TIMEOUT_MS + parallele RPCs
      const { ok: delegateOk, json: delegateJson } = await fetchJsonWithClientTimeout(
        apiUrl(`/api/ve-delegate-detect?address=${trimmedWallet}`),
        22000
      );

      const candidatesPrecheck = Array.isArray(delegateJson?.veDelegateCandidates)
        ? delegateJson.veDelegateCandidates
        : [];
      const hasDelegateCandidatesPrecheck = candidatesPrecheck.length > 0;

      if (
        !hasDelegateCandidatesPrecheck &&
        (!delegateOk ||
          delegateJson?.status === 'temporarily_unavailable' ||
          delegateJson?.degraded === true)
      ) {
        const msg =
          language === 'en'
            ? 'veDelegate detection is temporarily unavailable. Please try again in a moment.'
            : 'veDelegate-Erkennung ist gerade temporär nicht verfügbar. Bitte versuche es gleich erneut.';
        setError(msg);
        toast.error(msg);
        setIsAnalyzing(false);
        setIsNewAnalysis(false);
        return;
      }

      const candidates = candidatesPrecheck;

      const legacyAllowEmptyDelegate =
        candidates.length === 0 &&
        delegateJson?.status == null &&
        delegateJson?.degraded !== true;

      if (
        candidates.length === 0 &&
        delegateJson?.status !== 'not_found_confirmed' &&
        !legacyAllowEmptyDelegate
      ) {
        const msg =
          language === 'en'
            ? 'veDelegate detection is temporarily unavailable. Please try again in a moment.'
            : 'veDelegate-Erkennung ist gerade temporär nicht verfügbar. Bitte versuche es gleich erneut.';
        setError(msg);
        toast.error(msg);
        setIsAnalyzing(false);
        setIsNewAnalysis(false);
        return;
      }

      analysisFlowLog('ve-delegate-detect', {
        ms: Math.round(performance.now() - tDelegate),
        ok: delegateOk,
        candidates: candidates.length,
        status: delegateJson?.status,
      });

      // Kandidaten fürs UI merken (manuelle Auswahl etc.)
      setDelegateCandidates(candidates);

      if (candidates.length === 0) {
        // Kein veDelegate -> sauber resetten
        setDelegateWallet(null);
        data.delegateWallet = null;
        data.delegateTxCount = 0;
        data.delegateBreakdown = { vet: 0, vtho: 0, b3tr: 0, nft: 0 };
      } else {
        // ✅ Nimm den "besten" Candidate (Backend kann bereits auf 1 reduzieren)
        const candidate = String(candidates[0]).toLowerCase();

        // ✅ Nur 1 Request fürs AUSGEWÄHLTE Jahr (kein Jahres-Spam)
        const tDelTx = performance.now();
        const txResp = await fetch(
          apiUrl(`/api/v2/transaction-count?wallet=${candidate}&year=${year}`),
          { credentials: 'include', signal: analyzeSignal }
        );
        delegateTxData = await txResp.json();

        analysisFlowLog('transaction-count delegate', {
          ms: Math.round(performance.now() - tDelTx),
          ok: txResp.ok,
          transactionCount: delegateTxData.transactionCount,
        });

        const txCount = delegateTxData.transactionCount ?? 0;

        setDelegateWallet(candidate);
        data.delegateWallet = candidate;
        data.delegateTxCount = txCount;

        data.delegateBreakdown = {
          vet: delegateTxData?.vet?.transactions || 0,
          vtho: delegateTxData?.vtho?.transactions || 0,
          b3tr: delegateTxData?.b3tr?.transactions || 0,
          nft: delegateTxData?.nft?.transactions || 0,
        };

              }
    } catch (e) {
            setDelegateCandidates([]);
      setDelegateWallet(null);
      data.delegateWallet = null;
      data.delegateTxCount = 0;
      data.delegateBreakdown = { vet: 0, vtho: 0, b3tr: 0, nft: 0 };
    }

    data.appContext = data.appContext || {};

    analysisFlowLog('analyze fetch phases total', {
      msTotal: Math.round(performance.now() - analysisFlowT0),
    });

    setWalletData({
      ...data,
      appContext: data.appContext,
      mainWalletTxCount:
        (data.vet.transactions ?? 0) +
        (data.vtho.transactions ?? 0) +
        (data.b3tr.transactions ?? 0) +
        (data.nft.transactions ?? 0),
      delegateWalletTxCount: data.delegateTxCount ?? 0,
      // 🚀 NEU: Speichere Delegate-Breakdown für Kategorien
      delegate: data.delegateBreakdown || {
        vet: 0,
        vtho: 0,
        b3tr: 0,
        nft: 0
      }
    });
    
    // 🎨 Animation nach erfolgreicher Analyse triggern (direkt nach setWalletData)
    setTxAnimKey(k => k + 1);

    // 🚀 UPGRADE-MODUS: Werte aufaddieren statt ersetzen
    if (upgradeMode && upgradeDatasetData) {
            
      // Transaktionen aufaddieren
      const newMainTxCount = (data.vet.transactions ?? 0) + (data.vtho.transactions ?? 0) + (data.b3tr.transactions ?? 0) + (data.nft.transactions ?? 0);
      const newDelegateTxCount = data.delegateTxCount ?? 0;
      const newTotalTxCount = newMainTxCount + newDelegateTxCount;
      
      // 🚀 WICHTIG: State-Updates zusammenfassen für korrekte Button-Logik
      const updatedFrozenTotal = frozenTxTotal + newTotalTxCount;
      
      setFrozenTxTotal(updatedFrozenTotal);
      setTotalTransactionCount(prev => {
        const newTotal = prev + newTotalTxCount;
        
        // 🚀 Button-Logik direkt hier berechnen mit neuen Werten
        // Verwende newTotal statt updatedFrozenTotal, da totalTransactionCount = frozenTxTotal + liveTx ist
        // Aber im Upgrade-Modus ist liveTx = 0, also newTotal = updatedFrozenTotal
        setTimeout(() => {
          // 🚀 Berechne neuen Wallet-Count (getCurrentWalletCount() berücksichtigt bereits alles)
          const newWalletCount = getCurrentWalletCount();
          
          // 🚀 Im Upgrade-Modus: Prüfe gegen ursprüngliche Limits
          // Im normalen Modus: Prüfe gegen aktuelle Plan-Limits
          const checkPlanLimit = upgradeMode && upgradeDatasetData ? originalPlanLimit : planLimit;
          
          // 🚀 Nur noch Transaktions-Limit relevant
          const txOk = checkPlanLimit == null || newTotal <= checkPlanLimit;
          
                    
          if (txOk) {
            // Set innerhalb des (alten/neuen) Plans → Wallet zum Set hinzufügen
            setButtonMode("addWalletToSet");
          } else {
            // Transaktionsvolumen des Sets > Planlimit → Upgrade anbieten
            setButtonMode("upgrade");
          }
        }, 100);
        
        return newTotal;
      });
      
      // Token-Transaktionen aufaddieren (Main + Delegate)
      setGlobalTotals(prev => ({
        vet: (prev.vet || 0) + (data.vet?.transactions || 0) + (data.delegateBreakdown?.vet || 0),
        vtho: (prev.vtho || 0) + (data.vtho?.transactions || 0) + (data.delegateBreakdown?.vtho || 0),
        b3tr: (prev.b3tr || 0) + (data.b3tr?.transactions || 0) + (data.delegateBreakdown?.b3tr || 0),
        nft: (prev.nft || 0) + (data.nft?.transactions || 0) + (data.delegateBreakdown?.nft || 0)
      }));
      
      // Wallet-Count erhöhen (+2 wenn beide, +1 wenn nur Main)
      const walletIncrement = (validDelegate || data.delegateWallet) ? 2 : 1;
      
          }

    // 🚀 UPGRADE-MODUS: Wallet zu analyzedWallets hinzufügen
    if (upgradeMode && upgradeDatasetData) {
      const newWalletEntry = {
        wallet: trimmedWallet,
        delegateWallet: data.delegateWallet || validDelegate || null,
        appContext: data.appContext || {},
        mainTxCount: (data.vet.transactions ?? 0) + (data.vtho.transactions ?? 0) + (data.b3tr.transactions ?? 0) + (data.nft.transactions ?? 0),
        veDelegateTxCount: data.delegateTxCount ?? 0,
        vet: (data.vet?.transactions || 0) + (data.delegateBreakdown?.vet || 0),
        vtho: (data.vtho?.transactions || 0) + (data.delegateBreakdown?.vtho || 0),
        b3tr: (data.b3tr?.transactions || 0) + (data.delegateBreakdown?.b3tr || 0),
        nft: (data.nft?.transactions || 0) + (data.delegateBreakdown?.nft || 0),
        name: `Wallet ${trimmedWallet.slice(0, 8)}...`,
        year: year,
        plan: plan,
        transactionCount: count
      };
      
      setAnalyzedWallets(prev => [...prev, newWalletEntry]);
            
      // 🚀 Button-Logik wird jetzt direkt im setFrozenTxTotal Handler gesetzt (siehe oben)
      
      // 🚀 Reset walletData nach dem Hinzufügen (damit der User eine neue Wallet eingeben kann)
      // ABER: Upgrade-Modus und buttonMode BEIBEHALTEN!
      setTimeout(() => {
        setWalletData(null);
        setWallet("");
        setSubmittedWallet("");
        setDelegateWallet(null);
        // WICHTIG: upgradeMode und buttonMode bleiben erhalten!
              }, 500);
      
      // 🚀 WICHTIG: Upgrade-Modus ENDE - nicht weiter im normalen Modus ausführen!
      setIsAnalyzing(false);
      setIsNewAnalysis(false);
      // 🎨 Animation nach erfolgreicher Analyse triggern
      setTxAnimKey(k => k + 1);
      return; // 🚨 KRITISCH: Verlasse die Funktion hier, damit normaler Modus nicht ausgeführt wird!
    } else {
  // 🚀 NORMALER MODUS: Standard-Verhalten
const currentAnalyzedWallets = [
  {
    wallet: trimmedWallet,
    delegateWallet: data.delegateWallet || null, // 🎯 VeDelegate-Wallet als Teil des Haupt-Wallets!
    appContext: data.appContext || {},
    name: `Wallet ${trimmedWallet.slice(0, 8)}...`,
    year: year,
    plan: plan,
    transactionCount: count
  }
];

// 🎯 WICHTIG: VeDelegate-Wallet wird NICHT als separates Objekt gespeichert,
// sondern als Teil des Haupt-Wallets, damit PaymentSuccess sie korrekt findet!

localStorage.setItem('analyzedWallets', JSON.stringify(currentAnalyzedWallets));


          // 👑 Admin: Wallet zum Set hinzufügen (mehrere Wallets möglich), dann CSV-Modal
          if (isAdminWallet) {
            const del = data.delegateWallet || null;
            if (isWalletComboAlreadyFrozen(trimmedWallet, del)) {
              toast.info(
                language === "en"
                  ? "This wallet pair is already in the admin set."
                  : "Dieses Wallet-Paar ist bereits im Admin-Set."
              );
            } else {
              const mainTx =
                (data.vet?.transactions ?? 0) +
                (data.vtho?.transactions ?? 0) +
                (data.b3tr?.transactions ?? 0) +
                (data.nft?.transactions ?? 0);
              const delegateTx = data.delegateTxCount ?? 0;
              const newWalletEntry = {
                wallet: trimmedWallet,
                delegateWallet: del,
                appContext: data.appContext || {},
                mainTxCount: mainTx,
                veDelegateTxCount: delegateTx,
                vet:
                  (data.vet?.transactions || 0) +
                  (data.delegateBreakdown?.vet || 0),
                vtho:
                  (data.vtho?.transactions || 0) +
                  (data.delegateBreakdown?.vtho || 0),
                b3tr:
                  (data.b3tr?.transactions || 0) +
                  (data.delegateBreakdown?.b3tr || 0),
                nft:
                  (data.nft?.transactions || 0) +
                  (data.delegateBreakdown?.nft || 0),
                name: `Wallet ${trimmedWallet.slice(0, 8)}...`,
                year: year,
                plan: plan,
                transactionCount: count,
              };

              setAnalyzedWallets((prev) => {
                const next = [...prev, newWalletEntry];
                toast.success(
                  language === "en"
                    ? `Wallet added to set (${next.length} total). Add more or continue with CSV.`
                    : `Wallet zum Set hinzugefügt (${next.length} gesamt). Weitere hinzufügen oder mit CSV fortfahren.`
                );
                return next;
              });
              setGlobalTotals((prev) => ({
                vet: (prev.vet || 0) + newWalletEntry.vet,
                vtho: (prev.vtho || 0) + newWalletEntry.vtho,
                b3tr: (prev.b3tr || 0) + newWalletEntry.b3tr,
                nft: (prev.nft || 0) + newWalletEntry.nft,
              }));
              setFrozenTxTotal((prev) => prev + mainTx + delegateTx);
            }

            setWallet("");
            setSubmittedWallet("");
            setWalletData(null);
            setDelegateWallet(null);
            setDelegateCandidates([]);
            setTransactionCount(null);
            setButtonMode("analyze");
            setShowNextWalletHint(true);
            setShowPreviewImports(true);
            setIsAnalyzing(false);
            setIsNewAnalysis(false);
            return;
          }

          // 🎯 NORMALE USER: Nach Analyse soll standardmäßig Preview aktiv sein
          setBuyPreviewMode("preview");
          setButtonMode("buy");
          
          // 🚀 Abschluss Normaler Modus (else-Branch)
          }
} catch (err) {
  if (err?.name !== 'AbortError' && err?.code !== 'ABORT_ERR') {
    setError("Fehler beim Abrufen der Wallet-Daten.");
  }
} finally {
  // Nur der aktuelle Analyze-Waiter darf den Busy-State zurücksetzen —
  // ein neuer Klick hat den alten Request bereits detached.
  if (analyzeAbortRef.current === analyzeController) {
    analyzeAbortRef.current = null;
    setIsAnalyzing(false);
    setIsNewAnalysis(false);
  }
}
};

const normalizeResidenceCountryForApi = (value) => {
  const c = normalizeCountryCode(value);
  return TAX_COUNTRY_CODES.includes(c) ? c : '';
};

const resolveResidenceCountryForApi = (value) => {
  const direct = normalizeResidenceCountryForApi(value);
  if (direct) return direct;

  const upper = String(value || '').trim().toUpperCase();
  const byName = getAvailableCountries().find(
    (c) => c.name.toUpperCase() === upper || c.code.toUpperCase() === upper
  );
  return byName?.code || '';
};

const readResidenceCountryFromDraft = () => {
  try {
    const raw = sessionStorage.getItem('v3tax:draft_wallet_set');
    if (!raw) return '';
    const draft = JSON.parse(raw);
    return resolveResidenceCountryForApi(draft?.country);
  } catch {
    return '';
  }
};

// ✅ Helper: Speichere 2024 Free Set
const saveFree2024Set = async (walletPairs, residenceCountryCode, planId) => {
  const countryCode = resolveResidenceCountryForApi(residenceCountryCode);
  if (!countryCode) {
    throw new Error('Tax country is required. Allowed values: DE, US, IT, UK.');
  }

  const response = await fetch('/api/wallet-sets/free-2024', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
    body: JSON.stringify({
      walletPairs,
      year: '2024',
      plan: planId ?? plan,
      residenceCountry: countryCode,
      country: countryCode,
      selectedCountry: countryCode,
    })
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
    throw new Error(errorData.message || 'Fehler beim Speichern des 2024 Sets');
  }

  return await response.json();
};

// ✅ Helper: Speichere Free-Plan Set für 2025/2026
const saveFreePlanSet = async (walletPairs, residenceCountryCode, planId, yearValue) => {
  const countryCode = resolveResidenceCountryForApi(residenceCountryCode);
  if (!countryCode) {
    throw new Error('Tax country is required. Allowed values: DE, US, IT, UK.');
  }

  const response = await fetch('/api/wallet-sets/free-plan', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
    body: JSON.stringify({
      walletPairs,
      year: yearValue ?? year,
      plan: planId ?? plan,
      residenceCountry: countryCode,
      country: countryCode,
      selectedCountry: countryCode,
    })
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
    throw new Error(errorData.message || 'Fehler beim Speichern des Free-Plan Sets');
  }

  return await response.json();
};

const handleAddToDashboard = async (e) => {
  // 🚀 FIX: Doppelklick-Schutz - verhindert parallele POSTs
  if (addInFlightRef.current) {
        return;
  }
  
  // Prüfe ob Wallet-Daten vorhanden sind oder bereits eine Wallet analysiert wurde
  if (!walletData && analyzedWallets.length === 0) {
    setError("Wallet-Daten fehlen – bitte zuerst analysieren.");
    return;
  }

  // 🔐 Prüfe Authentifizierung aus AuthContext (kein extra Auth-Check nötig!)
  if (!isAuthenticated || !user?.walletAddress) {
    // Zeige VeWorld Wallet Login Modal
    setShowWalletModal(true);
    return;
  }

  // ✅ Prüfe ob Plan ausgewählt ist (genauso wie bei normalem Buy)
  if (!plan) {
    setError("Bitte wähle zuerst einen Plan aus.");
    return;
  }

  // ✅ 2024: auch blocken, wenn TX-Limit des ausgewählten Plans überschritten ist
  if (isFreeYear && txExceededOnSelectedPlan) {
    setError(language === 'en'
      ? `Plan limit exceeded (${totalTransactionCount}/${planLimit}). Please choose a higher plan.`
      : `Plan-Limit überschritten (${totalTransactionCount}/${planLimit}). Bitte höheren Plan wählen.`);
    return;
  }

  // ✅ Free-Plan (>=2025): wie gehabt
  if (isFreePlanSelected && !isFreeYear && !canSaveFreePlan) {
    setError(language === 'en' 
      ? 'Free-Plan limit exceeded (100 transactions). Please upgrade.'
      : 'Free-Plan Limit überschritten (100 Transaktionen). Bitte upgraden.');
    return;
  }

  let selectedResidenceCountry =
    resolveResidenceCountryForApi(country) || readResidenceCountryFromDraft();

  if (!selectedResidenceCountry) {
    setError(language === 'en'
      ? 'Please select your tax country first.'
      : 'Bitte wähle zuerst dein Steuerland aus.');
    return;
  }

  addInFlightRef.current = true;
  
  try {
    // Sammle alle analysierten Wallets
    const allWalletsForPurchase = [...analyzedWallets];
    
    // Falls noch eine aktuelle Wallet vorhanden ist, auch diese hinzufügen
    if (wallet && !analyzedWallets.some(w => w.wallet === wallet)) {
      allWalletsForPurchase.push({
        wallet: wallet,
        delegateWallet: delegateWallet || null,
        mainTxCount: walletData?.mainWalletTxCount || 0,
        veDelegateTxCount: walletData?.delegateWalletTxCount || 0,
        vet: walletData?.vet?.transactions || 0,
        vtho: walletData?.vtho?.transactions || 0,
        b3tr: walletData?.b3tr?.transactions || 0,
        nft: walletData?.nft?.transactions || 0
      });
    }

    // Wallet-Paare für Backend erstellen
    const walletPairs = allWalletsForPurchase.map(w => ({
      mainWallet: w.wallet,
      delegateWallet: w.delegateWallet || null,
      mainTxCount: w.mainTxCount || 0,
      delegateTxCount: w.veDelegateTxCount || 0,
      vet: w.vet || 0,
      vtho: w.vtho || 0,
      b3tr: w.b3tr || 0,
      nft: w.nft || 0,
      appContext: w.appContext || {},
    }));

    let result;

    // ✅ Routing: 2024 → free-2024, Free-Plan → free-plan
    if (isFreeYear) {
      // 2024 bleibt wie bisher
            result = await saveFree2024Set(walletPairs, selectedResidenceCountry, plan);
            
      toast.success(
        language === 'en' 
          ? `🎉 ${walletPairs.length} wallet${walletPairs.length > 1 ? 's' : ''} added to your free 2024 set!` 
          : `🎉 ${walletPairs.length} Wallet${walletPairs.length > 1 ? 's' : ''} zum kostenlosen 2024 Set hinzugefügt!`,
        {
          position: "top-center",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        }
      );
    } else if (isFreePlanSelected) {
      // Free-Plan für 2025/2026
            result = await saveFreePlanSet(walletPairs, selectedResidenceCountry, plan, year);
            
      toast.success(
        language === 'en' 
          ? `🎉 ${walletPairs.length} wallet${walletPairs.length > 1 ? 's' : ''} added to your Free-Plan set!` 
          : `🎉 ${walletPairs.length} Wallet${walletPairs.length > 1 ? 's' : ''} zum Free-Plan Set hinzugefügt!`,
        {
          position: "top-center",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        }
      );
    } else {
      // Sollte nicht vorkommen, aber Fallback
            setError('Unerwarteter Fehler');
      return;
    }
    
    // analyzedWallets aufräumen
    localStorage.removeItem('analyzedWallets');
    
    // Dashboard öffnen
    setShowMyWalletsDashboard(true);

  } catch (error) {
        
    // Zeige klare Fehlermeldung bei 401
    if (error.message?.includes('401') || error.message?.includes('Not authenticated')) {
      const errorMsg = language === 'en' 
        ? 'Please log in first to add wallets to dashboard.'
        : 'Bitte zuerst einloggen.';
      toast.error(errorMsg);
      setError(errorMsg);
    } else {
      const errorMsg = error.message || (language === 'en' 
        ? 'Error adding wallets to dashboard'
        : 'Fehler beim Hinzufügen zum Dashboard');
      toast.error(errorMsg);
      setError(errorMsg);
    }
  } finally {
    // 🚀 FIX: Doppelklick-Schutz zurücksetzen
    addInFlightRef.current = false;
  }
};

// 🚀 Helper: Hole Breakdown-Daten von der API
const fetchBreakdown = async (address, year, { signal } = {}) => {
  if (!address) return { transactionCount: 0, vet: {transactions:0}, vtho:{transactions:0}, b3tr:{transactions:0}, nft:{transactions:0} };
  try {
    const r = await fetch(
      apiUrl(`/api/v2/transaction-count?wallet=${address}&year=${year}`),
      { credentials: 'include', signal: signal || analyzeAbortRef.current?.signal }
    );
    const j = await r.json();
    return {
      transactionCount: Number(j?.transactionCount || 0),
      vet:  { transactions: Number(j?.vet?.transactions  || 0) },
      vtho: { transactions: Number(j?.vtho?.transactions || 0) },
      b3tr: { transactions: Number(j?.b3tr?.transactions || 0) }, // VIP-180 -> b3tr
      nft:  { transactions: Number(j?.nft?.transactions  || 0) },
    };
  } catch (error) {
        return { transactionCount: 0, vet: {transactions:0}, vtho:{transactions:0}, b3tr:{transactions:0}, nft:{transactions:0} };
  }
};

// 🚀 Upgrade-Modus: Wallets zum bestehenden Set hinzufügen (nur neu analysierte Wallets)
const handleAddWalletsToSet = async () => {
  try {
    if (!upgradeMode || !upgradeDatasetData) return;

    // 1) Kandidaten: NUR die neu analysierten Wallets
    const newPairs = (analyzedWallets || [])
      .map(w => ({
        main_wallet: (w.wallet || "").toLowerCase(),
        delegate_wallet: w.delegateWallet ? String(w.delegateWallet).toLowerCase() : null,
        main_tx_count: Number(w.mainTxCount || 0),
        delegate_tx_count: Number(w.veDelegateTxCount || 0),
        vet_amount: Number(w.vet || 0),
        vtho_amount: Number(w.vtho || 0),
        b3tr_amount: Number(w.b3tr || 0),
        nft_count: Number(w.nft || 0),
        app_context: w.appContext || {},
      }))
      // Doppelte im selben Batch vorsichtig rausfiltern
      .filter((p, idx, arr) => idx === arr.findIndex(x => x.main_wallet === p.main_wallet));

    if (newPairs.length === 0) {
      toast.error(language === 'en' ? 'Please analyze wallet(s) first.' : 'Bitte zuerst Wallet(s) analysieren.');
      return;
    }

    setIsAnalyzing(true);
    if (showNextWalletHint) setShowNextWalletHint(false);

    const dsId = upgradeDatasetData?.datasetId;
    if (!dsId) {
            toast.error(t('toast.datasetIdMissing', language));
      setIsAnalyzing(false);
      return;
    }

    const res = await fetch(apiUrl(`/api/datasets/${dsId}/add-wallets`), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        walletPairs: newPairs,
        updateOnly: false,              // 🔑 jetzt Einfügen erlauben
        source: 'add-wallets-button'
      })
    });
    const json = await res.json().catch(() => null);

    if (!res.ok || !json?.success) {
            toast.error(language === 'en' ? 'Save failed' : 'Speichern fehlgeschlagen');
      setIsAnalyzing(false);
      return;
    }

    // LocalStorage pflegen (Preview im Dashboard)
    const existing = JSON.parse(localStorage.getItem('wallet_pairs') || '[]');
    const merged = [...existing];
    for (const nw of newPairs) {
      const i = merged.findIndex(p => (p.main_wallet||'').toLowerCase() === nw.main_wallet);
      if (i >= 0) merged[i] = { ...merged[i], ...nw };
      else merged.push(nw);
    }
    localStorage.setItem('wallet_pairs', JSON.stringify(merged));

    // UI aktualisieren
    window.dispatchEvent(new CustomEvent('datasets:refresh', { detail: { datasetId: dsId }}));

    // Aufräumen: neu analysierte Liste leeren & Zähler visuell zurücksetzen
    setAnalyzedWallets([]);
    setWallet('');
    setDelegateWallet(null);
    setWalletData(null);
    setSubmittedWallet('');
    setButtonMode("analyze");
    setShowNextWalletHint(true);
    setIsAnalyzing(false);

    toast.success(language === 'en' ? 'Wallet(s) added to dashboard.' : 'Wallet(s) zum Set hinzugefügt.');
  } catch (error) {
        toast.error(error.message || (language === 'en' ? 'Failed to add wallets to set' : 'Fehler beim Hinzufügen der Wallets zum Set'));
    setIsAnalyzing(false);
  }
};

const handlePreviewClick = async (e) => {
  e.preventDefault();

  // Prüfe ob Wallet-Daten vorhanden sind oder bereits eine Wallet analysiert wurde
  if (!walletData && analyzedWallets.length === 0) {
    setError(language === 'en' ? 'Wallet data missing – please analyze first.' : 'Wallet-Daten fehlen – bitte zuerst analysieren.');
    return;
  }

  // 🔐 NEU: Wie beim Kaufen → wenn nicht eingeloggt, Login-Modal öffnen und abbrechen
  if (!isAuthenticated) {
    setShowWalletModal(true);
    return;
  }

  // Pflichtfelder prüfen (ähnlich wie Buy, aber ohne Payment)
  if (!wallet || !year || !plan || !country) {
    setError(language === 'en' ? 'Wallet, year, plan and country are required.' : 'Wallet, Jahr, Plan und Land sind erforderlich.');
    return;
  }

  // ✅ Öffne Exchange-Imports-Modal im Preview-Mode
  setShowPreviewImports(true);
};

// ✅ Helper: Navigiere zur AnalysisApp mit Exchange-Imports (wird vom Modal aufgerufen)
const navigateToPreviewWithImports = (exchangeRows = []) => {
  if (!isAuthenticated) {
    setShowWalletModal(true);
    return;
  }

  const allWalletsForPreview = [...analyzedWallets];

  if (wallet && !allWalletsForPreview.some(w => w.wallet === wallet)) {
    allWalletsForPreview.push({
      wallet: wallet,
      delegateWallet: delegateWallet || null,
      name: `Wallet ${wallet.slice(0, 8)}...`,
      year: year,
      plan: plan,
      transactionCount: transactionCount || 0,
      mainTxCount: walletData?.mainWalletTxCount || 0,
      veDelegateTxCount: walletData?.delegateWalletTxCount || 0,
      vet: walletData?.vet?.transactions || 0,
      vtho: walletData?.vtho?.transactions || 0,
      b3tr: walletData?.b3tr?.transactions || 0,
      nft: walletData?.nft?.transactions || 0
    });
  }

  const y = String(year || "2025");

  try {
    const draft = {
      wallet,
      delegateWallet: delegateWallet || null,
      year: y,
      plan,
      country,
      paymentMethod,
      buyPreviewMode,
      analyzedWallets: allWalletsForPreview,
    };
    sessionStorage.setItem("v3tax:draft_wallet_set", JSON.stringify(draft));
      } catch (err) {
      }

  localStorage.setItem('previewWallets', JSON.stringify(allWalletsForPreview));
  localStorage.setItem('v3tax_preview_mode', 'true');

  try {
    sessionStorage.setItem("preview_exchange_imports", JSON.stringify(exchangeRows || []));
  } catch (_) {
      }

  navigate(`/analyze?year=${encodeURIComponent(y)}&preview=1`, {
    state: {
      wallet: allWalletsForPreview[0]?.wallet || wallet,
      year: y,
      plan: "preview",
      delegateWallet: allWalletsForPreview[0]?.delegateWallet || delegateWallet || null,
      selectedCountry: country,
      isPurchased: false,
      isPreview: true,
      wallets: allWalletsForPreview,
      previewExchangeRows: exchangeRows || [],
      previewFlow: true,
      autorun: "calc"
    }
  });
};

/** Alle Wallets für Admin-/Preview-Lauf (Set + ggf. aktuell analysierte, noch nicht im Set). */
const collectAllWalletsForAnalysis = () => {
  const out = [...analyzedWallets];

  const trimmed = String(wallet || "").trim();
  if (trimmed && walletData) {
    const del = delegateWallet || walletData?.delegateWallet || null;
    const already = out.some(
      (w) =>
        String(w.wallet || "").toLowerCase() === trimmed.toLowerCase() &&
        String(w.delegateWallet || "").toLowerCase() === String(del || "").toLowerCase()
    );
    if (!already) {
      out.push({
        wallet: trimmed,
        delegateWallet: del,
        name: `Wallet ${trimmed.slice(0, 8)}...`,
        year: year,
        plan: plan,
        transactionCount: transactionCount || 0,
        mainTxCount: walletData?.mainWalletTxCount || 0,
        veDelegateTxCount: walletData?.delegateWalletTxCount || 0,
        vet:
          (walletData?.vet?.transactions || 0) +
          (walletData?.delegate?.vet || 0),
        vtho:
          (walletData?.vtho?.transactions || 0) +
          (walletData?.delegate?.vtho || 0),
        b3tr:
          (walletData?.b3tr?.transactions || 0) +
          (walletData?.delegate?.b3tr || 0),
        nft:
          (walletData?.nft?.transactions || 0) +
          (walletData?.delegate?.nft || 0),
      });
    }
  }

  return out;
};

const navigateToAdminFullAnalysisWithImports = async (exchangeRows = []) => {
  try {
    await handleAdminDirectAnalysis({ exchangeRows });
  } catch (err) {
    toast.error(
      err?.message ||
        (language === "en"
          ? "Admin analysis could not be started."
          : "Admin-Analyse konnte nicht gestartet werden.")
    );
  }
};

const handleBuyClick = async (e) => {
  e.preventDefault();

  // ✅ Force-CTA löschen, sobald der User wirklich kaufen will
  setForceCta(null);

  // Preview-Mode deaktivieren beim normalen Buy/Kauf Flow
  localStorage.setItem('v3tax_preview_mode', 'false');

  // Prüfe ob Wallet-Daten vorhanden sind oder bereits eine Wallet analysiert wurde
  if (!walletData && analyzedWallets.length === 0) {
    setError("Wallet-Daten fehlen – bitte zuerst analysieren.");
    return;
  }

  // 🔐 Prüfe Authentifizierung
  if (!isAuthenticated) {
    // Zeige VeWorld Wallet Login Modal
    setShowWalletModal(true);
    return;
  }

  // ✅ User ist authentifiziert - prüfe ob Plan und Zahlungsmethode bereits ausgewählt sind
    
  // 🚀 Falls Plan oder Zahlungsmethode fehlt: Fehler anzeigen (außer bei 2024)
  if (!plan) {
    setError("Bitte wähle zuerst einen Plan aus.");
    return;
  }
  
  if (!paymentMethod && !isFreeYear) {
    setError("Bitte wähle zuerst eine Zahlungsmethode aus.");
    return;
  }

  const residenceCountryForPurchase = resolveResidenceCountryForApi(country);
  if (!residenceCountryForPurchase) {
    setMissingFields((prev) => ({ ...prev, country: true }));
    setError(
      language === 'en'
        ? 'Please select your tax country before buying.'
        : 'Bitte wähle vor dem Kauf dein Steuerland aus.'
    );
    return;
  }
  
  // 🚀 Alle Werte vorhanden - direkt zur Zahlung!
  const selectedPlan = plan;
  const selectedPaymentMethod = paymentMethod;
  const selectedYear = year || '2025';
  
  
  // 🚀 Zahlungsmethode prüfen und entsprechend verarbeiten
  if (['vet', 'b3tr', 'vtho'].includes(paymentMethod)) {
            
    try {
      // 🚀 NEU: Automatisch die neue automatische Transaktion starten
                  
      // 🎯 WICHTIG: Token korrekt setzen
      setSelectedToken(paymentMethod);
            
      // Neue automatische Transaktion starten
      // 🎯 Plan kann String oder Objekt sein - extrahiere den Namen
      const planName = typeof plan === 'string' ? plan : plan.name;
            await handleCryptoPayment(planName, year, 1, paymentMethod, residenceCountryForPurchase);
      return; // Beende hier, da wir das neue Modal öffnen
      
      // 🚨 ALTER CODE (wird nicht mehr ausgeführt):
      // 🚀 Sammle ALLE analysierten Wallets (Main + Delegate)
      const walletsList = [];
      
      // Alle analyzedWallets hinzufügen
      for (const analyzedWallet of analyzedWallets) {
        walletsList.push(analyzedWallet.wallet);
        if (analyzedWallet.delegateWallet) {
          walletsList.push(analyzedWallet.delegateWallet);
        }
      }
      
      // Falls noch eine aktuelle Wallet vorhanden ist, auch diese hinzufügen
      if (wallet && !analyzedWallets.some(w => w.wallet === wallet)) {
        walletsList.push(wallet);
        if (delegateWallet) walletsList.push(delegateWallet);
      }
      
      // Duplikate entfernen
      const uniqueWallets = [...new Set(walletsList.filter(Boolean))];
      
      // 🚀 WICHTIG: Speichere ALLE analyzedWallets im localStorage für PaymentSuccess!
      const allWalletsForPurchase = [...analyzedWallets];
      
      // Falls noch eine aktuelle Wallet vorhanden ist, auch diese hinzufügen
      if (wallet && !analyzedWallets.some(w => w.wallet === wallet)) {
        allWalletsForPurchase.push({
          wallet: wallet,
          delegateWallet: delegateWallet || null,
          mainTxCount: walletData?.mainWalletTxCount || 0,
          veDelegateTxCount: walletData?.delegateWalletTxCount || 0,
          vet: walletData?.vet?.transactions || 0,
          vtho: walletData?.vtho?.transactions || 0,
          b3tr: walletData?.b3tr?.transactions || 0,
          nft: walletData?.nft?.transactions || 0
        });
      }
      
      // Im localStorage speichern - das findet PaymentSuccess!
      localStorage.setItem('analyzedWallets', JSON.stringify(allWalletsForPurchase));
      
      // 🎯 KRITISCH: Speichere auch als wallet_pairs für den Transaction Monitor!
      const walletPairs = allWalletsForPurchase.map(w => ({
        main_wallet: w.wallet,
        delegate_wallet: w.delegateWallet || null,
        label: w.label || null,
        chain: 'vechain',
        // 🎯 KRITISCH: Transaktionsdaten mit übertragen! (korrekte Feldnamen)
        main_tx_count: w.mainTxCount || 0,
        delegate_tx_count: w.veDelegateTxCount || 0,
        vet_amount: w.vet || 0,
        vtho_amount: w.vtho || 0,
        b3tr_amount: w.b3tr || 0,
        nft_count: w.nft || 0,
        app_context: w.appContext || {},
      }));
      localStorage.setItem('wallet_pairs', JSON.stringify(walletPairs));
      
                  
      // 🚀 Krypto-Checkout über neue Crypto-Route erstellen
      const response = await fetch(apiUrl('/api/crypto/create-checkout-session'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          plan: plan,
          year: year,
          walletCount: uniqueWallets.length,
          walletAddresses: uniqueWallets,
          preferredToken: 'vet' // Standard: VET, kann später auf b3tr/vtho erweitert werden
        })
      });

      if (response.ok) {
        const result = await response.json();
                
        // 🚀 Eigene Krypto-Checkout-Seite anzeigen
        if (result.paymentAddresses) {
          setShowCryptoCheckout(true);
          setCryptoCheckoutData(result);
          
          // 🚨 ALTER CODE (wird nicht mehr ausgeführt):
          // 🕐 Session-Timer starten (240 Sekunden) - ALTE SESSION BEENDEN!
          if (result.sessionStartTime && result.sessionDuration) {
            // 🚨 WICHTIG: Alte Session beenden falls vorhanden
            if (sessionTimer) {
              clearInterval(sessionTimer);
              setSessionTimer(null);
              setTimeRemaining(0);
            }
            
            const startTime = result.sessionStartTime;
            const duration = result.sessionDuration;
            
            const timer = setInterval(() => {
              const elapsed = Date.now() - startTime;
              const remaining = Math.max(0, duration - elapsed);
              setTimeRemaining(remaining);
              
              if (remaining <= 0) {
                clearInterval(timer);
                setSessionTimer(null);
                setTimeRemaining(0);
                // Session abgelaufen - Modal schließen
                setShowCryptoCheckout(false);
                setCryptoCheckoutData(null);
              }
            }, 1000);
            
            setSessionTimer(timer);
            setTimeRemaining(duration);
          }
          
          // 🆕 NEU: Blockchain-Überwachung automatisch starten
          try {
            const preferredToken = 'vet'; // Standard: VET
            const expectedAmount = result.paymentAmounts[preferredToken];
            
            if (expectedAmount) {
              await cryptoPaymentAPI.startBlockchainMonitoring(
                result.paymentId, 
                expectedAmount, 
                preferredToken
              );
                          }
          } catch (error) {
                      }
        } else {
          throw new Error('Keine Krypto-Adressen erhalten');
        }
        
      } else {
        const error = await response.json();
                setError(error.error || 'VET payment setup failed');
      }

    } catch (error) {
            setError('VET payment error. Please try again.');
    }
    
  } else if (paymentMethod === 'creditcard') {
        
    try {
      // 🚀 Sammle ALLE analysierten Wallets (Main + Delegate)
      const walletsList = [];
      
      // Alle analyzedWallets hinzufügen
      for (const analyzedWallet of analyzedWallets) {
        walletsList.push(analyzedWallet.wallet);
        if (analyzedWallet.delegateWallet) {
          walletsList.push(analyzedWallet.delegateWallet);
        }
      }
      
      // Falls noch eine aktuelle Wallet vorhanden ist, auch diese hinzufügen
      if (wallet && !analyzedWallets.some(w => w.wallet === wallet)) {
        walletsList.push(wallet);
        if (delegateWallet) walletsList.push(delegateWallet);
      }
      
      // Duplikate entfernen
      const uniqueWallets = [...new Set(walletsList.filter(Boolean))];
      
      // 🚀 WICHTIG: Speichere ALLE analyzedWallets im localStorage für PaymentSuccess!
      const allWalletsForPurchase = [...analyzedWallets];
      
      // Falls noch eine aktuelle Wallet vorhanden ist, auch diese hinzufügen
      if (wallet && !analyzedWallets.some(w => w.wallet === wallet)) {
        allWalletsForPurchase.push({
          wallet: wallet,
          delegateWallet: delegateWallet || null,
          mainTxCount: walletData?.mainWalletTxCount || 0,
          veDelegateTxCount: walletData?.delegateWalletTxCount || 0,
          vet: walletData?.vet?.transactions || 0,
          vtho: walletData?.vtho?.transactions || 0,
          b3tr: walletData?.b3tr?.transactions || 0,
          nft: walletData?.nft?.transactions || 0
        });
      }
      
      // Im localStorage speichern - das findet PaymentSuccess!
      localStorage.setItem('analyzedWallets', JSON.stringify(allWalletsForPurchase));
      
      // 🎯 KRITISCH: Speichere auch als wallet_pairs für den Transaction Monitor!
      const walletPairs = allWalletsForPurchase.map(w => ({
        main_wallet: w.wallet,
        delegate_wallet: w.delegateWallet || null,
        label: w.label || null,
        chain: 'vechain',
        // 🎯 KRITISCH: Transaktionsdaten mit übertragen! (korrekte Feldnamen)
        main_tx_count: w.mainTxCount || 0,
        delegate_tx_count: w.veDelegateTxCount || 0,
        vet_amount: w.vet || 0,
        vtho_amount: w.vtho || 0,
        b3tr_amount: w.b3tr || 0,
        nft_count: w.nft || 0,
        app_context: w.appContext || {},
      }));
      localStorage.setItem('wallet_pairs', JSON.stringify(walletPairs));
      
                  
      // Safety: Plan immer persistieren (unabhängig von Modal/Select)
      localStorage.setItem('selectedPlan', typeof plan === 'string' ? plan : plan?.name || '');
      
      // Stripe Checkout Session erstellen
      const response = await fetch(apiUrl('/api/stripe/create-checkout-session'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          plan: plan,
          year: year,
          walletCount: uniqueWallets.length,
          walletAddresses: uniqueWallets
        })
      });

      if (response.ok) {
        const result = await response.json();
                
        // 🚀 Weiterleitung zu Stripe Checkout
        window.location.href = result.url;
        
      } else {
        const error = await response.json();
                setError(error.error || 'Payment setup failed');
      }

    } catch (error) {
            setError('Payment error. Please try again.');
    }
  }
};

  // 🎯 Smooth Drag für Auto-Transaction-Modal – identisch zur Logik aus MyWalletsDashboard
  useEffect(() => {
    if (showAutoTransactionModal) {
      setAutoModalPos({ x: 0, y: 0 });
    }
  }, [showAutoTransactionModal, setAutoModalPos]);

  const getAutoModalStyle = () => {
    if (isHandheldDevice) {
      return {
        width: '100%',
        maxWidth: '590px',
      };
    }

    return {
      transform: `translate(${dragAutoModal.pos.x}px, ${dragAutoModal.pos.y}px)`,
    };
  };

  // 🎯 Copy to Clipboard Function für Wallet Adresse
  const copyWalletAddressToClipboard = async () => {
    try {
      if (autoTransactionData?.to) {
        await navigator.clipboard.writeText(autoTransactionData.to);
                // Erfolgs-Status setzen für visuelles Feedback
        setCopySuccess(true);
        // Nach 2 Sekunden zurücksetzen
        setTimeout(() => setCopySuccess(false), 2000);
      }
    } catch (err) {
          }
  };

  // 📋 Copy helper with toast
  const copyAddress = async (text) => {
    try {
      if (!text) return;
      await navigator.clipboard.writeText(text);
      if (window.toast) {
        window.toast.success(t('toast.addressCopied', language));
      }
    } catch (err) {
      if (window.toast) {
        window.toast.error(t('toast.copyFailed', language));
      }
    }
  };

// ✅ ENTFERNT: handleCalculateClick wurde durch setShowMyWalletsDashboard(true) ersetzt

  // 👉 Automatische Button-Umschaltung bei Plan-Wechsel (nach Analyse)
  useEffect(() => {
    // ⛔️ Im Upgrade-Modus NICHT in die normale Button-Logik eingreifen
    if (upgradeMode && upgradeDatasetData) {
      return;
    }
    
    const limit = currentPlanLimits.transactions;
    // 🚀 WICHTIG: Nur Button-Modus ändern wenn bereits eine Wallet analysiert wurde UND nicht gerade analysiert wird UND keine neue Analyse gestartet wird!
    if (transactionCount !== null && walletData && !isAnalyzing && !isNewAnalysis) {
      // 🚀 ADMIN-WALLET PRÜFUNG: Prüfe ob eingeloggte Wallet eine Admin-Wallet ist
      if (wallet && year && isAuthenticated) {
        if (isAdminWallet) {
          // 👑 Admin: Button bleibt auf Analyze (kein Dashboard-Umweg)
          setButtonMode("analyze");
                    return;
        }
        
        // 🎯 NORMALE USER: Prüfe Limits und setze Button-Modus
        // 🚨 WICHTIG: Verwende totalTransactionCount statt transactionCount für Konsistenz mit Plakette
        const transactionsExceeded = (totalTransactionCount ?? 0) > limit;
        // Nur noch für Logging interessant, kein Plan-Limit mehr:
        const walletsExceeded = currentWalletCount > walletLimit;
        
        // 🚨 Paywall: nur noch Transaktions-Limit relevant
        if (transactionsExceeded) {
          setButtonMode("analyze");
                  } else {
          setButtonMode("buy");
                  }
      }
    }
  }, [
    plan,
    transactionCount,
    wallet,
    year,
    isAuthenticated,
    user?.walletAddress,
    walletData,
    isAnalyzing,
    isNewAnalysis,
    currentWalletCount,
    walletLimit,
    upgradeMode,
    upgradeDatasetData, // ⬅️ neu in den Dependencies
  ]);



  // 🔍 Smart Button Logic - Prüfe gekaufte Wallets in Real-time (HÖCHSTE PRIORITÄT)
  // ✅ FIX: BUY wird auch gesetzt, wenn analyzedWallets vorhanden sind (auch ohne walletData)
  //        (Preview-Return restored meist nur Draft/Wallet-Set, walletData ist dann oft noch null)
  useEffect(() => {
    // ⛔️ Im Upgrade-Modus eigene Button-Logik, daher hier nichts tun
    if (upgradeMode) {
            return;
    }

    const checkWalletStatus = () => {
      const hasAnyAnalysis = analyzedWallets.length > 0 || !!walletData;

      
      // 🧱 Hard Guard: Noch keine Analyse → Analyze
      if (!hasAnyAnalysis) {
        if (buttonMode !== "analyze") {
          setButtonMode("analyze");
                  }
        return;
      }

      // ⛔️ Während Analyse/NewAnalysis nicht dazwischenfunken
      if (isAnalyzing || isNewAnalysis) return;

      // ⛔️ Ohne Year bleibt es bei Analyze
      // Auth ist NICHT nötig – BUY öffnet bei Bedarf das Login-Modal
      if (!year) {
        if (buttonMode !== "analyze") setButtonMode("analyze");
        return;
      }

      // 👑 Admin: niemals auf Dashboard umschalten (direkter Analyse-Flow)
      if (isAdminWallet && walletData) {
        if (buttonMode !== "analyze") {
          setButtonMode("analyze");
                  }
        return;
      }

      // ✅ Standard: Sobald Analyse vorhanden → BUY (auch ohne walletData)
      if (buttonMode !== "buy") {
        setButtonMode("buy");
              }
    };

    checkWalletStatus();
  }, [
    wallet,
    year,
    isAuthenticated,
    user?.walletAddress,
    walletData,
    isAnalyzing,
    isNewAnalysis,
    upgradeMode,
    upgradeDatasetData,
    analyzedWallets,
    buttonMode,
    delegateWallet,
  ]);

  // 🚀 WICHTIG: Lokale Authentifizierung bei DAppKit-Verbindung setzen
  useEffect(() => {
    if (account && source === 'wallet-connect' && !localStorage.getItem('v3tax_auth')) {
            
      // 🚀 WICHTIG: Lokale Authentifizierung für VeWorld-Integration setzen
      const walletAddress = account.includes(':') ? account.split(':').pop() : account;
      const authData = {
        walletAddress: walletAddress,
        plan: 'free', // Standard-Plan
        isDev: false,
        authenticated: true,
        loginTime: new Date().toISOString()
      };
      localStorage.setItem('v3tax_auth', JSON.stringify(authData));
          }
  }, [account, source]);

  // 🚀 WICHTIG: Funktion zum Setzen der lokalen Authentifizierung
  const setLocalAuthForVeWorld = () => {
    // 🚀 WICHTIG: Lokale Authentifizierung für VeWorld-Integration setzen
    const walletAddress = wallet || (account && account.includes(':') ? account.split(':').pop() : account);
    
    if (walletAddress) {
      const derivedPlan = latestPlan?.plan || user?.plan || plan || 'free';

      const authData = {
        walletAddress: walletAddress,
        plan: derivedPlan,
        isDev: false,
        authenticated: true,
        loginTime: new Date().toISOString()
      };
      localStorage.setItem('v3tax_auth', JSON.stringify(authData));
            
      // 🔹 UI sofort aktualisieren (Header zeigt Wallet direkt)
      applyLocalAuth(authData);
      
      // 🔹 Trigger Event für expliziten Same-Tab-Update
      window.dispatchEvent(new Event('v3tax-auth'));
      
      return true;
    }
    return false;
  };

  // ✅ 1x zentraler Handler für "Wallet zum Set hinzufügen"
  const handleAddWalletToSet = async () => {
    // ✅ Wenn User nach Preview-Return jetzt Wallets hinzufügen will:
    // Hinweis "Zurück aus Vorschau → Jetzt kaufen" sofort ausblenden
    if (showPreviewBuyHint) setShowPreviewBuyHint(false);

    // Wenn keine aktuelle Wallet-Analyse läuft, starte den "Add Wallet" Modus
    if (!walletData) {
      // reset / prepare new analysis
      setWallet("");
      setSubmittedWallet("");
      setWalletData(null);
      setDelegateWallet(null);
      setTransactionCount(null);
      setIsNewAnalysis(true);   // blocks the effect that re-derives BUY
      setButtonMode("analyze"); // force blue button
      setShowNextWalletHint(true);
      return;
    }

    // Wenn eine Wallet-Analyse läuft, füge sie zuerst zur Liste hinzu
    if (submittedWallet) {
      // Prüfe ob das Wallet Transaktionen hat
      const totalTransactions = (mainTxCount ?? 0) + (veDelegateTxCount ?? 0);
      if (totalTransactions === 0) {
        // ✅ Erlauben: Wallet darf auch ohne Transaktionen hinzugefügt werden (z.B. neue Wallets / reine Holding Wallets).
        // Nur als Hinweis anzeigen, aber NICHT blockieren.
        if (window.toast?.info) {
          window.toast.info(
            language === 'en'
              ? 'This wallet has no transactions to analyze (yet). You can still add it.'
              : 'Diese Wallet hat (noch) keine Transaktionen zum Analysieren. Du kannst sie trotzdem hinzufügen.',
            { duration: 5000 }
          );
        } else {
                  }
      }

      // Duplikat-Prüfung
      const norm = (x) => (x || "").trim().toLowerCase();
      if (analyzedWallets.some(w => norm(w.wallet) === norm(submittedWallet))) {
        // Wallet bereits vorhanden - einfach Felder leeren
      } else {
        // Wallet zum Set hinzufügen
        setAnalyzedWallets(prev => [
          {
            wallet: submittedWallet,
            delegateWallet,
            mainTxCount,
            veDelegateTxCount,
            vet: (walletData.vet?.transactions ?? 0) + (walletData.delegate?.vet ?? 0),
            vtho: (walletData.vtho?.transactions ?? 0) + (walletData.delegate?.vtho ?? 0),
            b3tr: (walletData.b3tr?.transactions ?? 0) + (walletData.delegate?.b3tr ?? 0),
            nft: (walletData.nft?.transactions ?? 0) + (walletData.delegate?.nft ?? 0),
          },
          ...prev
        ]);
        setGlobalTotals(prev => ({
          vet: prev.vet + (walletData.vet?.transactions ?? 0) + (walletData.delegate?.vet ?? 0),
          vtho: prev.vtho + (walletData.vtho?.transactions ?? 0) + (walletData.delegate?.vtho ?? 0),
          b3tr: prev.b3tr + (walletData.b3tr?.transactions ?? 0) + (walletData.delegate?.b3tr ?? 0),
          nft: prev.nft + (walletData.nft?.transactions ?? 0) + (walletData.delegate?.nft ?? 0),
        }));
        setFrozenTxTotal(prev => prev + (mainTxCount ?? 0) + (veDelegateTxCount ?? 0));
      }
    }

    // IMMER: Eingabefeld leeren und zurück zum "Analyze"-Modus
    setWallet("");
    setSubmittedWallet("");
    setWalletData(null);
    setDelegateWallet(null);
    setTransactionCount(null);
    setIsNewAnalysis(true);   // prevents the smart-button effect from flipping back to BUY
    setButtonMode("analyze"); // Button zurück zu "Analyze"
    setShowNextWalletHint(true); // Hinweis anzeigen
    
    // Scroll zum Eingabefeld
    setTimeout(() => {
      const el = document.getElementById('wallet-input');
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.focus?.();
      }
    }, 100);
  };

  return (
    <>
      {/* 🎨 Header mit Navigation */}
      <div
        className="app-header v3-shell fixed top-0 left-1/2 z-40 flex h-[54px] -translate-x-1/2 items-center justify-between border border-white/10 bg-gradient-to-r from-[#2563eb] via-[#2458dc] to-[#1e40af] px-3 sm:px-4 print:hidden shadow-[0_10px_28px_rgba(15,23,42,0.16)] backdrop-blur-sm"
      >
        {/* Mobile Header */}
        <div className="flex items-center gap-3 md:hidden force-mobile">
          <button
            onClick={toggleMobileMenu}
            className="text-white text-2xl flex items-center justify-center focus:outline-none focus-visible:ring-2 focus-visible:ring-white/70"
            aria-label={language === 'en' ? 'Toggle navigation' : 'Navigation öffnen'}
          >
            ☰
          </button>
          <div className="flex flex-col leading-tight">
            <span className="mobile-logo-title text-sm font-semibold text-white">V3Tax</span>
            <span className="mobile-logo-subtitle text-[11px] text-blue-100">{APP_VERSION_LABEL}</span>
          </div>
        </div>

        {/* Dropdown-Menü links (Desktop) */}
        {/* Dropdown-Menü */}
        <div className="relative dropdown-container hidden md:block force-desktop">
          <button 
            onClick={() => setShowDropdown(!showDropdown)}
            className="text-white hover:text-blue-100 px-2.5 py-1.5 rounded-lg transition-all duration-300 flex items-center gap-2 hover:bg-white/10 group"
          >
            <svg className="w-5 h-5 group-hover:rotate-12 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
            <span className="text-sm font-medium">Menu</span>
          </button>
          
                  {showDropdown && (
          <div 
            className="fixed top-[54px] left-0 right-0 bg-white shadow-2xl border-t border-b border-gray-200 z-50 header-mega-menu" 
            style={{ width: '100%' }}
          >
            <div className="v3-shell">
              <div className="flex w-full">
                {/* Navigation (links) - kompakter */}
                <div className="bg-gradient-to-r from-blue-50 to-blue-100 px-4 py-2 border-r border-gray-200 flex items-center flex-shrink-0 min-w-[140px]">
                  <h3 className="text-xs font-semibold text-gray-700">
                    {language === 'en' ? 'Navigation' : 'Navigation'}
                  </h3>
                </div>

                {/* 5 Buttons (rechts) - gleich groß, flex: 1 */}
                <div className="flex flex-1 gap-0">
                  {/* Funktion */}
                  <button
                    onClick={() => { setShowFunctionModal(true); setShowDropdown(false); }}
                    className="flex-1 text-left px-4 py-3 h-[56px] text-gray-700 hover:bg-blue-50 transition-colors duration-200 font-semibold min-w-0 flex flex-col justify-center leading-tight border-l-4 border-transparent hover:border-blue-400 border-r border-gray-100 last:border-r-0"
                  >
                    <div className="text-sm font-semibold leading-tight whitespace-normal">
                      {language === 'en' ? 'How V3Tax Works' : 'Wie V3Tax funktioniert'}
                    </div>
                    <div className="text-xs text-gray-500 leading-tight whitespace-normal">
                      {language === 'en' ? 'Functions & Features' : 'Funktionen & Features'}
                    </div>
                  </button>

                  {/* My Wallets */}
                  <button
                    onClick={() => { setShowMyWalletsDashboard(true); setShowDropdown(false); }}
                    className="flex-1 text-left px-4 py-3 h-[56px] text-gray-700 hover:bg-blue-50 transition-colors duration-200 font-semibold min-w-0 flex flex-col justify-center leading-tight border-l-4 border-transparent hover:border-blue-400 border-r border-gray-100 last:border-r-0"
                  >
                    <div className="text-sm font-semibold leading-tight whitespace-normal">{t('myWallets', language)}</div>
                    <div className="text-xs text-gray-500 leading-tight whitespace-normal">{t('manageActiveWallets', language)}</div>
                  </button>

                  {/* Roadmap */}
                  <button
                    onClick={() => { setShowRoadmap(true); setShowDropdown(false); }}
                    className="flex-1 text-left px-4 py-3 h-[56px] text-gray-700 hover:bg-blue-50 transition-colors duration-200 font-semibold min-w-0 flex flex-col justify-center leading-tight border-l-4 border-transparent hover:border-blue-400 border-r border-gray-100 last:border-r-0"
                  >
                    <div className="text-sm font-semibold leading-tight whitespace-normal">{language === 'en' ? 'Roadmap' : 'Roadmap'}</div>
                    <div className="text-xs text-gray-500 leading-tight whitespace-normal">
                      {language === 'en' ? 'Planned features & limitations' : 'Geplante Features & Limits'}
                    </div>
                  </button>

                  {/* Legal Notices */}
                  <button
                    onClick={() => { setShowLegalModal(true); setShowDropdown(false); }}
                    className="flex-1 text-left px-4 py-3 h-[56px] text-gray-700 hover:bg-blue-50 transition-colors duration-200 font-semibold min-w-0 flex flex-col justify-center leading-tight border-l-4 border-transparent hover:border-blue-400 border-r border-gray-100 last:border-r-0"
                  >
                    <div className="text-sm font-semibold leading-tight whitespace-normal">{t('legalNotices', language)}</div>
                    <div className="text-xs text-gray-500 leading-tight whitespace-normal">{t('termsAndPrivacy', language)}</div>
                  </button>

                  {/* Help & Support */}
                  <button
                    onClick={() => {
                      setHelpActiveSection("overview");
                      setShowHelpModal(true);
                      setShowDropdown(false);
                    }}
                    className="flex-1 text-left px-4 py-3 h-[56px] text-gray-700 hover:bg-blue-50 transition-colors duration-200 font-semibold min-w-0 flex flex-col justify-center leading-tight border-l-4 border-transparent hover:border-blue-400 border-r border-gray-100 last:border-r-0"
                  >
                    <div className="text-sm font-semibold leading-tight whitespace-normal">{t('helpAndSupport', language)}</div>
                    <div className="text-xs text-gray-500 leading-tight whitespace-normal">{t('getHelpAndDocumentation', language)}</div>
                  </button>
                </div>
              </div>
            </div>
          </div>
          )}
        </div>

        {/* Right actions – mobile only */}
        <div className="ml-auto flex items-center gap-2 md:hidden">
          <button
            type="button"
            onClick={handleWalletLoginClick}
            className="
              flex items-center justify-center w-8 h-8
              rounded-lg
              bg-white/10 hover:bg-white/20
              transition-all duration-200
              shadow-sm hover:shadow
              focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/30
            "
            aria-label={language === 'en' ? 'Login' : 'Login'}
          >
            <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
            </svg>
          </button>
          <button
            type="button"
            onClick={() => changeLanguage(language === 'de' ? 'en' : 'de')}
            className="
              inline-flex items-center justify-center h-8
              px-3 rounded-lg
              bg-white/10 hover:bg-white/20
              text-white text-xs font-semibold
              transition-all duration-200
              shadow-sm hover:shadow
              focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/30
            "
            aria-label={language === 'en' ? 'Switch language' : 'Sprache wechseln'}
          >
            {language?.toUpperCase?.() || 'EN'}
          </button>
        </div>

        {/* Titel in der Mitte */}
        <div className="absolute left-1/2 transform -translate-x-1/2 hidden md:flex force-desktop items-center gap-2 header-title">
          <span className="text-white text-lg font-semibold hover:text-blue-100 transition-all duration-500 relative overflow-hidden group">
            <span className="relative z-20">VeChain</span>
            
            {/* ✨ Eleganter Glow-Effekt */}
            <div className="absolute inset-0 bg-gradient-to-r from-blue-400 via-blue-500 to-blue-600 opacity-0 group-hover:opacity-30 transition-all duration-700 rounded-lg blur-lg scale-110 group-hover:scale-100"></div>
            
            {/* 🌊 Sanfte Wellen-Animation */}
            <div className="absolute inset-0 bg-gradient-to-r from-blue-300 via-blue-400 to-blue-500 opacity-0 group-hover:opacity-20 transition-all duration-1000 rounded-lg blur-xl scale-125 group-hover:scale-110 group-hover:animate-[gentleWave_3s_ease-in-out_infinite]"></div>
            
            {/* 💎 Subtiler Glanz */}
            <div className="absolute inset-0 bg-gradient-to-r from-blue-200 via-blue-300 to-blue-400 opacity-0 group-hover:opacity-15 transition-all duration-1200 rounded-lg blur-2xl scale-150 group-hover:scale-120"></div>
          </span>
          <span className="text-blue-100 text-sm">•</span>
          <span className="text-blue-100 text-sm font-medium">Blockchain Analysis</span>
          <span className="text-blue-200 text-xs bg-blue-700 px-2 py-1 rounded-full ml-2">{APP_VERSION_LABEL}</span>
        </div>

        {/* Controls rechts - kompakter */}
        <div className="hidden md:flex force-desktop items-center gap-2">
          {/* User Account Status */}
          {isAuthenticated && user ? (
            <div className="relative">
              <div
                className="flex items-center gap-1.5 bg-white/10 px-2.5 py-1 rounded-lg cursor-pointer hover:bg-white/20 transition-all duration-200"
                onClick={() => setShowUserDropdown(!showUserDropdown)}
              >
                <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                <span className="text-white text-xs font-medium">
                  {user.walletAddress?.slice(0, 6)}...{user.walletAddress?.slice(-4)}
                </span>
                {/* 🆕 NEU: Zeige den aktuellen Plan als Icon */}
                <div className="flex items-center">
                  <PlanBadge
                    planId={user?.isDev ? 'dev' : planToUse}
                    size="xxs"
                    variant="compact"
                    title={currentPlanInfo?.name || (user?.plan ? formatPlanDisplay(user.plan) : (planToUse ? formatPlanDisplay(planToUse) : ''))}
                  />
                </div>
                <svg
                  className={`w-3 h-3 text-white ml-1 transition-transform duration-200 ${showUserDropdown ? "rotate-180" : ""}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>

              {/* Dropdown Menu */}
              {showUserDropdown && (() => {
                // --- Account Dropdown: consistent typography / UI tokens ---
                const DD_LABEL = "text-[11px] font-semibold uppercase tracking-wider";
                const DD_VALUE = "text-sm font-semibold text-slate-900 leading-5";
                const DD_CARD =
                  "bg-white border border-slate-200 rounded-xl p-3 shadow-sm hover:shadow-md hover:border-sky-300 transition-all duration-200";
                const DD_ICON_BTN =
                  "inline-flex h-8 w-8 items-center justify-center rounded-lg border border-blue-200 bg-blue-50 text-blue-700 " +
                  "hover:bg-blue-100 hover:border-blue-300 transition-colors " +
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-300 " +
                  "disabled:opacity-50 disabled:pointer-events-none";
                const DD_DOT =
                  "w-2.5 h-2.5 bg-gradient-to-r from-green-400 to-emerald-500 rounded-full shadow-sm ring-2 ring-white";

                return (
                  <div
                    className="absolute right-0 mt-3 w-[460px] max-w-[95vw] bg-white rounded-2xl shadow-xl border border-gray-100 z-50 overflow-hidden"
                  >
                                    <div className="p-6 border-b border-gray-100 bg-gradient-to-r from-gray-50 to-blue-50/20">
                    <div className="flex items-center gap-4 mb-5">
                      {/* 🎯 GEILER PLAN-INDICATOR */}
                      <PlanBadge
                        planId={user?.isDev ? 'dev' : planToUse}
                        size="lg"
                        title={currentPlanInfo?.name || (user?.plan ? formatPlanDisplay(user.plan) : (planToUse ? formatPlanDisplay(planToUse) : ''))}
                      />
                      
                      <div className="flex-1">
                        <div className="font-bold text-gray-800 text-xl mb-2">
                          {language === 'en' ? 'Connected Wallet' : 'Verbundene Wallet'}
                        </div>
                        <div className="text-sm text-gray-600 font-medium">
                          {language === 'en' ? 'Premium Account' : 'Premium-Account'}
                        </div>
                                              </div>
                      </div>
                      
                                            <div className="space-y-4">
                        {/* ✅ Wallet Address – one line + inline copy */}
                        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                          <div className={`${DD_LABEL} text-slate-500`}>
                            {language === "en" ? "Wallet Address" : "Wallet-Adresse"}
                          </div>

                          <div className="mt-2 flex items-center justify-between gap-3">
                            <div className="overflow-x-auto flex-1 min-w-0">
                              <div
                                className="font-mono text-sm font-semibold text-slate-900 whitespace-nowrap"
                                title={user?.walletAddress || ""}
                              >
                                {user?.walletAddress}
                              </div>
                            </div>

                            <button
                              onClick={() => copyAddress(user?.walletAddress)}
                              className={`${DD_ICON_BTN} flex-shrink-0`}
                              title={language === "en" ? "Copy address" : "Adresse kopieren"}
                              aria-label={language === "en" ? "Copy address" : "Adresse kopieren"}
                              type="button"
                            >
                              <Copy className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                        
                        {/* Karten unterhalb der Wallet-Adresse */}
                        <div className="grid grid-cols-3 gap-3">
                          {/* User ID (fix: value aligned, copy inline) */}
                          <div className={DD_CARD}>
                            <div className={`${DD_LABEL} text-slate-500`}>
                              {language === "en" ? "User ID" : "User-ID"}
                            </div>

                            <div className="mt-1 flex items-center justify-between gap-2">
                              <div className={`${DD_VALUE} tabular-nums`}>{user?.userId ?? "-"}</div>

                              <button
                                onClick={() => user?.userId && copyAddress(String(user.userId))}
                                className={DD_ICON_BTN}
                                title={language === "en" ? "Copy user ID" : "User-ID kopieren"}
                                aria-label={language === "en" ? "Copy user ID" : "User-ID kopieren"}
                                type="button"
                              >
                                <Copy className="w-4 h-4" />
                              </button>
                            </div>
                          </div>

                          {/* Plan */}
                          <div className={DD_CARD}>
                            <div className={`${DD_LABEL} text-blue-600`}>
                              {language === "en" ? "Plan" : "Plan"}
                            </div>
                            <div className={`${DD_VALUE} mt-1`}>{currentPlanInfo?.name || (user?.plan ? formatPlanDisplay(user.plan) : (planToUse ? formatPlanDisplay(planToUse) : "Free"))}</div>
                          </div>

                          {/* Status (fix: dot left of text, not top-right) */}
                          <div className={DD_CARD}>
                            <div className={`${DD_LABEL} text-emerald-600`}>
                              {language === "en" ? "Status" : "Status"}
                            </div>

                            <div className="mt-1 flex items-center gap-2">
                              <span className={DD_DOT} />
                              <span className={DD_VALUE}>{language === "en" ? "Active" : "Aktiv"}</span>
                            </div>
                          </div>
                        </div>
                        
                        {/* 🎯 GEILES KAUFDATUM - KOMPAKTER UND ELEGANT */}
                        {latestPlan?.purchaseDate && (
                          <div className="rounded-xl border border-emerald-200 bg-emerald-50/60 p-3 shadow-sm">
                            <div className="flex items-center justify-between gap-3">
                              <span className={`${DD_LABEL} text-emerald-700`}>
                                {language === "en" ? "Purchased" : "Gekauft"}
                              </span>

                              <span className="text-sm font-semibold text-emerald-900 tabular-nums">
                                {new Date(latestPlan.purchaseDate).toLocaleDateString(
                                  language === "de" ? "de-DE" : "en-US"
                                )}
                              </span>
                            </div>
                          </div>
                        )}
                      
                      {user.isDev && (
                        <div className="bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 border-2 border-gradient-to-r from-blue-300 via-indigo-400 to-purple-400 rounded-xl p-3 shadow-sm hover:shadow-md transition-all duration-300 group">
                          <div className="flex items-center gap-3">
                            <div className="relative">
                              <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center shadow-md group-hover:shadow-lg transition-shadow">
                                <span className="text-white text-sm">👑</span>
                              </div>
                              {/* Subtle glow effect */}
                              <div className="absolute inset-0 bg-gradient-to-br from-blue-400 to-indigo-500 rounded-full opacity-20 blur-sm group-hover:opacity-30 transition-opacity"></div>
                            </div>
                            
                            <div className="flex-1">
                              <div className="text-blue-700 font-bold text-sm group-hover:text-blue-800 transition-colors">
                                Admin
                              </div>
                              <div className="text-indigo-600 text-xs font-medium opacity-80">
                                {language === 'en' ? 'Premium Access' : 'Premium-Zugriff'}
                              </div>
                            </div>
                            
                            {/* Elegant status dot */}
                            <div className="w-2.5 h-2.5 bg-gradient-to-br from-blue-400 to-indigo-500 rounded-full shadow-sm"></div>
                          </div>
                        </div>
                      )}

                      {isAdminWallet ? (
                        <button
                          type="button"
                          onClick={() => {
                            setShowUserDropdown(false);
                            window.dispatchEvent(new CustomEvent("v3tax:open-admin-dashboard"));
                          }}
                          className="w-full rounded-xl border border-blue-200 bg-gradient-to-r from-blue-50 to-indigo-50 px-4 py-3 text-left shadow-sm transition hover:border-blue-300 hover:shadow-md"
                        >
                          <div className="text-sm font-bold text-blue-800">
                            {language === "en" ? "Platform Activity" : "Platform-Aktivität"}
                          </div>
                          <div className="mt-0.5 text-xs text-slate-600">
                            {language === "en"
                              ? "Logins, previews & calculations"
                              : "Logins, Previews & Berechnungen"}
                          </div>
                        </button>
                      ) : null}

                      {isAdminWallet ? (
                        <button
                          type="button"
                          onClick={() => {
                            setShowUserDropdown(false);
                            window.dispatchEvent(
                              new CustomEvent("v3tax:open-b2b-invoices")
                            );
                          }}
                          className="mt-2 w-full rounded-xl border border-emerald-200 bg-gradient-to-r from-emerald-50 to-teal-50 px-4 py-3 text-left shadow-sm transition hover:border-emerald-300 hover:shadow-md"
                        >
                          <div className="text-sm font-bold text-emerald-800">
                            B2B Rechnungen
                          </div>
                          <div className="mt-0.5 text-xs text-slate-600">
                            Kunden, Entwürfe & Rechnungen
                          </div>
                        </button>
                      ) : null}
                    </div>
                  </div>
                  
                  <div className="p-6 space-y-3 bg-gradient-to-r from-gray-50/50 to-blue-50/20 border-t border-gray-100">

                    
                    {/* 🆕 Account wechseln Button - NUR bei VeWorld (WalletConnect) Verbindungen */}
                    {source === 'wallet-connect' && (
                      <button
                        onClick={startFreshVeWorldSwitch}
                        className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2.5 px-4 rounded-lg transition-all duration-200 flex items-center justify-center gap-2"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                        {language === 'en' ? 'Switch Account' : 'Account wechseln'}
                      </button>
                    )}
                    


                    {/* Wallet trennen Button */}
                    <button
                      onClick={logoutFromV3TaxOnly}
                      className="w-full bg-emerald-500 hover:bg-emerald-600 text-white font-semibold py-2.5 px-4 rounded-xl shadow-sm hover:shadow-md transition-all duration-200 flex items-center justify-center gap-2"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                      </svg>
                      {language === 'en' ? 'Disconnect Wallet' : 'Wallet trennen'}
                    </button>
                  </div>
                </div>
                );
              })()}
            </div>
          ) : (
            /* Offline/Disconnected Status */
            <button
              type="button"
              onClick={handleWalletLoginClick}
              className="
                group inline-flex items-center gap-2 h-8
                px-3 rounded-lg
                bg-white/10 hover:bg-white/20
                text-white
                transition-all duration-200
                shadow-sm hover:shadow
                focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/30
              "
              aria-label={language === "en" ? "Login" : "Anmelden"}
              aria-expanded={showWalletModal ? "true" : "false"}
            >
              <span className="text-white text-sm font-medium whitespace-nowrap">
                {language === "en" ? "Login" : "Login"}
              </span>

              {/* Link/Icon */}
              <svg className="w-4 h-4 text-white/90 group-hover:text-white transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
              </svg>

              {/* Chevron wie Language (rotiert wenn Modal offen) */}
              <svg
                className={`w-3 h-3 text-white/90 transition-transform duration-200 ${showWalletModal ? "rotate-180" : ""}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
          )}
          
          {/* Language Switcher - kompakt */}
          <button
            type="button"
            onClick={() => changeLanguage(language === 'de' ? 'en' : 'de')}
            className="
              inline-flex items-center justify-center h-8
              px-3 rounded-lg
              bg-white/10 hover:bg-white/20
              text-white text-sm font-medium
              transition-all duration-200
              shadow-sm hover:shadow
              focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/30
            "
            aria-label={language === 'en' ? 'Switch language' : 'Sprache wechseln'}
          >
            {language?.toUpperCase?.() || 'EN'}
          </button>
        </div>
      </div>

      {isMobileMenuOpen && (
        <div className="md:hidden" role="dialog" aria-modal="true">
          <div
            className="fixed inset-0 z-40 bg-slate-900/25"
            onClick={closeMobileMenu}
          />

          <nav className="fixed inset-x-0 top-[56px] z-50 px-3 pb-4">
            <ul
              className="mobile-nav-panel rounded-2xl border border-blue-100 bg-white shadow-2xl overflow-y-auto divide-y"
              style={{ maxHeight: 'calc(100dvh - 80px)' }}
            >
              {mobileNavItems.map((item) => (
                <li key={item.key}>
                  <button
                    onClick={() => {
                      item.onPress();
                    }}
                    className="w-full text-left px-4 py-3 hover:bg-blue-50 transition"
                  >
                    <div className="text-[15px] font-medium text-slate-800">{item.label}</div>
                    {item.description && (
                      <div className="text-xs text-slate-500">{item.description}</div>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          </nav>
        </div>
      )}

      {/* 🎉 Wallet Connected Notification */}
      {showWalletConnectedNotification && (
        <div className="fixed top-20 left-1/2 transform -translate-x-1/2 z-50 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg animate-pulse">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 bg-green-200 rounded-full"></div>
            <span className="font-medium">
              {language === 'en' ? 'Wallet Connected Successfully!' : 'Wallet erfolgreich verbunden!'}
            </span>
            <div className="text-green-200 text-sm">
              {user?.walletAddress?.slice(0, 6)}...{user?.walletAddress?.slice(-4)}
            </div>
          </div>
        </div>
      )}

      {/* 🎉 Purchase Success Notification - Genau wie andere Toasts */}
      {showPurchaseSuccessNotification && (
        <div className="fixed top-20 left-1/2 transform -translate-x-1/2 z-50 bg-white border border-green-200 text-green-800 px-6 py-3 rounded-lg shadow-lg">
          <div className="flex items-center gap-3">
            <svg className="w-5 h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            <span className="font-medium text-sm">
              {purchaseSuccessMessage}
            </span>
          </div>
        </div>
      )}

      <div className="min-h-screen v3-page-bg">
        <div
          className={`v3-shell v3-main-sheet pt-0 pb-10 ${landingReturnShellClass}`}
        >

        <div className="hero-strip relative overflow-hidden print:hidden -mb-7 mt-0 group">
          {/* Hintergrund-Gradienten – vertikal schmaler (nur Fläche, Logo/Copy unverändert) */}
          <div
            className="
              pointer-events-none
              absolute inset-x-0
              top-10 bottom-10 sm:top-11 sm:bottom-11 lg:top-12 lg:bottom-12
              z-0
              bg-gradient-to-br from-blue-50 via-white to-blue-100 opacity-70
            "
          ></div>

          <div
            className="
              pointer-events-none
              absolute inset-x-0
              top-10 bottom-10 sm:top-11 sm:bottom-11 lg:top-12 lg:bottom-12
              z-0
              bg-gradient-to-r from-blue-200/40 via-transparent to-blue-600/10
            "
          ></div>

          {/* Hover-Radial: exakt gleiche Fläche wie Gradienten — endet nicht unter dem Band */}
          <div
            className="
              hero-hover-radial
              pointer-events-none
              absolute inset-x-0
              top-10 bottom-10 sm:top-11 sm:bottom-11 lg:top-12 lg:bottom-12
              z-[1]
              opacity-0 group-hover:opacity-100
              transition-opacity duration-500
              [transform:translateZ(0)]
            "
            style={{
              willChange: 'opacity',
              WebkitMaskImage: 'linear-gradient(to right, transparent 0, black 10%, black 100%)',
              maskImage: 'linear-gradient(to right, transparent 0, black 10%, black 100%)'
            }}
          >
            <div
              className="h-full w-full bg-[radial-gradient(140%_110%_at_-12%_50%,rgba(219,39,119,0.22),rgba(99,102,241,0.18)_42%,transparent_72%)]"
            />
          </div>

          {/* Inhalt – Hero (klassische Proportionen, Ausrichtung Logo ↔ Copy) */}
          <div className="relative z-10 flex flex-col items-center gap-2 py-0 sm:py-0.5 lg:py-0 px-4 sm:px-6 lg:px-8 hero-section lg:flex-row lg:justify-between lg:items-center">
            <div
              className={`${getLandingIntroClass('hero-brand')} w-full max-w-[260px] sm:max-w-[340px] lg:max-w-[490px] relative group`}
              style={{
                '--intro-delay': '90ms',
                '--intro-x': '30px',
                '--intro-y': '14px',
                '--intro-scale': '0.985',
              }}
            >
              <img
                src={
                  shouldUseMobileBrandHeroLogo()
                    ? V3TAX_MOBILE_LOGO_PATH
                    : desktopLogoCopy
                }
                alt="V3Tax Logo"
                className={
                  shouldUseMobileBrandHeroLogo()
                    ? "relative h-16 w-16 rounded-2xl object-contain drop-shadow-lg sm:h-20 sm:w-20"
                    : "relative w-full h-auto object-contain transition-all duration-500 ease-out drop-shadow-lg translate-y-[10px] scale-90 sm:scale-95 lg:scale-[0.9] lg:translate-x-[82px] lg:translate-y-[3px] lg:group-hover:scale-[0.95] lg:group-hover:translate-x-[82px] lg:group-hover:translate-y-[-2px]"
                }
              />
            </div>

            <div
              className={`${getLandingIntroClass('hidden lg:block w-full max-w-[640px] relative text-center hero-copy')}`}
              style={{
                '--intro-delay': '170ms',
                '--intro-x': '30px',
                '--intro-y': '18px',
                '--intro-scale': '0.992',
              }}
            >
              <div className="lg:transform lg:scale-[1.05] lg:translate-y-[18px] lg:translate-x-[-92px]">
                <h1 className="text-[26px] sm:text-[30px] lg:text-[34px] leading-[1.06] tracking-[-0.02em] font-bold text-blue-700 mb-3 text-center group">
                  <span className="group-hover:text-blue-800 group-hover:scale-105 group-hover:drop-shadow-lg transition-all duration-300 ease-out">
                    {t('ultimateAioWalletAnalysis', language)}
                  </span>
                </h1>
                <p className="text-gray-800 text-sm sm:text-base mb-6 max-w-2xl whitespace-normal lg:whitespace-nowrap">
                  {t('enterWalletAddressDescription', language)}
                </p>
                <div className="flex justify-center items-center gap-3">
                  <button
                    onClick={() => setShowModal(true)}
                    className="group inline-flex items-center gap-2 px-8 sm:px-10 lg:px-12 py-3 rounded-full bg-white/80 text-blue-800 text-sm font-semibold border border-blue-100/80 shadow-sm backdrop-blur-sm hover:bg-white hover:border-blue-300 hover:shadow-lg hover:-translate-y-0.5 active:translate-y-0 transition-all duration-200"
                  >
                    <span className="flex h-6 w-6 items-center justify-center rounded-full bg-blue-50 group-hover:bg-blue-100 transition-colors">
                      <CreditCard className="w-3.5 h-3.5 text-blue-600" />
                    </span>
                    <span>{t('pricingAndFeatures', language)}</span>
                  </button>
                  {/* 🎯 Trustpilot Widget - Zum Wiedereinfügen einfach auskommentieren: */}
                  {/* <TrustpilotReviewCollector language={language} /> */}
                </div>
              </div>
            </div>
          </div>

          {/* Dezente Linie genau an der unteren Kante des Gradient-Bands (gleiche bottom-* wie die Layer oben — nicht bottom-0, sonst sitzt sie im weißen Streifen darunter) */}
          <div
            className="
              absolute inset-x-0
              h-px
              bottom-10 sm:bottom-11 lg:bottom-12
              bg-gradient-to-b from-transparent via-blue-200/25 to-transparent
            "
          ></div>
        </div>

        {/* Wallet Connection entfernt - wird jetzt über Modal beim Buy Button gehandelt */}

        <div className="landing-content-shell relative z-10">
        <div
          className={getLandingIntroClass(
            `mt-[calc(2.5rem-30px)] sm:mt-[calc(4rem-30px)] relative ${
              isLandingFormDropdownOpen ? 'z-[140]' : 'z-30'
            }`
          )}
          style={{
            '--intro-delay': '250ms',
            '--intro-y': '20px',
            '--intro-scale': '0.996',
          }}
        >
          {/* 🚀 Upgrade-Modus Badge – jetzt direkt am Formular dran */}
          {upgradeMode && upgradeDatasetData && (
            <div className="mb-3 inline-flex items-center gap-4 rounded-md bg-purple-50 border border-purple-200 px-3 py-2 text-xs text-purple-800">
              <span>
                Wallet-set<strong>#{upgradeDatasetData.datasetId || ''}</strong> - Upgrade
              </span>
              <button
                onClick={() => {
                                    
                  // Reset Upgrade-Modus States - kompletter Reset wie bei Startseite
                  setUpgradeMode(false);
                  setUpgradeDatasetData(null);
                  setIsUpgradeAnalyzing(false);
                  setShowNextWalletHint(false);
                  setWallet("");
                  setWalletData(null);
                  setSubmittedWallet("");
                  setDelegateWallet(null);
                  setTransactionCount(null);
                  setTotalTransactionCount(null);
                  setFrozenTxTotal(0);
                  setAnalyzedWallets([]);
                  setGlobalTotals({ vet: 0, vtho: 0, b3tr: 0, nft: 0 });
                  setPlan("");
                  setYear("");
                  setPaymentMethod("");
                  setButtonMode("analyze");
                  
                  // Öffne MyWalletsDashboard
                  setShowMyWalletsDashboard(true);
                }}
                className="text-purple-700 hover:text-purple-900 font-medium underline transition-colors"
              >
                {language === 'en' ? 'Cancel' : 'Abbrechen'}
              </button>
            </div>
          )}
          <form
            onSubmit={async (e) => {
              e.preventDefault();

              // Im Upgrade-Mode NICHT über submit arbeiten, sondern über onClick des Buttons
              if (upgradeMode && (buttonMode === 'addWalletToSet' || buttonMode === 'upgrade')) {
                return;
              }

              // Normaler Modus: Standard-Analyse-Logik
              if (buttonMode === "analyze") {
                await handleAnalyzeClick(e);
              } else if (buttonMode === "buy") {
                // 🧪 Preview-Mode (nur wenn Toggle aktiv)
                if (buyPreviewMode === "preview") {
                  await handlePreviewClick(e);
                } else {
                  // ✅ Button-Logik: 2024 oder Free-Plan → AddToDashboard, sonst Buy
                  const showAddToDashboard = isFreeYear || isFreePlanSelected;
                  if (showAddToDashboard) {
                    await handleAddToDashboard(e);
                  } else {
                    await handleBuyClick(e);
                  }
                }
              } else if (buttonMode === "dashboard") {
                setShowMyWalletsDashboard(true);
              } else {
                await handleAnalyzeClick(e);
              }
            }}
            className="flex flex-col gap-2 mb-4"
          >
            {/* Warnung GANZ OBEN IM FORMULAR, EIGENE ZEILE - bleibt sichtbar solange isDuplicateWallet === true - KEIN BLINKEN */}
            {isDuplicateWallet && (
              <div className="max-w-[230px] w-full flex items-center text-yellow-700 bg-yellow-100 px-4 py-2 mb-2 rounded gap-2 text-sm font-medium">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-yellow-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1 4v-4m0 0h-1v-4h1m0 0h1m-1 0V5a2 2 0 114 0v4" />
                </svg>
                {t('walletAlreadyAnalyzed', language)}
              </div>
            )}

            <div
              className={`flex flex-col lg:flex-row items-stretch lg:items-center gap-4 flex-wrap lg:flex-nowrap relative overflow-visible mobile-form-grid pb-2 ${
                isLandingFormDropdownOpen ? 'z-[160]' : ''
              }`}
            >

  {/* WALLET */}
<div className="flex flex-col relative flex-1 min-w-[260px] lg:min-w-0 w-full full-row">
      {missingFields.wallet && !isPurchasedWalletLoaded && (
    <div className="absolute -top-6 left-0 z-10 flex items-center text-yellow-700 bg-yellow-100 px-2 py-0.5 rounded text-xs font-medium shadow-sm whitespace-nowrap animate-pulse">
      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-yellow-500 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1 4v-4m0 0h-1v-4h1m0 0h1m-1 0V5a2 2 0 114 0v4" />
      </svg>
      {t('fieldRequired', language)}
    </div>
  )}
  <input
    id="wallet-input"
    type="text"
    placeholder="e. g. 0x3c1d3cd2a46543cec3ef586h1fcd8cb3821b9c5e"
    className="h-[46px] w-full rounded-xl border border-slate-200 bg-white/95 px-3.5 text-sm text-slate-700 shadow-[0_1px_2px_rgba(15,23,42,0.02)] transition-all duration-200 placeholder:text-slate-400 hover:border-slate-300 hover:shadow-[0_6px_18px_rgba(15,23,42,0.06)] focus:border-blue-500 focus:outline-none focus:ring-4 focus:ring-blue-500/10"
    value={wallet || ''}
    onChange={handleWalletChange}
  />

  </div>

  {/* PLAN */}
<div className={`flex flex-col relative w-full md:w-auto ${showPlanDropdown ? 'z-[170]' : ''}`}>
    {missingFields.plan && !isPurchasedWalletLoaded && (
              <div className="absolute -top-6 left-0 z-10 flex items-center text-yellow-700 bg-yellow-100 px-2 py-0.5 rounded text-xs font-medium shadow-sm whitespace-nowrap animate-pulse">
        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-yellow-500 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1 4v-4m0 0h-1v-4h1m0 0h1m-1 0V5a2 2 0 114 0v4" />
        </svg>
        {t('fieldRequired', language)}
      </div>
    )}
    {/* 🚀 CUSTOM PLAN DROPDOWN - MIT BLAUEN HOVER-EFFEKTEN */}
    <div className="relative custom-dropdown overflow-visible">
      <button
        type="button"
        onClick={() => {
          // Während Analyse gesperrt
          if (isAnalyzing || isUpgradeAnalyzing) return;
          // Im Upgrade-Modus: nur erlauben, wenn Upgrade wirklich nötig ist
          if (upgradeMode && !upgradeRequiredOnOriginalPlan) return;
          setShowPlanDropdown(!showPlanDropdown);
        }}
        disabled={
          // Während Analyse gesperrt
          isAnalyzing || isUpgradeAnalyzing ||
          // Im Upgrade-Modus: nur freigeben, wenn Upgrade wirklich nötig ist
          (upgradeMode && !upgradeRequiredOnOriginalPlan)
        }
        className={`h-[46px] w-full md:w-[170px] rounded-xl border border-slate-200 bg-white/95 px-3.5 text-left text-sm text-slate-700 shadow-[0_1px_2px_rgba(15,23,42,0.02)] flex items-center justify-between transition-all duration-200 hover:border-slate-300 hover:shadow-[0_6px_18px_rgba(15,23,42,0.06)] focus:outline-none focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 ${
          (isAnalyzing || isUpgradeAnalyzing || (upgradeMode && !upgradeRequiredOnOriginalPlan)) ? 'cursor-not-allowed bg-slate-50 text-slate-400 opacity-60' : ''
        }`}
      >
        <span className={`${plan ? 'text-gray-900' : 'text-gray-500'} flex items-center gap-1.5 min-w-0 flex-1 overflow-hidden`}>
          {plan ? (
            <>
              <PlanBadge planId={plan} size="xs" variant="compact" className="flex-shrink-0" />
              <span className="truncate text-sm">{formatPlanDisplay(plan)}</span>
            </>
          ) : (
            <span className="truncate text-sm">{t('selectPlan', language)}</span>
          )}
        </span>
        <svg className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${showPlanDropdown ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      
      {showPlanDropdown && (
        <div className="absolute top-full left-0 z-[180] mt-2 w-full overflow-hidden rounded-xl border border-slate-200 bg-white p-1 shadow-[0_18px_38px_rgba(15,23,42,0.10)]">
          {[
            { value: 'Free', label: 'Free', order: 0, icon: 'checkmark', isFree: true },
            { value: 'Standard', label: 'Standard', order: 1, icon: '📦' },
            { value: 'Pro', label: 'Pro', order: 2, icon: '⚡' },
            { value: 'Elite', label: 'Elite', order: 3, icon: '💎' },
            { value: 'Ultimate', label: 'Ultimate', order: 4, icon: '🚀' }
          ]
          .filter((option) => {
            // 🚀 Upgrade-Modus: IMMER nur höhere Pläne als der ursprüngliche Plan zeigen
            // Unabhängig davon, ob die aktuellen Transaktionen über oder unter dem ursprünglichen Limit liegen
            if (upgradeMode && upgradeDatasetData) {
              // Im Upgrade-Modus dürfen nur Pläne angezeigt werden, die höher sind als der ursprüngliche Plan
              // Das gilt auch wenn Wallets gelöscht wurden und totalTransactionCount wieder unter originalPlanLimit fällt
              return option.order > originalPlanOrder;
            }
            
            // Normaler Modus → keine Einschränkung
            return true;
          })
          .map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => {
                // ✅ Plan normalisiert speichern (konsistent mit DB/Backend)
                setPlan(normalizePlanId(option.value));

                // ✨ Immer schließen – auch im Upgrade-Modus
                setShowPlanDropdown(false);

                setMissingFields(prev => ({ ...prev, plan: false }));
                
                // ✅ Free-Plan: Zahlungsmethode zurücksetzen und als nicht erforderlich markieren
                const selectedPlanNormalized = normalizePlanId(option.value);
                if (selectedPlanNormalized === "free") {
                  setPaymentMethod("");
                  setMissingFields(prev => ({ ...prev, payment: false }));
                  setShowPaymentDropdown(false);
                }
                
                // ✅ Upgrade-Modus: Button-Logik bleibt unverändert
                if (upgradeMode && upgradeDatasetData) {
                  const selectedPlan = option.value;
                  const selectedPlanOrder = getPlanOrder(selectedPlan);

                  const originalPlan = upgradeDatasetData.plan || plan;
                  const originalPlanOrder = getPlanOrder(originalPlan);
                  const originalPlanLimits = getPlanLimits(originalPlan);
                  const newPlanLimits = getPlanLimits(selectedPlan);

                  const originalTxExceeded =
                    totalTransactionCount !== null &&
                    originalPlanLimits.transactions != null &&
                    totalTransactionCount > originalPlanLimits.transactions;

                  if (selectedPlanOrder > originalPlanOrder) {
                    // höherer Plan → Upgrade-Button aktiv
                    setButtonMode('upgrade');
                                      } else {
                    if (originalTxExceeded) {
                      // Limit noch überschritten → Upgrade-Button bleibt (disabled-Logik wie gehabt)
                      setButtonMode('upgrade');
                                          } else {
                      // Limit ok → Add Wallet to Set
                      setButtonMode('addWalletToSet');
                                          }
                  }

                  return; // normale Logik abbrechen
                }

                // ⬇️ Ab hier nur noch normaler Modus (Admin/Buy/Analyze)
                // 🚀 ADMIN-WALLET PRÜFUNG: Prüfe ob eingeloggte Wallet eine Admin-Wallet ist
                if (isAuthenticated && user?.walletAddress?.toLowerCase() === '0x3c1d3cc2a44569cecbef586c1fcd8cbb821b9c5e') {
                  // 🚀 ADMIN-WALLET: Immer Analyze Button anzeigen
                  setButtonMode("analyze");
                } else {
                  // 🎯 NORMALE USER: Button immer auf "buy" setzen (disabled-State verhindert Klicken bei Limit-Überschreitung)
                  setButtonMode("buy");
                  const selectedLimits = getPlanLimits(option.value);
                  const effectiveTx = (typeof totalTransactionCount === 'number' ? totalTransactionCount : (transactionCount || 0));
                  const transactionsExceeded =
                    selectedLimits.transactions != null &&
                    effectiveTx > selectedLimits.transactions;
                  
                                  }
              }}
              className={`w-full flex items-center gap-2 rounded-lg px-3 py-2.5 text-left text-sm transition-all duration-150 hover:bg-slate-50 hover:text-slate-900 ${
                plan === option.value ? 'bg-blue-50 text-blue-800 font-medium' : 'text-gray-700'
              } ${option.value === 'Free' ? 'rounded-t-md' : ''} ${option.value === 'Ultimate' ? 'rounded-b-md' : ''}`}
            >
              <PlanBadge planId={option.value} size="xs" variant="compact" className="flex-shrink-0" />
              <span>{option.isFree ? 'Free' : option.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  </div>

  {/* YEAR */}
<div className={`flex flex-col relative w-full md:w-auto ${showYearDropdown ? 'z-[170]' : ''}`}>
    {missingFields.year && !isPurchasedWalletLoaded && (
              <div className="absolute -top-6 left-0 z-10 flex items-center text-yellow-700 bg-yellow-100 px-2 py-0.5 rounded text-xs font-medium shadow-sm whitespace-nowrap animate-pulse">
        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-yellow-500 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1 4v-4m0 0h-1v-4h1m0 0h1m-1 0V5a2 2 0 114 0v4" />
        </svg>
        {t('fieldRequired', language)}
      </div>
    )}
    {/* 🚀 CUSTOM YEAR DROPDOWN - MIT BLAUEN HOVER-EFFEKTEN */}
    <div className="relative custom-dropdown overflow-visible">
      <button
        type="button"
        onClick={() => !upgradeMode && setShowYearDropdown(!showYearDropdown)}
        disabled={upgradeMode}
        className={`h-[46px] w-full md:w-[170px] rounded-xl border border-slate-200 bg-white/95 px-3.5 text-left text-sm text-slate-700 shadow-[0_1px_2px_rgba(15,23,42,0.02)] flex items-center justify-between transition-all duration-200 hover:border-slate-300 hover:shadow-[0_6px_18px_rgba(15,23,42,0.06)] focus:outline-none focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 ${
          upgradeMode ? 'cursor-not-allowed bg-slate-50 text-slate-400 opacity-60' : ''
        }`}
      >
        <div className="flex items-center justify-between w-full">
          <span className={year ? 'text-gray-900' : 'text-gray-500'}>
            {year ? formatTaxYearDisplayLabel(country, year) : t('selectYear', language)}
          </span>
          {isFreeYear && (
            <div className="flex items-center gap-1.5 ml-2">
              <svg className="w-4 h-4 text-emerald-500" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <span className="text-emerald-600 text-sm font-semibold">FREE</span>
            </div>
          )}
        </div>
        <svg className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${showYearDropdown ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      
      {showYearDropdown && (
        <div className="absolute top-full left-0 z-[180] mt-2 w-full overflow-hidden rounded-xl border border-slate-200 bg-white p-1 shadow-[0_18px_38px_rgba(15,23,42,0.10)]">
          {TAX_YEAR_OPTIONS.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => {
                setYear(option.value);
                setShowYearDropdown(false);
                setMissingFields(prev => ({ ...prev, year: false }));
              }}
              className={`w-full rounded-lg px-3 py-2.5 text-left text-sm transition-all duration-150 hover:bg-slate-50 hover:text-slate-900 ${
                String(year) === option.value ? 'bg-blue-50 text-blue-800 font-medium' : 'text-gray-700'
              } ${option.value === '2024' ? 'rounded-t-md' : ''} ${option.value === '2026' ? 'rounded-b-md' : ''}`}
            >
              <div className="flex items-center justify-between">
                <span>{formatTaxYearDisplayLabel(country, option.value)}</span>
                {option.isFree && (
                  <div className="flex items-center gap-1.5">
                    <svg className="w-4 h-4 text-emerald-500" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span className="text-emerald-600 text-sm font-semibold">FREE</span>
                  </div>
                )}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  </div>

    {/* PAYMENT */}
     <div className={`flex flex-col relative w-full md:w-auto ${showPaymentDropdown ? 'z-[170]' : ''}`}>
      {missingFields.payment && !isPurchasedWalletLoaded && !isFreeYear && !isFreePlanSelected && (
                <div className="absolute -top-6 left-0 z-10 flex items-center text-yellow-700 bg-yellow-100 px-2 py-0.5 rounded text-xs font-medium shadow-sm whitespace-nowrap animate-pulse">
        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-yellow-500 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1 4v-4m0 0h-1v-4h1m0 0h1m-1 0V5a2 2 0 114 0v4" />
        </svg>
        {t('fieldRequired', language)}
      </div>
    )}
    {/* 🚀 CUSTOM PAYMENT DROPDOWN - MIT BLAUEN HOVER-EFFEKTEN */}
    <div className="relative custom-dropdown overflow-visible">
      <button
        type="button"
        onClick={() => {
          // 2024 ODER Free-Plan: Payment nicht verfügbar
          if (isPaymentDisabled) return;
          // Während Analyse sperren
          if (isAnalyzing || isUpgradeAnalyzing) return;
          // Im Upgrade-Modus: nur erlauben, wenn Upgrade wirklich nötig ist
          if (upgradeMode && !upgradeRequiredOnOriginalPlan) return;
          setShowPaymentDropdown(!showPaymentDropdown);
        }}
        disabled={
          isPaymentDisabled ||
          isAnalyzing || isUpgradeAnalyzing ||
          // Im Upgrade-Modus: nur freigeben, wenn Upgrade wirklich nötig ist
          (upgradeMode && !upgradeRequiredOnOriginalPlan)
        }
        className={`h-[46px] w-full md:w-[190px] rounded-xl border border-slate-200 bg-white/95 px-3.5 text-left text-sm text-slate-700 shadow-[0_1px_2px_rgba(15,23,42,0.02)] flex items-center justify-between transition-all duration-200 hover:border-slate-300 hover:shadow-[0_6px_18px_rgba(15,23,42,0.06)] focus:outline-none focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 ${
          isPaymentDisabled || isAnalyzing || isUpgradeAnalyzing || (upgradeMode && !upgradeRequiredOnOriginalPlan)
            ? 'cursor-not-allowed bg-slate-50 text-slate-400 opacity-60'
            : ''
        }`}
      >
        <span className={`${paymentMethod ? 'text-gray-900' : 'text-gray-500'} flex items-center gap-2`}>
          {paymentMethod ? (
            paymentMethod === 'vet' ? (
              <>
                <img src="/vet-icon.png" alt="VET" className="w-5 h-5 object-contain" />
                <span>VET</span>
              </>
            ) : paymentMethod === 'b3tr' ? (
              <>
                <img src="/b3tr-icon.png" alt="B3TR" className="w-5 h-5 object-contain" />
                <span>B3TR</span>
              </>
            ) : paymentMethod === 'vtho' ? (
              <>
                <img src="/vtho-icon.png" alt="VTHO" className="w-5 h-5 object-contain" />
                <span>VTHO</span>
              </>
            ) : paymentMethod === 'creditcard' ? (
              language === 'en' ? '💳 Credit Card' : '💳 Kreditkarte'
            ) : paymentMethod
          ) : (language === 'en' ? 'Select payment...' : 'Zahlungsmethode')}
        </span>
        <svg className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${showPaymentDropdown ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      
      {showPaymentDropdown && !isPaymentDisabled && (
        <div className="absolute top-full left-0 z-[180] mt-2 w-full overflow-hidden rounded-xl border border-slate-200 bg-white p-1 shadow-[0_18px_38px_rgba(15,23,42,0.10)]">
          {[
            { value: 'vet', label: 'VET', icon: '/vet-icon.png' },
            { value: 'b3tr', label: 'B3TR', icon: '/b3tr-icon.png' },
            { value: 'vtho', label: 'VTHO', icon: '/vtho-icon.png' }
          ].map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => {
                const selectedMethod = option.value;
                setPaymentMethod(selectedMethod);
                setShowPaymentDropdown(false);
                setMissingFields(prev => ({ ...prev, payment: false }));
                
                // 🎯 Token und PaymentMethod synchronisieren
                if (['vet', 'b3tr', 'vtho'].includes(selectedMethod)) {
                  setSelectedToken(selectedMethod);
                                  }
                
                // 🔁 SPEZIELLE LOGIK IM UPGRADE-MODUS
                if (upgradeMode && upgradeDatasetData) {
                  // Wir bleiben IMMER im Upgrade-/AddWalletToSet-Flow
                  const originalPlan = upgradeDatasetData.plan || plan;
                  const originalPlanLimits = getPlanLimits(originalPlan);

                  const originalTxExceeded =
                    totalTransactionCount !== null &&
                    originalPlanLimits.transactions != null &&
                    totalTransactionCount > originalPlanLimits.transactions;

                  if (originalTxExceeded) {
                    // Limit auf ursprünglichem Plan überschritten → klarer Upgrade-Flow
                    setButtonMode("upgrade");
                                      } else {
                    // theoretischer Sonderfall: kein Upgrade nötig
                    setButtonMode("addWalletToSet");
                                      }

                  return; // ⛔ normale Analyze/Buy-Logik nicht ausführen
                }

                // ⬇️ ab hier nur normaler Modus (nicht Upgrade)

                // 🚀 ADMIN-WALLET PRÜFUNG
                if (isAuthenticated && user?.walletAddress?.toLowerCase() === '0x3c1d3cc2a44569cecbef586c1fcd8cbb821b9c5e') {
                  setButtonMode("analyze");
                } else {
                  // 🎯 NORMALE USER: Button immer auf "buy" setzen (disabled-State verhindert Klicken bei Limit-Überschreitung)
                  setButtonMode("buy");
                  const effectiveTx = (typeof totalTransactionCount === 'number' ? totalTransactionCount : (transactionCount || 0));
                  const transactionsExceeded = effectiveTx > currentPlanLimits.transactions;
                  
                                  }
              }}
              className={`w-full flex items-center gap-2 rounded-lg px-3 py-2.5 text-left text-sm transition-all duration-150 hover:bg-slate-50 hover:text-slate-900 ${
                paymentMethod === option.value ? 'bg-blue-50 text-blue-800 font-medium' : 'text-gray-700'
              } ${option.value === 'vet' ? 'rounded-t-md' : ''} ${option.value === 'vtho' ? 'rounded-b-md' : ''}`}
            >
              <img src={option.icon} alt={option.label} className="w-5 h-5 object-contain" />
              <span>{option.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  </div>



  {/* COUNTRY */}
     <div className={`flex flex-col relative w-full md:w-auto ${showCountryDropdown ? 'z-[170]' : ''}`}>
    {missingFields.country && !isPurchasedWalletLoaded && (
              <div className="absolute -top-6 left-0 z-10 flex items-center text-yellow-700 bg-yellow-100 px-2 py-0.5 rounded text-xs font-medium shadow-sm whitespace-nowrap animate-pulse">
        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-yellow-500 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1 4v-4m0 0h-1v-4h1m0 0h1m-1 0V5a2 2 0 114 0v4" />
        </svg>
        {t('fieldRequired', language)}
      </div>
    )}
    {/* 🚀 CUSTOM COUNTRY DROPDOWN - MIT BLAUEN HOVER-EFFEKTEN */}
    <div className="relative custom-dropdown overflow-visible">
      <button
        type="button"
        onClick={() => !upgradeMode && setShowCountryDropdown(!showCountryDropdown)}
        disabled={upgradeMode}
        className={`h-[46px] w-full md:w-[190px] rounded-xl border border-slate-200 bg-white/95 px-3.5 text-left text-sm text-slate-700 shadow-[0_1px_2px_rgba(15,23,42,0.02)] flex items-center justify-between transition-all duration-200 hover:border-slate-300 hover:shadow-[0_6px_18px_rgba(15,23,42,0.06)] focus:outline-none focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 ${
          upgradeMode ? 'cursor-not-allowed bg-slate-50 text-slate-400 opacity-60' : ''
        }`}
      >
        <div className="flex items-center gap-2 flex-1">
          {country && (
            <img 
              src={getFlagUrlForCountry(country)} 
              alt={`${getAvailableCountries().find(c => c.code === country)?.name || country} flag`}
              className="h-[15px] w-5 flex-shrink-0 rounded-[2px] border border-slate-200/70 object-cover"
              loading="lazy"
            />
          )}
          <span className={country ? 'text-gray-900' : 'text-gray-500'}>
            {country ? getAvailableCountries().find(c => c.code === country)?.name : t('selectResidence', language)}
          </span>
        </div>
        <svg className={`w-4 h-4 text-gray-400 transition-transform duration-200 flex-shrink-0 ${showCountryDropdown ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      
      {showCountryDropdown && (
        <div className="absolute top-full left-0 z-[180] mt-2 w-full max-h-60 overflow-y-auto rounded-xl border border-slate-200 bg-white p-1 shadow-[0_18px_38px_rgba(15,23,42,0.10)]">
          {getAvailableCountries().map((countryOption) => (
            <button
              key={countryOption.code}
              type="button"
              onClick={() => {
                setCountry(countryOption.code);
                setShowCountryDropdown(false);
                setMissingFields(prev => ({ ...prev, country: false }));
                
                // 🚀 ADMIN-WALLET PRÜFUNG: Prüfe ob eingeloggte Wallet eine Admin-Wallet ist
                if (isAuthenticated && user?.walletAddress?.toLowerCase() === '0x3c1d3cc2a44569cecbef586c1fcd8cbb821b9c5e') {
                  // 🚀 ADMIN-WALLET: Immer Analyze Button anzeigen
                  setButtonMode("analyze");
                } else {
                  // 🎯 NORMALE USER: Button immer auf "buy" setzen (disabled-State verhindert Klicken bei Limit-Überschreitung)
                  setButtonMode("buy");
                  const effectiveTx = (typeof totalTransactionCount === 'number' ? totalTransactionCount : (transactionCount || 0));
                  const transactionsExceeded = effectiveTx > currentPlanLimits.transactions;
                  
                                  }
              }}
              className={`flex w-full items-center gap-2 rounded-lg px-3 py-2.5 text-left text-sm transition-all duration-150 hover:bg-slate-50 hover:text-slate-900 ${
                country === countryOption.code ? 'bg-blue-50 text-blue-800 font-medium' : 'text-gray-700'
              }`}
            >
              <img 
                src={getFlagUrlForCountry(countryOption.code)} 
                alt={`${countryOption.name} flag`}
                className="h-[15px] w-5 rounded-[2px] border border-slate-200/70 object-cover"
                loading="lazy"
              />
              {countryOption.name}
            </button>
          ))}
        </div>
      )}
    </div>
  </div>

  {/* CTA WRAPPER: nimmt nur EINEN Platz in der Reihe ein */}
  <div ref={ctaRef} className="w-[150px] md:w-[220px] flex-shrink-0 flex items-end">
    {/* Button bleibt exakt aligned mit den anderen Feldern */}
    <button
      id="primary-cta"
      data-cta-button
      type={
        upgradeMode && (buttonMode === "addWalletToSet" || buttonMode === "upgrade")
          ? "button"
          : "submit"
      }
      disabled={
        (isAnalyzing || isUpgradeAnalyzing) ||
        isDuplicateWallet ||
        currentWalletCount >= MAX_WALLETS_PER_SET ||
        // ✅ TX-Limit: Disabled für alle Pläne wenn TX > Limit (inkl. 2024)
        //    Diese Bedingung greift für: 2024, Free-Plan 2025/2026, Standard, Pro, Elite, Ultimate
        (!upgradeMode && (buttonMode === "buy" || buttonMode === "addWalletToSet") && txExceededOnSelectedPlan) ||
        (upgradeMode && upgradeDatasetData && buttonMode === "upgrade" && (() => {
          if (!upgradeRequiredOnOriginalPlan) return false;

          const currentPlanOrder = getPlanOrder(plan);
          const originalPlanOrder = getPlanOrder(originalPlan);

          return currentPlanOrder <= originalPlanOrder || !paymentMethod;
        })())
      }
      onClick={async (e) => {
        // Normaler Modus: macht das submit/onSubmit
        if (!upgradeMode) return;

        // Upgrade-Modus: Button-Aktionen direkt ausführen
        if (buttonMode === "addWalletToSet") {
          await handleAddWalletsToSet();
        } else if (buttonMode === "upgrade") {
          await handleUpgradeClick();
        }
      }}
      className={`full-row flex-shrink-0 group relative overflow-hidden h-11 w-[150px] md:w-[220px]
        inline-flex items-center justify-center rounded-md text-sm whitespace-nowrap
        transition-[box-shadow,transform] duration-200
        ${(buttonMode === "buy" && buyPulse) ? "animate-[buyPulse_900ms_ease-in-out_3]" : ""}
        ${(!ctaNudge ? "" : (
          ctaVariant === "preview" ? "ring-2 ring-indigo-300/60 shadow-lg" :
          ctaVariant === "buy" ? "ring-2 ring-emerald-200/60 shadow-lg" :
          ctaVariant === "analyze" ? "ring-2 ring-sky-200/70 shadow-lg" :
          ctaVariant === "dashboard" ? "ring-2 ring-blue-200/60 shadow-lg" :
          "ring-2 ring-slate-200/60 shadow-lg"
        ))}
        ${isDuplicateWallet ? "cursor-not-allowed" : ""}
        ${currentWalletCount >= MAX_WALLETS_PER_SET ? "cursor-not-allowed" : ""}
        disabled:opacity-50 disabled:cursor-not-allowed`}
    >
      {/* ✅ Background layers (crossfade) */}
      <span
        className={[
          "pointer-events-none absolute inset-0 transition-opacity duration-200",
          (ctaVariant === "buy" || ctaVariant === "upgrade" || ctaVariant === "add") ? "opacity-100" : "opacity-0",
          "bg-green-600",
          "group-hover:bg-green-700",
        ].join(" ")}
      />
      <span
        className={[
          "pointer-events-none absolute inset-0 transition-opacity duration-200",
          ctaVariant === "preview" ? "opacity-100" : "opacity-0",
          "bg-gradient-to-r from-[#2F3BA6] via-[#3B4CC7] to-[#4C1D95]",
          "group-hover:from-[#26328F] group-hover:via-[#2F3BA6] group-hover:to-[#3B167B]",
        ].join(" ")}
      />
      <span
        className={[
          "pointer-events-none absolute inset-0 transition-opacity duration-200",
          ctaVariant === "analyze" ? "opacity-100" : "opacity-0",
          "bg-blue-600",
          "group-hover:bg-blue-700",
        ].join(" ")}
      />
      <span
        className={[
          "pointer-events-none absolute inset-0 transition-opacity duration-200",
          ctaVariant === "dashboard" ? "opacity-100" : "opacity-0",
          "bg-[#3B4CC7]",
          "group-hover:bg-[#2F3BA6]",
        ].join(" ")}
      />
      <span
        className={[
          "pointer-events-none absolute inset-0 transition-opacity duration-200",
          (ctaVariant === "loading") ? "opacity-100" : "opacity-0",
          "bg-blue-700",
        ].join(" ")}
      />
      <span
        className={[
          "pointer-events-none absolute inset-0 transition-opacity duration-200",
          (ctaVariant === "limit") ? "opacity-100" : "opacity-0",
          "bg-red-500",
        ].join(" ")}
      />
      {isDuplicateWallet && (
        <span
          className="pointer-events-none absolute inset-0 bg-blue-300 opacity-100"
        />
      )}

      {/* ✅ Content über den Background legen */}
      <span className="relative z-10 inline-flex items-center justify-center gap-2 px-4 text-white">
        {(isAnalyzing || isUpgradeAnalyzing) && (
          <svg
            className="animate-spin h-4 w-4 text-white"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
          </svg>
        )}

        {(isAnalyzing || isUpgradeAnalyzing)
          ? (isUpgradeAnalyzing ? (language === "en" ? "Loading Set Data..." : "Lade Set-Daten...") : t("analyzing", language))
          : currentWalletCount >= MAX_WALLETS_PER_SET
          ? (language === "en" ? "Technical Limit Reached (50 Wallets)" : "Technisches Limit erreicht (50 Wallets)")
          : buttonMode === "addWalletToSet"
          ? (language === "en" ? "Add to Set" : "Zum Set hinzufügen")
          : buttonMode === "upgrade"
          ? (language === "en" ? "Upgrade" : "Upgrade")
          : (() => {
            // ✅ Einheitliches Morph-Stack für analyze/buy/preview/calculate
            // Texte
            const analyzeLabel = t("analyze", language);
            // ✅ Button-Text: 2024 oder Free-Plan → Add to Dashboard, sonst Buy
            const showAddToDashboard = isFreeYear || isFreePlanSelected;
            const buyLabel = showAddToDashboard
              ? (language === "en" ? "Add to Dashboard" : "Zum Dashboard hinzufügen")
              : t("buy", language);
            const previewLabel = language === "en" ? "Preview" : "Vorschau";
            const dashboardLabel = language === "en" ? "My Wallets" : "Meine Wallets";

            // Helper: gemeinsame Klassen für Fade/Slide
            const layer = (active) =>
              [
                "col-start-1 row-start-1 inline-flex items-center gap-2",
                "transition-all duration-200 ease-out will-change-transform",
                active ? "opacity-100 translate-y-0 scale-100" : "opacity-0 translate-y-1 scale-[0.98]",
              ].join(" ");

            return (
              <span className="relative grid grid-cols-1 grid-rows-1 place-items-center">
                {/* ANALYZE */}
                <span className={layer(ctaVariant === "analyze")} aria-hidden={ctaVariant !== "analyze"}>
                  <svg className="h-4 w-4 opacity-90" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 21l-4.3-4.3" />
                    <circle cx="11" cy="11" r="7" />
                  </svg>
                  <span className="whitespace-nowrap">{analyzeLabel}</span>
                </span>

                {/* BUY */}
                <span className={layer(ctaVariant === "buy")} aria-hidden={ctaVariant !== "buy"}>
                  <svg className="h-4 w-4 opacity-90" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M6 6h15l-1.5 9h-12z" />
                    <path d="M6 6l-2 0" />
                    <path d="M9 22a1 1 0 1 0 0-2 1 1 0 0 0 0 2z" />
                    <path d="M18 22a1 1 0 1 0 0-2 1 1 0 0 0 0 2z" />
                  </svg>
                  <span className="whitespace-nowrap">{buyLabel}</span>
                </span>

                {/* PREVIEW */}
                <span className={layer(ctaVariant === "preview")} aria-hidden={ctaVariant !== "preview"}>
                  <svg className="h-4 w-4 opacity-90" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12z" />
                    <path d="M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6z" />
                  </svg>
                  <span className="whitespace-nowrap">{previewLabel}</span>
                </span>

                {/* CALCULATE */}
                <span className={layer(ctaVariant === "dashboard")} aria-hidden={ctaVariant !== "dashboard"}>
                  <svg className="h-4 w-4 opacity-90" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                    <polyline points="9 22 9 12 15 12 15 22" />
                  </svg>
                  <span className="whitespace-nowrap">{dashboardLabel}</span>
                </span>
              </span>
            );
          })()
        }
      </span>
    </button>
  </div>
</div>
</form>
</div>

{/* Alter grüner "Wallet zur Liste hinzufügen" Button - ENTFERNT, jetzt in Tabellen-Header integriert */}

{/* Globaler Banner - ENTFERNT, jetzt als Pill im Premium-Tabellen-Header integriert */}


{/* Plan-Status & Hinweisblock - ENTFERNT, jetzt in Tabellen-Header integriert */}

{/* Fester Spacer für Hinweis über dem Wallet-Set-Modal - verhindert Layout-Sprünge */}
<div className="relative h-6 sm:h-8">
  {showNextWalletHint && !isAnalyzing && (
    <div className="absolute -top-2 left-0 flex items-start justify-start">
      <div className="relative inline-flex items-center gap-2 px-4 py-2 pr-8 rounded bg-blue-50 border-l-4 border-blue-400 text-blue-700 text-sm font-medium shadow-sm animate-pulse">
        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-blue-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
        </svg>
        <span className="whitespace-nowrap">
          {isAdminWallet
            ? (language === "en"
                ? "Admin: analyze another wallet for this set, then continue via the exchange CSV dialog…"
                : "Admin: weitere Wallet für dieses Set analysieren, danach im Börsen-CSV-Dialog fortfahren…")
            : upgradeMode && upgradeDatasetData
            ? (language === 'en'
                ? `Insert next VeChain wallet to ${upgradeDatasetData.datasetLabel || `Wallet-set#${upgradeDatasetData.datasetId || ''}` || 'this set'}...`
                : `Nächste VeChain Wallet zu ${upgradeDatasetData.datasetLabel || `Wallet-set#${upgradeDatasetData.datasetId || ''}` || 'diesem Set'} hinzufügen...`)
            : t('insertNextWalletHint', language)}
        </span>
        <button
          onClick={() => setShowNextWalletHint(false)}
          className="absolute -top-1 -right-1 text-red-500 hover:text-red-700 font-semibold bg-white rounded-full w-5 h-5 flex items-center justify-center shadow-sm border border-red-200"
          title={language === 'de' ? 'Hinweis schließen' : 'Dismiss'}
        >
          ✕
        </button>
      </div>
    </div>
  )}
</div>

{/* Analyse-Container: Wallet-Paar + Token-Transaktionen */}
<section className="relative z-0 bg-white border border-gray-200 rounded-2xl p-6 shadow-sm space-y-6">
  {/* 2. Wallet-Set – einheitliches Premium-Modal */}
  <div className="mt-2 sm:mt-3">
    {(() => {
      // Sammle alle Wallet-Paare
      const allPairs = [];
      
      // Aktuelles Wallet-Paar (wenn vorhanden und noch nicht in analyzedWallets)
      if (walletData && submittedWallet) {
        const isAlreadyInAnalyzed = analyzedWallets.some(w => w.wallet === submittedWallet);
        if (!isAlreadyInAnalyzed) {
          allPairs.push({
            wallet: submittedWallet,
            delegateWallet: delegateWallet || null,
            appContext: walletData?.appContext || {},
            mainTxCount: walletData?.mainWalletTxCount ?? 0,
            veDelegateTxCount: walletData?.delegateWalletTxCount ?? 0,
            isCurrent: true
          });
        }
      }
      
      // Gespeicherte Wallet-Paare
      analyzedWallets.forEach(entry => {
        allPairs.push({
          ...entry,
          isCurrent: false
        });
      });

      // ✅ kleine Helfer-Pille für Plan/Limits – wird im Header verwendet
      const renderPlanLimitPill = () => {
        if (!plan) return null;

        const loading = (isAnalyzing || isUpgradeAnalyzing);
        const hasCount = totalTransactionCount !== null && totalTransactionCount !== undefined;
        const within = !hasCount || planLimitUnlimited || planLimit == null || (totalTransactionCount <= planLimit);

        const label = loading
          ? (language === 'en' ? 'Loading…' : 'Laden…')
          : hasCount
            ? planLimitUnlimited
              ? `${totalTransactionCount} — ${planLimitLabel}`
              : `${totalTransactionCount}/${planLimit} ${
                within
                  ? (language === 'en' ? 'Analysis can be started.' : 'Analyse kann gestartet werden.')
                  : (language === 'en' ? 'Upgrade required!' : 'Upgrade erforderlich!')
              }`
            : `${planLimitLabel}`;

        return (
          <span
            className={[
              "inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-semibold border",
              within ? "text-green-700 bg-green-50 border-green-200" : "text-red-700 bg-red-50 border-red-200"
            ].join(" ")}
            title={label}
          >
            {loading ? (
              <svg className="animate-spin h-4 w-4 opacity-70" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
              </svg>
            ) : within ? (
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            ) : (
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            )}
            <span className="whitespace-nowrap">{label}</span>
          </span>
        );
      };


      if (allPairs.length === 0) {
        return (
          <div
            className={getLandingIntroClass('mt-0 relative rounded-2xl border border-sky-50 bg-white/95 shadow-lg overflow-hidden text-[13px] sm:text-[14px]')}
            style={{
              '--intro-delay': '360ms',
              '--intro-y': '20px',
              '--intro-scale': '0.996',
            }}
          >
            {/* Dünner Gradient-Balken oben */}
            <div className="pointer-events-none absolute inset-x-0 top-0 h-1.5 bg-gradient-to-r from-sky-400 via-indigo-400 to-sky-500" />

            {/* Header: Desktop – Kopfzeile mit Icon + Text + Plan-Pille + CTA */}
            <div className="hidden md:flex items-center justify-between px-4 pt-3 pb-2 flex-wrap gap-2">
              {/* Linke Seite */}
              <div className="flex items-center gap-3">
                <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-gradient-to-tr from-sky-500 via-indigo-500 to-violet-500 shadow-sm transition-all duration-300 hover:scale-110 hover:rotate-3 hover:shadow-lg hover:shadow-sky-500/50 group/icon cursor-pointer">
                  <WalletSetIcon className="w-4.5 h-4.5 text-white transition-transform duration-300 group-hover/icon:scale-110" />
                </div>
                <div className="flex flex-col leading-tight">
                  <span className="text-sm font-semibold uppercase tracking-wide text-sky-600/80">
                    {language === 'en' ? 'Wallet set' : 'Wallet-Set'}
                  </span>
                  <span className="text-sm text-slate-500">
                    {language === 'en'
                      ? 'Each row shows one main wallet with its VeDelegate wallet.'
                      : 'Jede Zeile zeigt eine Main-Wallet mit zugehöriger VeDelegate-Wallet.'}
                  </span>
                </div>
              </div>

              {/* Rechte Seite: Plan-Limit + CTA */}
              <div className="hidden sm:flex items-center gap-3">
                {/* Plan-Limit-Pille */}
                {plan && (
                  <span
                    className={`
                      inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-sm font-medium border
                      transition-all duration-150
                      ${
                        planLimitUnlimited || planLimit == null || (totalTransactionCount ?? 0) <= planLimit
                          ? 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100 hover:border-emerald-300 hover:text-emerald-800 hover:shadow-sm'
                          : 'bg-red-50 text-red-700 border-red-200 hover:bg-red-100 hover:border-red-300 hover:text-red-800 hover:shadow-sm'
                      }
                    `}
                    title={language === 'en'
                      ? 'Transactions used / Plan limit'
                      : 'Verbrauchte Transaktionen / Planlimit'}
                  >
                    { (isAnalyzing || isUpgradeAnalyzing) ? (
                      // Spinner nur WÄHREND Analyse
                      <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"/>
                      </svg>
                    ) : (
                      // Statuspunkt
                      <span className={`w-2 h-2 rounded-full ${ planLimitUnlimited || planLimit == null || (totalTransactionCount ?? 0) <= planLimit ? 'bg-emerald-500' : 'bg-red-500' }`}/>
                    )}
                    <span className="tabular-nums">
                      {totalTransactionCount !== null
                        ? planLimitUnlimited
                          ? `${totalTransactionCount} — ${planLimitLabel}`
                          : `${totalTransactionCount}/${planLimit}`
                        : `${planLimitLabel}`}
                    </span>
                    <span className="uppercase tracking-wide text-xs opacity-70">
                      {language==='en' ? 'TX limit' : 'TX-Limit'}
                    </span>
                  </span>
                )}

                {/* ✅ NEU: Step-Banner nach Preview-Buy-Rückkehr */}
                {showPreviewBuyHint && (
                  <div className="hidden sm:flex items-center gap-2 text-xs text-emerald-600 bg-emerald-50 px-3 py-1.5 rounded-full border border-emerald-200 animate-fade-in-up">
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="font-medium">
                      {language === "en" ? "Preview created → Unlock now" : "Vorschau erstellt → Jetzt freischalten"}
                    </span>
                  </div>
                )}

                {/* Modus-Button (Container) mit Toggle INSIDE – zwischen TX-LIMIT und "Wallets zum Set hinzufügen" */}
                {showBuyPreviewToggle && (() => {
                  const isPreview = buyPreviewMode === "preview";
                  const currentLetter =
                    language === "de"
                      ? (isPreview ? "V" : "K")
                      : (isPreview ? "P" : "B");
                  const leftMarker = language === "de" ? "V" : "P";
                  const rightMarker = language === "de" ? "K" : "B";

                  return (
                    <div
                      className={[
                        "group hidden sm:inline-flex items-center gap-2 rounded-full border border-sky-200 bg-white/80",
                        "px-3 py-1.5 text-sm font-medium text-sky-700 shadow-sm",
                        "hover:border-sky-300 hover:bg-sky-50 transition",
                        "cursor-default select-none",
                        !isToggleInteractive ? "opacity-60 grayscale" : ""
                      ].join(" ")}
                      title={
                        !isBuyCtaVisible || upgradeMode
                          ? (language === "en"
                              ? "Available when Buy/Preview is active"
                              : "Verfügbar, sobald Kaufen/Vorschau aktiv ist")
                          : (buyPreviewMode === "preview"
                              ? (language === "en" ? "Action: Preview" : "Aktion: Vorschau")
                              : (language === "en" ? "Action: Buy" : "Aktion: Kaufen"))
                      }
                    >
                      <span className="whitespace-nowrap">
                        {language === "en" ? "Action:" : "Aktion:"}
                      </span>

                      <button
                        type="button"
                        onClick={() => {
                          if (!isToggleInteractive) return;
                          setBuyPreviewMode(prev => (prev === "preview" ? "buy" : "preview"));
                        }}
                        disabled={!isToggleInteractive}
                        aria-label={language === "en" ? "Toggle buy/preview" : "Kaufen/Vorschau umschalten"}
                        aria-pressed={buyPreviewMode === "preview"}
                        className={[
                          "relative inline-flex h-6 w-14 select-none items-center rounded-full",
                          "border border-slate-200 bg-gradient-to-r from-slate-50 to-slate-100/80 shadow-sm",
                          "transition-all duration-200",
                          "focus:outline-none focus:ring-2 focus:ring-sky-400/60",
                          "disabled:opacity-40 disabled:cursor-not-allowed disabled:grayscale",
                          isToggleInteractive ? "hover:from-slate-100 hover:to-slate-150" : "",
                          isToggleInteractive
                            ? (buyPreviewMode === "preview" ? "ring-1 ring-indigo-200" : "ring-1 ring-sky-200")
                            : "ring-1 ring-slate-200/60",
                        ].join(" ")}
                      >
                        {/* Track markers */}
                        <span className="absolute left-1.5 text-[9px] text-slate-300 font-medium leading-none opacity-40">
                          {leftMarker}
                        </span>
                        <span className="absolute right-1.5 text-[9px] text-slate-300 font-medium leading-none opacity-40">
                          {rightMarker}
                        </span>

                        {/* Thumb letter */}
                        <span
                          className={[
                            "absolute top-0.5 h-5 w-7 rounded-full bg-white shadow-md",
                            "border border-slate-200",
                            "transition-transform duration-200",
                            "flex items-center justify-center",
                            "text-[11px] font-bold leading-none",
                            isPreview ? "translate-x-0.5 text-indigo-600" : "translate-x-7 text-sky-600",
                          ].join(" ")}
                        >
                          {currentLetter}
                        </span>
                      </button>
                    </div>
                  );
                })()}

                {/* CTA: Wallets zum Set hinzufügen */}
                <button
                  type="button"
                  onClick={handleAddWalletToSet}
                  className="group hidden sm:inline-flex items-center gap-1.5 rounded-full border border-sky-200 bg-white/80 px-3 py-1.5 text-sm font-medium text-sky-700 shadow-sm hover:border-sky-300 hover:bg-sky-50 transition"
                >
                  <span>{language === 'en' ? 'Add wallets to set' : 'Wallets zum Set hinzufügen'}</span>
                  <span className="flex h-4 w-4 items-center justify-center rounded-full bg-sky-100 group-hover:bg-sky-200 transition-colors">
                    <span className="translate-x-0 text-xs group-hover:translate-x-0.5 transition-transform">→</span>
                  </span>
                </button>
              </div>
            </div>

            {/* Spalten-Header-Zeile (gleich wie im vollen Zustand) */}
            <div className="hidden md:grid grid-cols-[56px,minmax(0,1.5fr),minmax(0,1.5fr),auto,40px] items-center px-4 py-2.5 bg-gradient-to-r from-sky-50 via-indigo-50 to-sky-50 text-xs sm:text-sm font-medium text-sky-800 uppercase tracking-wide border-y border-sky-100/80">
              <div className="text-center">#</div>
              {/* MAIN WALLET Badge */}
              <div className="flex items-center gap-2">
                <span
                  className="inline-flex items-center gap-1 rounded-full border border-sky-200 bg-white/80 px-2 py-0.5 
                             text-[10px] font-semibold text-sky-700
                             transition-all duration-150
                             hover:bg-sky-50 hover:border-sky-300 hover:text-sky-800 hover:shadow-sm"
                >
                  <span className="h-1.5 w-1.5 rounded-full bg-sky-400" />
                  {language === 'en' ? 'Main wallet' : 'Main-Wallet'}
                </span>
              </div>
              {/* VEDELEGATE Badge */}
              <div className="flex items-center gap-2">
                <span
                  className="inline-flex items-center gap-1 rounded-full border border-violet-200 bg-white/80 px-2 py-0.5 
                             text-[10px] font-semibold text-violet-700
                             transition-all duration-150
                             hover:bg-violet-50 hover:border-violet-300 hover:text-violet-800 hover:shadow-sm"
                >
                  <span className="h-1.5 w-1.5 rounded-full bg-violet-400" />
                  {language === 'en' ? 'VeDelegate wallet' : 'VeDelegate-Wallet'}
                </span>
              </div>
              {/* Header über der Transaktionen-Spalte */}
              <div className="text-center text-[10px] text-slate-600">
                {language === 'en' ? 'Transactions' : 'Transaktionen'}
              </div>
              {/* Leere Header-Zelle für das X (nur für Screenreader benannt) */}
              <div className="text-right text-[10px] text-slate-600 pr-1">
                <span className="sr-only">
                  {language === 'en' ? 'Remove' : 'Entfernen'}
                </span>
              </div>
            </div>

            {/* Platzhalter-Zeile (kein Spinner) */}
            <div className="px-4 py-4">
              <div className="grid grid-cols-1 gap-3 md:grid-cols-[56px,minmax(0,1.5fr),minmax(0,1.5fr),auto,40px] md:items-center md:gap-4 rounded-xl border border-dashed border-sky-200 bg-white/95 px-4 py-3 text-xs sm:text-sm text-slate-500">
                <div className="flex items-center justify-center text-[11px] text-gray-500">
                  <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-sky-50 text-[10px] font-semibold text-sky-700 border border-sky-100 hover:bg-sky-100 hover:border-sky-300 hover:text-sky-900 transition-colors cursor-pointer">1</span>
                </div>
                <div className="text-[11px] text-slate-400 italic">— {language === 'en' ? 'No main wallet yet' : 'Noch keine Main-Wallet'}</div>
                <div className="text-[11px] text-slate-400 italic">— {language === 'en' ? 'No VeDelegate detected' : 'Kein VeDelegate erkannt'}</div>
                {/* 0 Transaktionen */}
                <div className="flex items-center justify-center">
                  <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-slate-50 border border-slate-200 text-xs font-semibold text-gray-900 hover:bg-slate-100 hover:border-slate-300 hover:text-gray-950 transition-colors cursor-pointer">0</span>
                </div>
                {/* + Button in eigener Spalte */}
                <div className="flex items-center justify-center">
                  <button
                    type="button"
                    onClick={() => {
                      const el = document.getElementById('wallet-input');
                      if (el) {
                        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                        el.focus?.();
                      }
                    }}
                    className="inline-flex items-center justify-center h-7 w-7 rounded-full border border-sky-200 bg-sky-50 text-sky-600 hover:bg-sky-100 hover:border-sky-300 transition-colors"
                  >
                    +
                  </button>
                </div>
              </div>

              <div className="mt-3 text-center text-[12px] text-slate-500">
                {language === 'en'
                  ? 'Start an analysis to add your first wallet pair to the set.'
                  : 'Starte die Analyse, um das erste Wallet-Paar zum Set hinzuzufügen.'}
              </div>
            </div>
          </div>
        );
      }
      
      // Funktion zum Entfernen eines Wallet-Paars aus dem Set
      const handleRemoveFromSet = (walletAddress) => {
        const entry = allPairs.find(p => p.wallet === walletAddress);
        if (!entry) return;

        const totalPairTx = (entry.mainTxCount || 0) + (entry.veDelegateTxCount || 0);

        if (entry.isCurrent) {
          // Aktuelles Wallet-Paar entfernen
          setWalletData(null);
          setSubmittedWallet("");
          setWallet("");
          setTransactionCount(null);
          setTotalTransactionCount(prev => prev - totalPairTx);
          if (entry.delegateWallet) {
            setDelegateWallet(null);
          }
        } else {
          // Gespeichertes Wallet-Paar entfernen
          const newList = analyzedWallets.filter(w => w.wallet !== walletAddress);
          setAnalyzedWallets(newList);
          setGlobalTotals(prev => ({
            vet: prev.vet - (entry.vet ?? 0),
            vtho: prev.vtho - (entry.vtho ?? 0),
            b3tr: prev.b3tr - (entry.b3tr ?? 0),
            nft: prev.nft - (entry.nft ?? 0),
          }));
          setFrozenTxTotal(prev => prev - totalPairTx);
          if (upgradeMode && upgradeDatasetData) {
            setTotalTransactionCount(prev => (prev ?? 0) - totalPairTx);
          }
        }
      };

      // Wenn Wallet-Paare im Set existieren -> Tabelle rendern
      return (
        <div
          className={getLandingIntroClass('mt-1 relative rounded-2xl border border-sky-50 bg-white/95 shadow-lg overflow-hidden text-[13px] sm:text-[14px]')}
          style={{
            '--intro-delay': '360ms',
            '--intro-y': '20px',
            '--intro-scale': '0.996',
          }}
        >
          {/* dünner Gradient-Balken oben */}
          <div className="pointer-events-none absolute inset-x-0 top-0 h-1.5 bg-gradient-to-r from-sky-400 via-indigo-400 to-sky-500" />
          
          {/* Header: Mobile – nur Titel */}
          <div className="px-4 py-2 border-b border-sky-50 bg-slate-50 text-[11px] sm:text-xs font-medium text-slate-500 flex items-center gap-2 md:hidden">
            <Wallet className="w-3.5 h-3.5 text-sky-500" />
            <span>
              {language === 'en'
                ? 'Wallet pairs in this set'
                : 'Wallet-Paare in diesem Set'}
            </span>
          </div>
            {/* Header: Desktop – Kopfzeile mit Icon + Text + Button */}
          <div className="hidden md:flex items-center justify-between px-4 pt-3 pb-2 flex-wrap gap-2">
            {/* Linke Seite: Titel + Unterzeile */}
            <div className="flex items-center gap-3">
              <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-gradient-to-tr from-sky-500 via-indigo-500 to-violet-500 shadow-sm transition-all duration-300 hover:scale-110 hover:rotate-3 hover:shadow-lg hover:shadow-sky-500/50 group/icon cursor-pointer">
                <WalletSetIcon className="w-4.5 h-4.5 text-white transition-transform duration-300 group-hover/icon:scale-110" />
              </div>
              <div className="flex flex-col leading-tight">
                <span className="text-sm font-semibold uppercase tracking-wide text-sky-600/80">
                  {language === 'en' ? 'Wallet set' : 'Wallet-Set'}
                </span>
                <span className="text-sm text-slate-500">
                  {language === 'en'
                    ? 'Each row shows one main wallet with its VeDelegate wallet.'
                    : 'Jede Zeile zeigt eine Main-Wallet mit zugehöriger VeDelegate-Wallet.'}
                </span>
              </div>
            </div>

            {/* Rechte Seite: Plan-Limit + CTA */}
            <div className="hidden sm:flex items-center gap-3">
              {/* ✅ NEU: Step-Banner nach Preview-Buy-Rückkehr */}
              {showPreviewBuyHint && (
                <button
                  type="button"
                  onClick={() => {
                    try {
                      const btn = document.querySelector('[data-cta-button]');
                      btn?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    } catch (_) {}
                  }}
                  className="hidden sm:flex items-center gap-2 text-xs text-emerald-700 bg-emerald-50 px-3 py-1.5 rounded-full border border-emerald-200 animate-fade-in-up hover:bg-emerald-100 hover:border-emerald-300 transition cursor-pointer"
                  title={language === "en" ? "Scroll to Buy" : "Zum Kaufen-Button springen"}
                >
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span className="font-medium">
                    {language === "en" ? "Back from preview → Click Buy" : "Zurück aus Vorschau → Jetzt Kaufen"}
                  </span>
                </button>
              )}

              {/* Plan-Limit-Pille */}
              {plan && (
                <span
                  className={`
                    inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-sm font-medium border
                    transition-all duration-150
                    ${
                      planLimitUnlimited || planLimit == null || (totalTransactionCount ?? 0) <= planLimit
                        ? 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100 hover:border-emerald-300 hover:text-emerald-800 hover:shadow-sm'
                        : 'bg-red-50 text-red-700 border-red-200 hover:bg-red-100 hover:border-red-300 hover:text-red-800 hover:shadow-sm'
                    }
                  `}
                  title={language === 'en'
                    ? 'Transactions used / Plan limit'
                    : 'Verbrauchte Transaktionen / Planlimit'}
                >
                  { (isAnalyzing || isUpgradeAnalyzing) ? (
                    // Spinner nur WÄHREND Analyse
                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"/>
                    </svg>
                  ) : (
                    // Statuspunkt
                    <span className={`w-2 h-2 rounded-full ${ planLimitUnlimited || planLimit == null || (totalTransactionCount ?? 0) <= planLimit ? 'bg-emerald-500' : 'bg-red-500' }`}/>
                  )}
                  <span className="tabular-nums">
                    {totalTransactionCount !== null
                      ? planLimitUnlimited
                        ? `${totalTransactionCount} — ${planLimitLabel}`
                        : `${totalTransactionCount}/${planLimit}`
                      : `${planLimitLabel}`}
                  </span>
                  <span className="uppercase tracking-wide text-xs opacity-70">
                    {language==='en' ? 'TX limit' : 'TX-Limit'}
                  </span>
                </span>
              )}

              {/* Modus-Button (Container) mit Toggle INSIDE – zwischen TX-LIMIT und "Wallets zum Set hinzufügen" */}
              {showBuyPreviewToggle && (() => {
                const isPreview = buyPreviewMode === "preview";
                const currentLetter =
                  language === "de"
                    ? (isPreview ? "V" : "K")
                    : (isPreview ? "P" : "B");
                const leftMarker = language === "de" ? "V" : "P";
                const rightMarker = language === "de" ? "K" : "B";

                return (
                  <div
                    className={[
                      "group hidden sm:inline-flex items-center gap-2 rounded-full border border-sky-200 bg-white/80",
                      "px-3 py-1.5 text-sm font-medium text-sky-700 shadow-sm",
                      "hover:border-sky-300 hover:bg-sky-50 transition",
                      "cursor-default select-none",
                      !isToggleInteractive ? "opacity-60 grayscale" : ""
                    ].join(" ")}
                    title={
                      !isBuyCtaVisible || upgradeMode
                        ? (language === "en"
                            ? "Available when Buy/Preview is active"
                            : "Verfügbar, sobald Kaufen/Vorschau aktiv ist")
                        : (buyPreviewMode === "preview"
                            ? (language === "en" ? "Action: Preview" : "Aktion: Vorschau")
                            : (language === "en" ? "Action: Buy" : "Aktion: Kaufen"))
                    }
                  >
                    <span className="whitespace-nowrap">
                      {language === "en" ? "Action:" : "Aktion:"}
                    </span>

                    <button
                      type="button"
                      onClick={() => {
                        if (!isToggleInteractive) return;
                        setBuyPreviewMode(prev => (prev === "preview" ? "buy" : "preview"));
                      }}
                      disabled={!isToggleInteractive}
                      aria-label={language === "en" ? "Toggle buy/preview" : "Kaufen/Vorschau umschalten"}
                      aria-pressed={buyPreviewMode === "preview"}
                      className={[
                        "relative inline-flex h-6 w-14 select-none items-center rounded-full",
                        "border border-slate-200 bg-gradient-to-r from-slate-50 to-slate-100/80 shadow-sm",
                        "transition-all duration-200",
                        "focus:outline-none focus:ring-2 focus:ring-sky-400/60",
                        "disabled:opacity-40 disabled:cursor-not-allowed disabled:grayscale",
                        isToggleInteractive ? "hover:from-slate-100 hover:to-slate-150" : "",
                        isToggleInteractive
                          ? (buyPreviewMode === "preview" ? "ring-1 ring-indigo-200" : "ring-1 ring-sky-200")
                          : "ring-1 ring-slate-200/60",
                      ].join(" ")}
                    >
                      {/* Track markers */}
                      <span className="absolute left-1.5 text-[9px] text-slate-300 font-medium leading-none opacity-40">
                        {leftMarker}
                      </span>
                      <span className="absolute right-1.5 text-[9px] text-slate-300 font-medium leading-none opacity-40">
                        {rightMarker}
                      </span>

                      {/* Thumb letter */}
                      <span
                        className={[
                          "absolute top-0.5 h-5 w-7 rounded-full bg-white shadow-md",
                          "border border-slate-200",
                          "transition-transform duration-200",
                          "flex items-center justify-center",
                          "text-[11px] font-bold leading-none",
                          isPreview ? "translate-x-0.5 text-indigo-600" : "translate-x-7 text-sky-600",
                        ].join(" ")}
                      >
                        {currentLetter}
                      </span>
                    </button>
                  </div>
                );
              })()}

              {/* CTA: Wallets zum Set hinzufügen */}
              <button
                type="button"
                onClick={handleAddWalletToSet}
                className="group hidden sm:inline-flex items-center gap-1.5 rounded-full border border-sky-200 bg-white/80 px-3 py-1.5 text-sm font-medium text-sky-700 shadow-sm hover:border-sky-300 hover:bg-sky-50 transition"
              >
                <span>
                  {language === 'en'
                    ? 'Add wallets to set'
                    : 'Wallets zum Set hinzufügen'}
                </span>
                <span className="flex h-4 w-4 items-center justify-center rounded-full bg-sky-100 group-hover:bg-sky-200 transition-colors">
                  <span className="translate-x-0 text-xs group-hover:translate-x-0.5 transition-transform">
                    →
                  </span>
                </span>
              </button>
            </div>
          </div>

          {/* Spalten-Header-Zeile */}
          <div className="hidden md:grid grid-cols-[56px,minmax(0,1.5fr),minmax(0,1.5fr),auto,40px] items-center px-4 py-2.5 bg-gradient-to-r from-sky-50 via-indigo-50 to-sky-50 text-xs sm:text-sm font-medium text-sky-800 uppercase tracking-wide border-y border-sky-100/80">
            <div className="text-center">#</div>

            {/* MAIN WALLET Badge */}
            <div className="flex items-center gap-2">
              <span
                className="inline-flex items-center gap-1 rounded-full border border-sky-200 bg-white/80 px-2 py-0.5 
                           text-[10px] font-semibold text-sky-700
                           transition-all duration-150
                           hover:bg-sky-50 hover:border-sky-300 hover:text-sky-800 hover:shadow-sm"
              >
                <span className="h-1.5 w-1.5 rounded-full bg-sky-400" />
                {language === 'en' ? 'Main wallet' : 'Main-Wallet'}
              </span>
            </div>

            {/* VEDELEGATE Badge */}
            <div className="flex items-center gap-2">
              <span
                className="inline-flex items-center gap-1 rounded-full border border-violet-200 bg-white/80 px-2 py-0.5 
                           text-[10px] font-semibold text-violet-700
                           transition-all duration-150
                           hover:bg-violet-50 hover:border-violet-300 hover:text-violet-800 hover:shadow-sm"
              >
                <span className="h-1.5 w-1.5 rounded-full bg-violet-400" />
                {language === 'en' ? 'VeDelegate wallet' : 'VeDelegate-Wallet'}
              </span>
            </div>

            {/* Header über der Transaktionen-Spalte */}
            <div className="text-center text-[10px] text-slate-600">
              {language === 'en' ? 'Transactions' : 'Transaktionen'}
            </div>

            {/* Leere Header-Zelle für das X (nur für Screenreader benannt) */}
            <div className="text-right text-[10px] text-slate-600 pr-1">
              <span className="sr-only">
                {language === 'en' ? 'Remove' : 'Entfernen'}
              </span>
            </div>
          </div>

          {/* Body */}
          <div className="px-4 py-3 bg-[#f5fcff] rounded-b-2xl">
            {allPairs.map((entry, index) => {
              const {
                wallet,
                delegateWallet,
                mainTxCount,
                veDelegateTxCount,
                isCurrent,
              } = entry;

              const pairTx =
                (typeof mainTxCount === 'number' ? mainTxCount : 0) +
                (typeof veDelegateTxCount === 'number' ? veDelegateTxCount : 0);

              return (
                <div
                  key={`${wallet}-${index}`}
                  className={index === 0 ? "" : "mt-2"}
                >
                  <div
                    className="wallet-row-enter grid grid-cols-1 gap-3 md:grid-cols-[56px,minmax(0,1.5fr),minmax(0,1.5fr),auto,40px] md:items-center md:gap-4 rounded-[10px] bg-white border border-transparent px-4 py-2.5 shadow-[0_1px_3px_rgba(0,0,0,0.04)] hover:border-sky-400 hover:shadow-[0_0_0_1px_rgba(40,209,212,0.2),0_4px_12px_rgba(0,0,0,0.08)] transition-all"
                    style={{ animationDelay: `${index * 60}ms` }}
                  >
                    {/* # */}
                    <div className="flex items-center justify-center text-[11px] text-gray-500">
                      <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-sky-50 text-[10px] font-semibold text-sky-700 border border-sky-100 hover:bg-sky-100 hover:border-sky-300 hover:text-sky-900 transition-colors cursor-pointer">
                        {index + 1}
                      </span>
                    </div>

                    {/* Main wallet – eigene Spalte */}
                    <div className="text-xs sm:text-sm text-gray-700">
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-[12px] sm:text-sm text-slate-900 break-all md:truncate">
                          {wallet}
                        </span>
                        <button
                          onClick={() => copyAddress(wallet)}
                          className="flex-shrink-0 text-sky-400 hover:text-sky-600 transition-colors"
                          title={language === 'de' ? 'Adresse kopieren' : 'Copy address'}
                        >
                          <Copy className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>

                    {/* VeDelegate wallet – eigene Spalte */}
                    <div className="text-xs sm:text-sm text-gray-700">
                      <div className="flex items-center gap-2">
                        {delegateWallet ? (
                          <>
                            <span className="font-mono text-[12px] sm:text-sm text-slate-900 break-all md:truncate">
                              {delegateWallet}
                            </span>
                            <button
                              onClick={() => copyAddress(delegateWallet)}
                              className="flex-shrink-0 text-violet-400 hover:text-violet-600 transition-colors"
                              title={language === 'de' ? 'Adresse kopieren' : 'Copy address'}
                            >
                              <Copy className="w-3.5 h-3.5" />
                            </button>
                          </>
                        ) : (
                          <span className="text-[11px] text-slate-400 italic">
                            {language === 'en'
                              ? 'No VeDelegate detected yet'
                              : 'Kein VeDelegate erkannt'}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Transactions – eigene Spalte */}
                    <div className="flex items-center justify-center">
                      <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-slate-50 border border-slate-200 text-xs font-semibold text-gray-900 hover:bg-slate-100 hover:border-slate-300 hover:text-gray-950 transition-colors cursor-pointer">
                        {pairTx}
                      </span>
                    </div>

                    {/* Entfernen – ganz rechte Spalte */}
                    <div className="flex items-center justify-center">
                      <button
                        type="button"
                        onClick={() => handleRemoveFromSet(wallet)}
                        className="p-1.5 rounded-full border border-gray-200 text-gray-400 hover:text-red-500 hover:border-red-200 hover:bg-red-50 transition-colors"
                        title={
                          language === 'en'
                            ? 'Remove wallet from set'
                            : 'Wallet aus Set entfernen'
                        }
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      );
    })()}
  </div>

    {/* dezenter Divider zwischen Wallets & Transaktionen */}
    <div className="border-t border-gray-100 pt-4 mt-2" />

    {/* 2.2 Transaktionsübersicht — key bewusst stabil: kein Remount bei Jahreswechsel (Inhalt kommt aus State) */}
    <div
      className={getLandingIntroClass('mt-8 mb-10')}
      style={{
        '--intro-delay': '470ms',
        '--intro-y': '18px',
        /* scale(1): verhindert weiche Icons in Edge durch dauerhaftes Subpixel-Scaling */
        '--intro-scale': '1',
      }}
    >
      <div className="mb-4 flex items-center gap-3">
        <h2 className="text-xl font-semibold tracking-[-0.02em] text-slate-900">
          {language === 'en'
            ? 'Transaction overview'
            : 'Transaktionsübersicht'}
        </h2>
        <div className="relative h-[2px] w-24 rounded-full bg-gradient-to-r from-sky-300 via-indigo-400 to-violet-300 shadow-[0_4px_14px_rgba(99,102,241,0.18)] opacity-95">
          <div className="absolute inset-0 rounded-full bg-white/35 blur-[0.5px]" />
        </div>
      </div>
      <p className="mb-5 text-sm text-slate-500">
        {language === 'en'
          ? 'Based on all active wallets in this set'
          : 'Basierend auf allen aktiven Wallets in diesem Set'}
      </p>

      {(() => {
        const hasValues =
          displayTotals.vet > 0 ||
          displayTotals.vtho > 0 ||
          displayTotals.b3tr > 0 ||
          displayTotals.nft > 0;

        const animationClass = hasValues
          ? 'animate-fade-in-up motion-reduce:animate-none'
          : '';

        const getCardAnimProps = (cardKey, delay) =>
          hasValues
            ? {
                key: `${cardKey}-${txAnimKey}`,
                className: animationClass,
                style: { animationDelay: `${delay}ms` },
              }
            : {
                key: cardKey,
                className: '',
                style: undefined,
              };

        const vetAnim = getCardAnimProps('vet-card', 0);
        const vthoAnim = getCardAnimProps('vtho-card', 95);
        const b3trAnim = getCardAnimProps('b3tr-card', 190);
        const nftAnim = getCardAnimProps('nft-card', 285);

        return (
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 text-center">

            {/* VET - Animated Card */}
            <div
              key={vetAnim.key}
              className={`transaction-card group relative overflow-hidden rounded-2xl border border-slate-200/80 bg-white/95 p-6 shadow-[0_10px_30px_rgba(15,23,42,0.06)] transition-all duration-300 hover:-translate-y-1 hover:border-sky-300 hover:shadow-[0_18px_40px_rgba(14,165,233,0.12)] cursor-pointer motion-reduce:transition-none ${vetAnim.className}`}
              style={vetAnim.style}
            >
              <div className="relative overflow-hidden">
                <div className="relative z-10 flex flex-col items-center">
                  <div className="mb-2">
                    <TokenIcon src="/vet.png" alt="VET logo" bgClass="bg-sky-50" />
                  </div>

                  <h3 className="text-sm font-bold text-sky-500 uppercase tracking-wider">
                    VET
                  </h3>

                  <p className="text-2xl font-semibold text-gray-800 mt-2 transition-all duration-300 hover:text-sky-600">
                    {(isAnalyzing || isUpgradeAnalyzing) ? (
                      <span className="inline-block w-[34px] h-[34px] border-2 border-sky-300 border-t-transparent rounded-full animate-spin" />
                    ) : (
                      displayTotals.vet || "0"
                    )}
                  </p>

                  <p className="text-xs text-gray-400 uppercase mt-1 tracking-wider">
                    {t('transactions', language)}
                  </p>
                </div>
              </div>
            </div>

            {/* VTHO - Animated Card */}
            <div
              key={vthoAnim.key}
              className={`transaction-card group relative overflow-hidden rounded-2xl border border-slate-200/80 bg-white/95 p-6 shadow-[0_10px_30px_rgba(15,23,42,0.06)] transition-all duration-300 hover:-translate-y-1 hover:border-violet-300 hover:shadow-[0_18px_40px_rgba(124,58,237,0.12)] cursor-pointer motion-reduce:transition-none ${vthoAnim.className}`}
              style={vthoAnim.style}
            >
              <div className="relative overflow-hidden">
                <div className="relative z-10 flex flex-col items-center">
                  <div className="mb-2">
                    <TokenIcon src="/vtho.png" alt="VTHO logo" bgClass="bg-blue-50" />
                  </div>

                  <h3 className="text-sm font-bold text-blue-800 uppercase tracking-wider">
                    VTHO
                  </h3>

                  <p className="text-2xl font-semibold text-gray-800 mt-2 transition-all duration-300 hover:text-blue-700">
                    {(isAnalyzing || isUpgradeAnalyzing) ? (
                      <span className="inline-block w-[34px] h-[34px] border-2 border-blue-400 border-t-transparent rounded-full animate-spin" />
                    ) : (
                      displayTotals.vtho || "0"
                    )}
                  </p>

                  <p className="text-xs text-gray-400 uppercase mt-1 tracking-wider">
                    {t('transactions', language)}
                  </p>
                </div>
              </div>
            </div>

            {/* VIP-180 - Animated Card */}
            <div
              key={b3trAnim.key}
              className={`transaction-card group relative overflow-hidden rounded-2xl border border-slate-200/80 bg-white/95 p-6 shadow-[0_10px_30px_rgba(15,23,42,0.06)] transition-all duration-300 hover:-translate-y-1 hover:border-emerald-300 hover:shadow-[0_18px_40px_rgba(16,185,129,0.12)] cursor-pointer motion-reduce:transition-none ${b3trAnim.className}`}
              style={b3trAnim.style}
            >
              <div className="relative overflow-hidden">
                <div className="relative z-10 flex flex-col items-center">
                  <div className="mb-2">
                    <TokenIcon src="/b3tr.png" alt="VIP-180" bgClass="bg-green-50" />
                  </div>

                  <h3 className="text-sm font-bold text-green-600 uppercase tracking-wider">
                    VIP-180
                  </h3>

                  <p className="text-2xl font-semibold text-gray-800 mt-2 transition-all duration-300 hover:text-green-700">
                    {(isAnalyzing || isUpgradeAnalyzing) ? (
                      <span className="inline-block w-[34px] h-[34px] border-2 border-green-400 border-t-transparent rounded-full animate-spin" />
                    ) : (
                      displayTotals.b3tr || "0"
                    )}
                  </p>

                  <p className="text-xs text-gray-400 uppercase mt-1 tracking-wider">
                    {t('transactions', language)}
                  </p>
                </div>
              </div>
            </div>

            {/* NFT - Animated Card */}
            <div
              key={nftAnim.key}
              className={`transaction-card group relative overflow-hidden rounded-2xl border border-slate-200/80 bg-white/95 p-6 shadow-[0_10px_30px_rgba(15,23,42,0.06)] transition-all duration-300 hover:-translate-y-1 hover:border-fuchsia-300 hover:shadow-[0_18px_40px_rgba(217,70,239,0.12)] cursor-pointer motion-reduce:transition-none ${nftAnim.className}`}
              style={nftAnim.style}
            >
              <div className="relative overflow-hidden">
                <div className="relative z-10 flex flex-col items-center">
                  <div className="mb-2">
                    <TokenIcon src="/earth.png" alt="NFT logo" bgClass="bg-purple-50" />
                  </div>

                  <h3 className="text-sm font-bold text-purple-600 uppercase tracking-wider">
                    NFT
                  </h3>

                  <p className="text-2xl font-semibold text-gray-800 mt-2 transition-all duration-300 hover:text-purple-700">
                    {(isAnalyzing || isUpgradeAnalyzing) ? (
                      <span className="inline-block w-[34px] h-[34px] border-2 border-purple-400 border-t-transparent rounded-full animate-spin" />
                    ) : (
                      displayTotals.nft || "0"
                    )}
                  </p>

                  <p className="text-xs text-gray-400 uppercase mt-1 tracking-wider">
                    {t('transactions', language)}
                  </p>
                </div>
              </div>
            </div>

          </div>
        );
      })()}
    </div>
</section>

  {/* 🚀 Getting Started – 3-Step Guide + Divider als Einheit */}
  <section
    className={getLandingIntroClass('mt-14 sm:mt-16')}
    style={{
      '--intro-delay': '610ms',
      '--intro-y': '16px',
      '--intro-scale': '0.998',
    }}
  >
    <div className="border-t border-slate-200/80" />

    <div className="mt-5 rounded-[28px] border border-slate-200/80 bg-white/80 p-5 sm:p-6 shadow-[0_12px_32px_rgba(15,23,42,0.05)]">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
        <h2 className="text-lg sm:text-xl font-semibold tracking-[-0.02em] text-slate-900">
          {language === 'en'
            ? 'V3Tax: Three steps to your VeChain tax report'
            : 'V3Tax: Drei Schritte zum VeChain-Steuerreport'}
        </h2>
        <p className="text-sm leading-6 text-slate-500 max-w-2xl">
          {language === 'en'
            ? 'From your VeChain wallets to a finished tax report – just follow these three steps.'
            : 'Von Ihren VeChain-Wallets zum fertigen Steuerreport – folgen Sie einfach diesen drei Schritten.'}
        </p>
      </div>

      <div className="grid gap-4 md:grid-cols-3">
      {/* 1️⃣ Wallet eingeben & analysieren */}
      <div className="relative overflow-hidden rounded-2xl border border-slate-200/80 bg-white/95 p-5 shadow-[0_8px_24px_rgba(15,23,42,0.05)] transition-all duration-300 group hover:-translate-y-1 hover:border-blue-300 hover:shadow-[0_16px_34px_rgba(59,130,246,0.12)]">
        {/* Hover Glow */}
        <div className="pointer-events-none absolute inset-0 rounded-2xl bg-gradient-to-r from-blue-400/0 via-blue-400/10 to-blue-400/0 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
        
        <div className="relative z-10 flex items-start gap-4">
          <div className="relative">
            <div className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-blue-600 text-white text-sm font-semibold shadow-sm transform transition-transform duration-300 group-hover:scale-110 group-hover:-translate-y-0.5">
              1
            </div>
            <div className="absolute -bottom-1 -right-1 bg-white rounded-full p-1 shadow-sm">
              <Wallet className="w-4 h-4 text-blue-500" />
            </div>
          </div>
          <div className="flex-1">
            <h3 className="text-sm font-semibold text-gray-900">
              {language === 'en'
                ? 'Enter wallet & start analysis'
                : 'Wallet eingeben & analysieren'}
            </h3>
            <p className="text-sm leading-6 text-slate-600">
              {language === 'en'
                ? 'Capture your first VeChain wallet, select plan, tax year and country of residence, and start the transaction analysis – linked VeDelegate wallets are automatically detected via the respective VeDelegate NFT.'
                : 'Erfassen Sie Ihre erste VeChain-Wallet, wählen Sie Plan, Steuerjahr und Wohnsitzland und starten Sie die Transaktionsanalyse – zugehörige VeDelegate-Wallets werden über den jeweiligen VeDelegate-NFT automatisch erkannt.'}
            </p>
          </div>
        </div>
      </div>

      {/* 2️⃣ Wallet-Set vervollständigen & Plan buchen */}
      <div className="relative overflow-hidden rounded-2xl border border-slate-200/80 bg-white/95 p-5 shadow-[0_8px_24px_rgba(15,23,42,0.05)] transition-all duration-300 group hover:-translate-y-1 hover:border-emerald-300 hover:shadow-[0_16px_34px_rgba(16,185,129,0.12)]">
        {/* Hover Glow */}
        <div className="pointer-events-none absolute inset-0 rounded-2xl bg-gradient-to-r from-emerald-400/0 via-emerald-400/10 to-emerald-400/0 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

        <div className="relative z-10 flex items-start gap-4">
          <div className="relative">
            <div className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-emerald-600 text-white text-sm font-semibold shadow-sm transform transition-transform duration-300 group-hover:scale-110 group-hover:-translate-y-0.5">
              2
            </div>
            <div className="absolute -bottom-1 -right-1 bg-white rounded-full p-1 shadow-sm">
              <Shield className="w-4 h-4 text-emerald-500" />
            </div>
          </div>
          <div className="flex-1">
            <h3 className="text-sm font-semibold text-gray-900">
              {language === 'en'
                ? 'Complete wallet set & book plan'
                : 'Wallet-Set vervollständigen & Plan buchen'}
            </h3>
            <p className="text-sm leading-6 text-slate-600">
              {language === 'en'
                ? 'Add all your other wallets to the wallet set until all relevant addresses are included. Then adjust the plan, confirm the GPS request and complete the payment via the VeWorld browser extension.'
                : 'Fügen Sie alle weiteren eigenen Wallets zum Wallet-Set hinzu, bis alle relevanten Adressen enthalten sind. Passen Sie anschließend den Plan an, bestätigen Sie die GPS-Abfrage und schließen Sie die Zahlung über die VeWorld Browser-Extension ab.'}
            </p>
          </div>
        </div>
      </div>

      {/* 3️⃣ Börsen-CSVs importieren & Set berechnen */}
      <div className="relative overflow-hidden rounded-2xl border border-slate-200/80 bg-white/95 p-5 shadow-[0_8px_24px_rgba(15,23,42,0.05)] transition-all duration-300 group hover:-translate-y-1 hover:border-indigo-300 hover:shadow-[0_16px_34px_rgba(99,102,241,0.12)]">
        {/* Hover Glow */}
        <div className="pointer-events-none absolute inset-0 rounded-2xl bg-gradient-to-r from-indigo-400/0 via-indigo-400/10 to-indigo-400/0 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

        <div className="relative z-10 flex items-start gap-4">
          <div className="relative">
            <div className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-indigo-600 text-white text-sm font-semibold shadow-sm transform transition-transform duration-300 group-hover:scale-110 group-hover:-translate-y-0.5">
              3
            </div>
            <div className="absolute -bottom-1 -right-1 bg-white rounded-full p-1 shadow-sm">
              <FileSpreadsheet className="w-4 h-4 text-indigo-500" />
            </div>
          </div>
          <div className="flex-1">
            <h3 className="text-sm font-semibold text-gray-900">
              {language === 'en'
                ? 'Import exchange CSVs & run set'
                : 'Börsen-CSVs importieren & Set berechnen'}
            </h3>
            <p className="text-sm leading-6 text-slate-600">
              {language === 'en'
                ? 'In the "My Wallets" area, upload the CSV files of your exchanges. Only with complete exchange data can token swaps and NFT trades be calculated seamlessly using FIFO. Then start the full tax analysis with "Calculate set".'
                : 'Laden Sie im Bereich „Meine Wallets" die CSV-Dateien der genutzten Börsen hoch. Nur mit vollständigen Börsendaten können Token-Swaps sowie NFT-Käufe und -Verkäufe lückenlos nach FIFO berechnet werden. Starten Sie anschließend mit „Set berechnen" die vollständige Steueranalyse.'}
            </p>
          </div>
        </div>
      </div>
    </div>
    </div>
  </section>

        </div>

          {/* Modal */}
          {/* 📊 Compare Plans Modal */}
    <ModalMotionShell
      isOpen={showModal}
      onClose={() => setShowModal(false)}
      zIndexClass="z-[200]"
      surfaceClassName="plan-modal-enter-shell"
    >
          <div
            ref={dragPlan.ref}
            className="modal-container plan-modal-container bg-white rounded-[28px] shadow-[0_30px_90px_-25px_rgba(15,23,42,0.42)] border border-white/70 w-[min(98vw,1660px)] max-w-none max-h-[min(96vh,1080px)] overflow-hidden flex flex-col"
            style={{
              transform: `translate(${dragPlan.pos.x}px, ${dragPlan.pos.y}px)`,
              zIndex: 1000
            }}
            onMouseDown={(e) => e.stopPropagation()}
            onDragStart={(e) => e.preventDefault()}
          >
          {/* Thin header (consistent with Roadmap / How it works / My Wallets) */}
          <div className="h-[5px] bg-gradient-to-r from-blue-700 via-blue-600 to-indigo-600" />

          <div
            className="px-7 py-3.5 border-b border-slate-200 flex items-start justify-between gap-4 bg-white cursor-move select-none"
            onMouseDown={(e) => {
              if (isHandheldDevice) return;
              if (e.button === 0) {
                e.preventDefault();
                e.stopPropagation();
                dragPlan.onMouseDown(e);
              }
            }}
          >
            <div className="flex items-center gap-3 min-w-0">
              {!isHandheldDevice && (
                <ModalDragHandle
                  onMouseDown={dragPlan.onMouseDown}
                  title={language === 'en' ? 'Move' : 'Verschieben'}
                  ariaLabel={language === 'en' ? 'Move modal' : 'Modal verschieben'}
                  className="mt-0.5 mr-2"
                />
              )}

              <div className="min-w-0">
                <h2 className="text-[1.2rem] font-semibold text-slate-900 truncate">
                  {language === 'en' ? 'Compare Plans' : 'Pläne vergleichen'}
                </h2>
                <p className="text-xs text-slate-500 mt-0.5">
                  {language === 'en'
                    ? 'Compare features and limits across plans'
                    : 'Vergleichen Sie Funktionen und Limits der Pläne'}
                </p>
              </div>
            </div>

            <button
              onClick={() => setShowModal(false)}
              onMouseDown={(e) => e.stopPropagation()}
              className="group text-gray-400 hover:text-gray-600 transition p-2 -m-2 flex-shrink-0"
              aria-label={language === 'en' ? 'Close' : 'Schließen'}
            >
              <X className="w-5 h-5 transition-transform duration-200 group-hover:rotate-90" />
            </button>
          </div>

          {/* Content */}
          <div className="px-6 md:px-7 pt-4 pb-5 overflow-y-auto flex-1 min-h-0">
            <div className="text-gray-700 text-sm mb-4">
              <p>
                {language === 'en' 
                  ? <>All plans include full support for <strong>VET</strong>, <strong>VTHO</strong>, and <strong>VIP-180 tokens</strong> (e.g. B3TR, VOT3, USDT). NFT transactions and SWAPs are processed with precise <strong>FIFO tax logic</strong>.</>
                  : <>Alle Pläne beinhalten vollständige Unterstützung für <strong>VET</strong>, <strong>VTHO</strong> und <strong>VIP-180-Token</strong> (z. B. B3TR, VOT3, USDT). NFT-Transaktionen und SWAPs werden mit präziser <strong>FIFO-Steuerlogik</strong> verarbeitet.</>
                }
              </p>
              <p className="mt-3 font-semibold text-gray-800">
                {language === 'en'
                  ? <>Prices are based on transactions in the respective tax year, not on the size of the entire portfolio.</>
                  : <>Die Preise basieren auf den Transaktionen im jeweiligen Steuerjahr, nicht auf der Größe des gesamten Portfolios.</>
                }
              </p>
            </div>

            {/* Premium Early-Adopter Banner */}
            <div className="relative mb-5 overflow-hidden rounded-[28px] border border-slate-200 bg-gradient-to-r from-slate-950 via-blue-950 to-slate-900 px-6 py-4 shadow-[0_22px_56px_-22px_rgba(15,23,42,0.58)]">
              <div className="pointer-events-none absolute -right-10 -top-10 h-40 w-40 rounded-full bg-amber-300/20 blur-3xl" />
              <div className="pointer-events-none absolute -left-10 bottom-0 h-32 w-32 rounded-full bg-blue-400/10 blur-3xl" />
              <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(255,255,255,0.08),transparent_34%)]" />

              <div className="relative flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                <div className="max-w-4xl">
                  <div className="group relative inline-flex items-center gap-2 overflow-hidden rounded-full border border-white/15 bg-white/10 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-amber-200 backdrop-blur-sm transition-all duration-300 hover:-translate-y-0.5 hover:border-amber-200/45 hover:bg-white/14 hover:shadow-[0_12px_28px_-16px_rgba(251,191,36,0.55)]">
                    <span className="pointer-events-none absolute -left-1/2 top-0 h-full w-1/2 rotate-12 bg-white/25 blur-md opacity-0 translate-x-0 group-hover:opacity-100 group-hover:translate-x-[220%] transition-all duration-700" />
                    <Sparkles className="relative z-10 h-3.5 w-3.5 transition-transform duration-300 group-hover:scale-110" />
                    <span className="relative z-10">
                      {language === 'en' ? 'Early-Adopter Discount' : 'Early-Adopter Rabatt'}
                    </span>
                  </div>

                  <h3 className="mt-3 text-base md:text-[1.08rem] font-semibold leading-tight text-white">
                    {language === 'en'
                      ? `Launch pricing is currently active — save up to ${maxEarlyAdopterDiscount}% before regular pricing starts.`
                      : `Aktuell ist die Launch-Preisphase aktiv — spare bis zu ${maxEarlyAdopterDiscount} %, bevor die regulären Preise gelten.`}
                  </h3>

                  <p className="mt-1.5 text-[13px] leading-5 text-blue-100/85">
                    {language === 'en'
                      ? 'These prices are currently available as a limited Early-Adopter offer. You secure the reduced launch price today; regular pricing will apply later.'
                      : 'Diese Preise gelten aktuell als limitiertes Early-Adopter-Angebot. Sie sichern sich heute den reduzierten Launch-Preis; später gelten die regulären Preise.'}
                  </p>
                </div>

                <div className="grid gap-2.5 sm:grid-cols-2 lg:w-[360px]">
                  <div className="group relative overflow-hidden rounded-2xl border border-white/12 bg-white/10 px-3.5 py-3 backdrop-blur-sm transition-all duration-300 hover:-translate-y-0.5 hover:border-blue-200/25 hover:bg-white/14 hover:shadow-[0_16px_34px_-18px_rgba(59,130,246,0.45)]">
                    <span className="pointer-events-none absolute -left-1/2 top-0 h-full w-1/2 rotate-12 bg-white/20 blur-md opacity-0 translate-x-0 group-hover:opacity-100 group-hover:translate-x-[220%] transition-all duration-700" />
                    <div className="relative z-10 text-[10px] font-semibold uppercase tracking-[0.16em] text-blue-100/70">
                      {language === 'en' ? 'Regular Pricing' : 'Reguläre Preise'}
                    </div>
                    <div className="relative z-10 mt-1 text-sm font-semibold text-white">
                      {formatPrice(lowestRegularPaidPrice)} – {formatPrice(highestRegularPaidPrice)}
                    </div>
                  </div>

                  <div className="group relative overflow-hidden rounded-2xl border border-amber-300/25 bg-amber-300/10 px-3.5 py-3 backdrop-blur-sm transition-all duration-300 hover:-translate-y-0.5 hover:border-amber-200/45 hover:bg-amber-300/14 hover:shadow-[0_16px_34px_-18px_rgba(251,191,36,0.42)]">
                    <span className="pointer-events-none absolute -left-1/2 top-0 h-full w-1/2 rotate-12 bg-white/20 blur-md opacity-0 translate-x-0 group-hover:opacity-100 group-hover:translate-x-[220%] transition-all duration-700" />
                    <div className="relative z-10 text-[10px] font-semibold uppercase tracking-[0.16em] text-amber-100/80">
                      {language === 'en' ? 'Launch Advantage' : 'Launch-Vorteil'}
                    </div>
                    <div className="relative z-10 mt-1 text-sm font-semibold text-white">
                      {language === 'en' ? `Up to ${maxEarlyAdopterDiscount}% off` : `Bis zu ${maxEarlyAdopterDiscount} % günstiger`}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="rounded-2xl border border-slate-200 shadow-sm bg-white overflow-hidden">
              <table className="w-full table-fixed text-[13.5px]">
                <thead>
                  <tr className="bg-gradient-to-r from-slate-50 via-blue-50 to-slate-50 text-left">
                    <th className="w-[25%] p-4 font-semibold text-slate-900">
                      {language === 'en' ? 'Feature' : 'Funktion'}
                    </th>

                    {/* Free */}
                    <th className="p-4 font-semibold text-center min-w-[130px] text-blue-900">
                      <div className={`group flex flex-col items-center justify-center gap-3 rounded-xl border border-transparent px-2 py-2 shadow-sm transition-all duration-300 hover:shadow-md ${accentClassByPlanId("free")}`}>
                        <span className="truncate text-sm font-semibold leading-none">Free</span>
                        <PlanBadge 
                          planId="free" 
                          size="sm" 
                          variant="compact" 
                          className="transform-gpu transition-transform duration-200 ease-out group-hover:-translate-y-0.5 group-hover:scale-[1.04]"
                        />
                      </div>
                    </th>

                    {/* Standard */}
                    <th className="p-4 font-semibold text-center min-w-[130px] text-blue-900">
                      <div className={`group flex flex-col items-center justify-center gap-3 rounded-xl border border-transparent px-2 py-2 shadow-sm transition-all duration-300 hover:shadow-md ${accentClassByPlanId("standard")}`}>
                        <span className="truncate text-sm font-semibold leading-none">Standard</span>
                        <PlanBadge 
                          planId="standard" 
                          size="sm" 
                          variant="compact" 
                          className="transform-gpu transition-transform duration-200 ease-out group-hover:-translate-y-0.5 group-hover:scale-[1.04]"
                        />
                      </div>
                    </th>

                    {/* Pro */}
                    <th className="p-4 font-semibold text-center min-w-[130px] text-blue-900">
                      <div className={`group flex flex-col items-center justify-center gap-3 rounded-xl border border-transparent px-2 py-2 shadow-sm transition-all duration-300 hover:shadow-md ${accentClassByPlanId("pro")}`}>
                        <span className="truncate text-sm font-semibold leading-none">Pro</span>
                        <PlanBadge 
                          planId="pro" 
                          size="sm" 
                          variant="compact" 
                          className="transform-gpu transition-transform duration-200 ease-out group-hover:-translate-y-0.5 group-hover:scale-[1.04]"
                        />
                      </div>
                    </th>

                    {/* Elite */}
                    <th className="p-4 font-semibold text-center min-w-[130px] text-blue-900">
                      <div className={`group flex flex-col items-center justify-center gap-3 rounded-xl border border-transparent px-2 py-2 shadow-sm transition-all duration-300 hover:shadow-md ${accentClassByPlanId("elite")}`}>
                        <span className="truncate text-sm font-semibold leading-none">Elite</span>
                        <PlanBadge 
                          planId="elite" 
                          size="sm" 
                          variant="compact" 
                          className="transform-gpu transition-transform duration-200 ease-out group-hover:-translate-y-0.5 group-hover:scale-[1.04]"
                        />
                      </div>
                    </th>

                    {/* Ultimate */}
                    <th className="p-4 font-semibold text-center min-w-[130px] text-blue-900">
                      <div className={`group flex flex-col items-center justify-center gap-3 rounded-xl border border-transparent px-2 py-2 shadow-sm transition-all duration-300 hover:shadow-md ${accentClassByPlanId("ultimate")}`}>
                        <span className="truncate text-sm font-semibold leading-none">Ultimate</span>
                        <PlanBadge 
                          planId="ultimate" 
                          size="sm" 
                          variant="compact" 
                          className="transform-gpu transition-transform duration-200 ease-out group-hover:-translate-y-0.5 group-hover:scale-[1.04]"
                        />
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-t transition-colors hover:bg-blue-50">
                    <td className="p-4">{language === 'en' ? 'Max. Transactions' : 'Max. Transaktionen'}</td>
                    <td className="p-4 text-center">100</td>
                    <td className="p-4 text-center">1.000</td>
                    <td className="p-4 text-center">3.000</td>
                    <td className="p-4 text-center">10.000</td>
                    <td className="p-4 text-center">{language === 'en' ? 'Unlimited' : 'Unbegrenzt'}</td>
                  </tr>
                  <tr className="border-t transition-colors hover:bg-blue-50">
                    <td className="p-4">{language === 'en' ? 'Airdrops, Governance Rewards, Stargate Staking' : 'Airdrops, Governance Rewards, Stargate Staking'}</td>
                    <td className="p-4 text-center"><FeatureYes /></td>
                    <td className="p-4 text-center"><FeatureYes /></td>
                    <td className="p-4 text-center"><FeatureYes /></td>
                    <td className="p-4 text-center"><FeatureYes /></td>
                    <td className="p-4 text-center"><FeatureYes /></td>
                  </tr>
                  <tr className="border-t transition-colors hover:bg-blue-50">
                    <td className="p-4">{language === 'en' ? 'Automatic Swap Classification & Calculation' : 'Automatische Swap-Klassifizierung & Berechnung'}</td>
                    <td className="p-4 text-center"><FeatureYes /></td>
                    <td className="p-4 text-center"><FeatureYes /></td>
                    <td className="p-4 text-center"><FeatureYes /></td>
                    <td className="p-4 text-center"><FeatureYes /></td>
                    <td className="p-4 text-center"><FeatureYes /></td>
                  </tr>
                  <tr className="border-t transition-colors hover:bg-blue-50">
                    <td className="p-4">{language === 'en' ? 'NFT Tax Support (Galaxy Upgrades, NFT Buy & Sell)' : 'NFT-Steuerunterstützung (Galaxy Upgrades, NFT Kauf & Verkauf)'}</td>
                    <td className="p-4 text-center"><FeatureYes /></td>
                    <td className="p-4 text-center"><FeatureYes /></td>
                    <td className="p-4 text-center"><FeatureYes /></td>
                    <td className="p-4 text-center"><FeatureYes /></td>
                    <td className="p-4 text-center"><FeatureYes /></td>
                  </tr>
                  <tr className="border-t transition-colors hover:bg-blue-50">
                    <td className="p-4">{language === 'en' ? 'CSV Export' : 'CSV-Export'}</td>
                    <td className="p-4 text-center"><FeatureNo /></td>
                    <td className="p-4 text-center"><FeatureYes /></td>
                    <td className="p-4 text-center"><FeatureYes /></td>
                    <td className="p-4 text-center"><FeatureYes /></td>
                    <td className="p-4 text-center"><FeatureYes /></td>
                    </tr>
                  <tr className="border-t transition-colors hover:bg-blue-50">
                    <td className="p-4">{language === 'en' ? 'PDF Export' : 'PDF-Export'}</td>
                    <td className="p-4 text-center"><FeatureNo /></td>
                    <td className="p-4 text-center"><FeatureYes /></td>
                    <td className="p-4 text-center"><FeatureYes /></td>
                    <td className="p-4 text-center"><FeatureYes /></td>
                    <td className="p-4 text-center"><FeatureYes /></td>
                  </tr>
                  <tr className="border-t border-slate-200 bg-gradient-to-b from-slate-50 to-white align-top">
                    <td className="p-4 align-middle">
                      <div className="w-[245px]">
                        <div className="text-sm font-bold text-slate-900">
                          {language === 'en' ? 'Price' : 'Preis'}
                        </div>

                        <div className="mt-1.5 whitespace-nowrap text-[12px] leading-5 text-slate-500">
                          {language === 'en'
                            ? 'Early-Adopter launch pricing per tax year.'
                            : 'Early-Adopter Launch-Preise je Steuerjahr.'}
                        </div>
                      </div>
                    </td>

                    <td className="p-2.5 align-top">
                      <div className="flex h-[154px] flex-col justify-between rounded-[20px] border border-slate-200 bg-white px-3.5 py-3.5 text-center shadow-[0_14px_34px_-28px_rgba(15,23,42,0.45)]">
                        <div className="flex items-center justify-between gap-2">
                          <span className="min-w-0 truncate text-xs font-semibold text-slate-700">
                            Free
                          </span>

                          <span className="shrink-0 whitespace-nowrap rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[9px] font-bold uppercase tracking-[0.1em] text-emerald-700">
                            {language === 'en' ? 'Included' : 'Inklusive'}
                          </span>
                        </div>

                        <div>
                          <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-400">
                            Starter
                          </div>

                          <div className="mt-1 flex items-baseline justify-center gap-1 text-slate-950">
                            <span className="text-[1.62rem] font-bold leading-none tracking-[-0.025em] tabular-nums">
                              0
                            </span>
                            <span className="text-base font-bold">€</span>
                          </div>

                          <div className="mt-2 text-[11px] font-medium text-slate-400">
                            {language === 'en' ? 'Always free' : 'Immer kostenlos'}
                          </div>
                        </div>

                        <div className="flex items-center justify-between rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-[11px]">
                          <span className="font-medium text-slate-500">
                            {language === 'en' ? 'Best for' : 'Ideal für'}
                          </span>

                          <span className="whitespace-nowrap font-semibold text-slate-700">
                            {language === 'en' ? 'Small wallets' : 'Kleine Wallets'}
                          </span>
                        </div>
                      </div>
                    </td>

                    {paidPlanIds.map((planId) => {
                      const launch = earlyAdopterLaunchPricesEUR[planId];
                      const regular = earlyAdopterRegularPricesEUR[planId];
                      const discountPct = getPlanDiscountPercent(planId);
                      const savings = getPlanSavingsEUR(planId);

                      return (
                        <td key={planId} className="p-2.5 align-top">
                          <div className="group relative flex h-[154px] flex-col justify-between rounded-[20px] border border-slate-200 bg-white px-3.5 py-3.5 text-center shadow-[0_14px_34px_-28px_rgba(15,23,42,0.45)] transition-all duration-300 hover:-translate-y-0.5 hover:border-blue-200/80 hover:shadow-[0_16px_36px_-28px_rgba(37,99,235,0.38)]">
                            <div className="pointer-events-none absolute inset-x-4 top-0 h-px bg-gradient-to-r from-transparent via-blue-400/45 to-transparent" />

                            <div className="flex items-center justify-between gap-2">
                              <span className="min-w-0 truncate text-xs font-semibold text-slate-700">
                                {formatPlanDisplay(planId)}
                              </span>

                              <span className="shrink-0 rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[9px] font-bold tabular-nums text-amber-700">
                                -{discountPct}%
                              </span>
                            </div>

                            <div>
                              <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-blue-600/70">
                                {language === 'en' ? 'Launch price' : 'Launch-Preis'}
                              </div>

                              <div className="mt-1 text-[1.62rem] font-bold leading-none tracking-[-0.025em] text-slate-950 tabular-nums">
                                {formatPrice(launch)}
                              </div>

                              <div className="mt-2 flex items-center justify-center gap-2 text-[11px]">
                                <span className="font-medium text-slate-400">
                                  {language === 'en' ? 'Regular' : 'Regulär'}
                                </span>

                                <span className="font-semibold text-slate-400 line-through decoration-slate-400/70">
                                  {formatPrice(regular)}
                                </span>
                              </div>
                            </div>

                            <div className="flex items-center justify-between rounded-xl border border-emerald-100 bg-emerald-50/70 px-3 py-2 text-[11px]">
                              <span className="font-medium text-slate-500">
                                {language === 'en' ? 'You save' : 'Du sparst'}
                              </span>

                              <span className="font-semibold tabular-nums text-emerald-700">
                                {formatPrice(savings)}
                              </span>
                            </div>
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </ModalMotionShell>

    {/* Help & Support Modal */}
    <HelpSupportModal
      open={showHelpModal}
      onClose={handleHelpModalClose}
      language={language}
      initialSection={helpActiveSection}
      initialCategory={supportForm.category}
    />

    {/* Legal Notices Modal */}
    <LegalNoticesModal
      open={showLegalModal}
      onClose={() => setShowLegalModal(false)}
      language={language}
    />

    {/* 🗺️ Roadmap Modal */}
    <RoadmapModal
      open={showRoadmap}
      onClose={() => setShowRoadmap(false)}
      language={language}
      connectedWalletAddress={user?.walletAddress}
    />

    {/* 🧮 Function Modal - Wie V3Tax funktioniert */}
    <HowV3TaxWorksModal
      open={showFunctionModal}
      onClose={() => setShowFunctionModal(false)}
      language={language}
    />


      {/* My Wallets Dashboard */}
      {/* ✅ Exchange-Imports-Modal für Preview */}
      <ExchangeImportsManager
        isOpen={showPreviewImports}
        mode="preview"
        isPreviewFlow={true}
        isAdminFlow={isAdminWallet}
        adminWallets={analyzedWallets}
        onAdminAddAnotherWallet={() => {
          setShowPreviewImports(false);
          setShowNextWalletHint(true);
        }}
        datasetId={null} // egal in preview
        initialRows={[]}
        onClose={() => setShowPreviewImports(false)}
        onOpenSupport={() => {
          setShowPreviewImports(false);
          setShowHelpModal(true);
          setHelpActiveSection('support');
          setSupportForm((prev) => ({
            ...prev,
            category: 'CSV Import',
          }));

          window._supportModalCloseHandler = () => {
            setShowHelpModal(false);
            setShowPreviewImports(true);
            window._supportModalCloseHandler = null;
          };
        }}
        onContinuePreview={({ rows, files }) => {
          setShowPreviewImports(false);
          setPreviewExchangeRows(rows || []);
          if (isAdminWallet) {
            navigateToAdminFullAnalysisWithImports(rows || []);
          } else {
            navigateToPreviewWithImports(rows || []);
          }
        }}
        onContinuePreviewWithoutCsv={({ rows, files }) => {
          setShowPreviewImports(false);
          setPreviewExchangeRows([]);
          if (isAdminWallet) {
            navigateToAdminFullAnalysisWithImports([]);
          } else {
            navigateToPreviewWithImports([]);
          }
        }}
        onConfirmPreview={(rows) => {
          // Fallback für Rückwärtskompatibilität
          setShowPreviewImports(false);
          setPreviewExchangeRows(rows || []);
          if (isAdminWallet) {
            navigateToAdminFullAnalysisWithImports(rows || []);
          } else {
            navigateToPreviewWithImports(rows || []);
          }
        }}
      />

      <MyWalletsDashboard 
        isOpen={showMyWalletsDashboard} 
        onClose={() => setShowMyWalletsDashboard(false)}
        onOpenSupport={(onSupportClose) => {
          // Dashboard in App offen lassen (showMyWalletsDashboard true).
          // Nur im Child: ExchangeImportsManager zu + UI per isSuspendedForSupport ausblenden,
          // dann Help öffnen; nach Schließen wieder ExchangeImportsManager.
          setShowHelpModal(true);
          setHelpActiveSection('support');
          setSupportForm(prev => ({
            ...prev,
            category: 'CSV Import'
          }));
          window._supportModalCloseHandler = () => {
            setShowHelpModal(false);
            onSupportClose?.();
            window._supportModalCloseHandler = null;
          };
        }}
        userWallet={wallet}
        onWalletSelect={(selectedWallet) => {
                    
          // 🚀 NEU: Unterstützung für Multi-Wallet-Sets
          if (selectedWallet.type === 'multi-wallet-set') {
                        
            // 🚀 WICHTIG: Für Multi-Wallet-Sets alle Wallets zur AnalysisApp übergeben
            // Setze die ersten beiden Wallets für die UI, aber alle Wallets werden zur Berechnung verwendet
            if (selectedWallet.mainWallets && selectedWallet.mainWallets.length > 0) {
              setWallet(selectedWallet.mainWallets[0]);
            }
            
            if (selectedWallet.delegateWallets && selectedWallet.delegateWallets.length > 0) {
              setDelegateWallet(selectedWallet.delegateWallets[0]);
            }
            
            // 🚀 WICHTIG: Alle Wallets sind bereits in selectedWallet.addresses verfügbar
                        
            // 🚀 WICHTIG: Für Multi-Wallet-Sets müssen wir alle Wallets zur Berechnung übergeben
            // Speichere alle Wallets in einem globalen State für die AnalysisApp
            window.multiWalletSet = {
              addresses: selectedWallet.addresses, // 🚀 KRITISCH: addresses für Komponenten
              allWallets: selectedWallet.addresses,
              mainWallets: selectedWallet.mainWallets,
              delegateWallets: selectedWallet.delegateWallets,
              datasetId: selectedWallet.datasetId,
              datasetLabel: selectedWallet.datasetLabel,
              year: selectedWallet.year
            };
            
                        
            // 🎯 Jahr aus Dataset setzen
            setYear(selectedWallet.year || "2025");
            
            // 🎯 Plan aus Dataset setzen
            setPlan(normalizePlanId(selectedWallet.plan || plan)); // Verwende Plan aus Dataset oder aktuellen Plan
            
                      } else {
            // 🎯 ALTE LOGIK: Einzelnes Wallet
            setWallet(selectedWallet.mainWallet);
            
            // 🎯 VeDelegate Wallet auch setzen (falls vorhanden)
            if (selectedWallet.delegateWallet) {
              setDelegateWallet(selectedWallet.delegateWallet);
            }
            
            // 🎯 Jahr auf 2025 setzen (da alle gekauften Wallets für 2025 sind)
            setYear("2025");
            
            // 🎯 Plan aus Dataset verwenden oder aktuellen Plan beibehalten
            if (selectedWallet.plan) {
              setPlan(normalizePlanId(selectedWallet.plan));
            }
          }
          
          // 🎯 WICHTIG: Missing Fields zurücksetzen da alle Felder korrekt gefüllt sind
          setMissingFields({
            wallet: false,
            year: false,
            plan: false,
            payment: false,
            country: false,
          });
          // 🎯 Flag setzen dass gekaufte Wallets geladen wurden
          setIsPurchasedWalletLoaded(true);
                    
          // Dashboard schließen
          setShowMyWalletsDashboard(false);
          
                    
          // 🚀 AUTOMATISCHE ANALYSE STARTEN wenn autoAnalyze Flag gesetzt ist
          if (selectedWallet.autoAnalyze) {
                        
            // 🎯 Direkt Button-Modus auf "calculate" setzen (da gekaufte Wallet)
            setButtonMode("dashboard");
                        
            // 🎯 WICHTIG: Missing Fields zurücksetzen da alle Felder korrekt gefüllt sind
            setMissingFields({
              wallet: false,
              year: false,
              plan: false,
              payment: false,
              country: false,
            });
                        
            // 🎯 WICHTIG: Für gekaufte Wallets direkt zur Analysis App navigieren
            // statt über handleAnalyzeClick zu gehen (da das für neue Analysen gedacht ist)
                        
            // Kleine Verzögerung damit die State-Updates abgeschlossen sind
            setTimeout(() => {
              const datasetCountry = resolveResidenceCountryForApi(
                selectedWallet.residence_country ||
                selectedWallet.residenceCountry ||
                selectedWallet.selectedCountry ||
                selectedWallet.country ||
                ''
              );

              navigate("/analyze", {
                state: {
                  wallet: selectedWallet.mainWallet, // 🎯 Main Wallet als primäre wallet
                  year: "2025",
                  plan: selectedWallet.plan || plan,
                  delegateWallet: selectedWallet.delegateWallet || null, // 🎯 VeDelegate Wallet mitgeben
                  selectedCountry: datasetCountry || undefined,
                  isPurchased: true, // Flag dass Wallet gekauft ist
                  fromViewAnalysis: false, // 🎯 NEU: Kein View Analysis, neue Berechnung
                  loadFromSnapshot: false, // 🎯 NEU: Kein Snapshot laden
                  autorun: 'calc',
                  // ✅ WICHTIG: datasetId für normale Analyse
                  datasetId: selectedWallet.datasetId || selectedWallet.purchaseId || selectedWallet.id
                }
              });
            }, 100);
          } else if (selectedWallet.loadFromSnapshot) {
            // 🎯 NEU: Snapshot laden statt neue Berechnung
                        
            // 🎯 Direkt Button-Modus auf "view" setzen (da Snapshot laden)
            setButtonMode("view");
                        
            // 🎯 WICHTIG: Missing Fields zurücksetzen da alle Felder korrekt gefüllt sind
            setMissingFields({
              wallet: false,
              year: false,
              plan: false,
              payment: false,
              country: false,
            });
                        
            // 🎯 WICHTIG: Für Snapshot-Laden direkt zur Analysis App navigieren
                        
            // Kleine Verzögerung damit die State-Updates abgeschlossen sind
            setTimeout(() => {
              const datasetCountry = resolveResidenceCountryForApi(
                selectedWallet.residence_country ||
                selectedWallet.residenceCountry ||
                selectedWallet.selectedCountry ||
                selectedWallet.country ||
                ''
              );

              navigate("/analyze", {
                state: {
                  wallet: selectedWallet.mainWallet, // 🎯 Main Wallet als primäre wallet
                  year: "2025",
                  plan: selectedWallet.plan || plan,
                  delegateWallet: selectedWallet.delegateWallet || null, // 🎯 VeDelegate Wallet mitgeben
                  selectedCountry: datasetCountry || undefined,
                  isPurchased: true, // Flag dass Wallet gekauft ist
                  fromViewAnalysis: true, // 🎯 NEU: View Analysis, Snapshot laden
                  loadFromSnapshot: true, // 🎯 NEU: Snapshot laden
                  autorun: 'view',
                  // ✅ WICHTIG: datasetId für View-Analyse
                  datasetId: selectedWallet.datasetId || selectedWallet.purchaseId || selectedWallet.id
                }
              });
            }, 100);
          }
        }}
      />

      {/* 🛒 Plan Modal */}
      <PlanModal
        isOpen={showPlanModal}
        onClose={() => setShowPlanModal(false)}
        selectedYear={year}
        walletAddress={wallet}
        transactionCount={transactionCount}
        onPlanSelected={(selectedPlan) => {
                    setPlan(normalizePlanId(selectedPlan.name));
          // 🚀 Plan auch im localStorage speichern für PaymentSuccess
          localStorage.setItem('selectedPlan', selectedPlan.name);
        }}
        onPaymentMethodSelected={(selectedPaymentMethod) => {
                    setPaymentMethod(selectedPaymentMethod);
        }}
      />

      {/* 🚀 Krypto-Checkout Modal */}
      <ModalMotionShell
        isOpen={showCryptoCheckout && !!cryptoCheckoutData}
        onClose={() => {
          setShowCryptoCheckout(false);
          setCryptoCheckoutData(null);
        }}
        zIndexClass="z-[200]"
      >
        {showCryptoCheckout && cryptoCheckoutData ? (
          <div
            className="modal-container bg-white rounded-xl p-8 max-w-lg w-full mx-4 shadow-2xl"
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div className="text-center">
              <h2 className="text-2xl font-bold text-gray-800 mb-4">
                {language === 'en' ? 'Crypto Payment' : 'Krypto-Zahlung'}
                        </h2>
              
              {/* 🕐 Session-Timer */}
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-4">
                <div className="flex items-center justify-center space-x-2">
                  <div className="text-orange-600">⏰</div>
                  <div className="text-sm font-medium text-orange-700">
                    {language === 'en' ? 'Session expires in:' : 'Session läuft ab in:'}
                  </div>
                  <div className="text-lg font-mono font-bold text-orange-800">
                    {Math.floor(timeRemaining / 1000 / 60)}:{(Math.floor(timeRemaining / 1000) % 60).toString().padStart(2, '0')}
                  </div>
                </div>
                      </div>

              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <p className="text-sm text-gray-600 mb-2">
                  {language === 'en' ? 'Send exactly this amount:' : 'Sende genau diesen Betrag:'}
                </p>
                <div className="text-2xl font-mono font-bold text-green-600">
                  {cryptoCheckoutData.priceAmount} {cryptoCheckoutData.priceCurrency.toUpperCase()}
                </div>
                
                {/* 💰 Live-Preise anzeigen */}
                {cryptoCheckoutData.currentPrices && (
                  <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded">
                    <p className="text-xs text-blue-700 mb-2">
                      💰 {language === 'en' ? 'Live Prices:' : 'Live-Preise:'}
                    </p>
                    <div className="space-y-1 text-xs">
                      <div className="flex justify-between">
                        <span>VET:</span>
                        <span className="font-mono">€{cryptoCheckoutData.currentPrices.vet?.price_eur?.toFixed(9) || 'N/A'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>b3tr:</span>
                        <span className="font-mono">€{cryptoCheckoutData.currentPrices.b3tr?.price_eur?.toFixed(9) || 'N/A'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>vtho:</span>
                        <span className="font-mono">€{cryptoCheckoutData.currentPrices.vtho?.price_eur?.toFixed(9) || 'N/A'}</span>
                      </div>
                    </div>
                  </div>
                )}
                
                <p className="text-xs text-gray-500 mt-3">
                  {language === 'en' ? 'Pay with any of these tokens:' : 'Zahle mit einem dieser Token:'}
                </p>
                <div className="mt-2 space-y-1">
                  {Object.entries(cryptoCheckoutData.paymentAmounts || {}).map(([token, amount]) => (
                    <div key={token} className="text-sm font-mono text-gray-700">
                      {parseFloat(amount).toFixed(7)} {token.toUpperCase()}
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <p className="text-sm text-gray-600 mb-2">
                  {language === 'en' ? 'Payment Address (same for all tokens):' : 'Zahlungsadresse (gleich für alle Token):'}
                </p>
                
                <div className="bg-white border border-gray-200 rounded p-3 mb-3">
                  <code className="text-sm font-mono break-all text-gray-800">
                    {cryptoCheckoutData.paymentAddresses?.vet || 'Adresse wird geladen...'}
                  </code>
                </div>
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(cryptoCheckoutData.paymentAddresses?.vet);
                  }}
                  className="text-blue-600 hover:text-blue-700 text-sm font-medium"
                >
                  {language === 'en' ? 'Copy Address' : 'Adresse kopieren'}
                </button>
                
                <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded">
                  <p className="text-sm text-blue-700">
                    💡 {language === 'en' 
                      ? 'You can pay with VET, b3tr, or vtho to the same address above' 
                      : 'Du kannst mit VET, b3tr oder vtho an dieselbe Adresse oben zahlen'}
                  </p>
                </div>
              </div>
              
              <div className="text-xs text-gray-500 mb-6">
                <p>
                  {language === 'en' 
                    ? 'Payment will be confirmed automatically once received. You can close this window after sending.' 
                    : 'Die Zahlung wird automatisch bestätigt sobald sie empfangen wird. Du kannst dieses Fenster nach dem Senden schließen.'}
                </p>
                <div className="mt-3 p-2 bg-green-50 border border-green-200 rounded">
                  <p className="text-xs text-green-700">
                    🔍 {language === 'en' 
                      ? 'Blockchain monitoring is active - payment will be confirmed automatically!' 
                      : 'Blockchain-Überwachung ist aktiv - Zahlung wird automatisch bestätigt!'}
                  </p>
                </div>
              </div>
              
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    // 🚨 WICHTIG: Timer komplett stoppen und zurücksetzen
                    if (sessionTimer) {
                      clearInterval(sessionTimer);
                      setSessionTimer(null);
                      setTimeRemaining(0);
                    }
                    
                    setShowCryptoCheckout(false);
                    setCryptoCheckoutData(null);
                  }}
                  className="flex-1 bg-gray-200 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-300 transition-colors"
                >
                  {language === 'en' ? 'Close' : 'Schließen'}
                </button>
                <button
                  onClick={() => {
                    // 🚨 WICHTIG: Timer komplett stoppen und zurücksetzen
                    if (sessionTimer) {
                      clearInterval(sessionTimer);
                      setSessionTimer(null);
                      setTimeRemaining(0);
                    }
                    
                    // Hier können wir den Payment-Status prüfen
                    setShowCryptoCheckout(false);
                    setCryptoCheckoutData(null);
                  }}
                  className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                >
                  {language === 'en' ? 'Check Status' : 'Status prüfen'}
                </button>
              </div>
            </div>
          </div>
        ) : null}
      </ModalMotionShell>

      {/* VeWorld-Zahlung: EUR/Token-Preis, Betrag, „Transaktion starten“ (Browser-Extension) */}
      <ModalMotionShell
        isOpen={showAutoTransactionModal}
        onClose={() => {
          setShowAutoTransactionModal(false);
          setAutoTransactionData(null);
          setAutoTransactionError(null);
          setAutoTransactionSuccess(false);
        }}
        zIndexClass="z-[200]"
        backdropTintClass="bg-slate-950/60"
        backdropClassName="p-4 !backdrop-blur-[5px]"
      >
        {showAutoTransactionModal && autoTransactionData ? (
            <div
              ref={dragAutoModal.ref}
              className="auto-modal-container relative w-full max-w-[590px] overflow-hidden rounded-[30px] border border-slate-200/90 bg-white shadow-[0_32px_90px_rgba(15,23,42,0.22)]"
              style={{ ...getAutoModalStyle(), zIndex: 1000 }}
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
              onDragStart={(e) => e.preventDefault()}
            >
              {/* Consistent top line like other premium modals */}
              <div className="h-[5px] bg-gradient-to-r from-blue-700 via-blue-600 to-indigo-600" />

              {/* Premium background layers */}
              <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(59,130,246,0.08),transparent_34%),radial-gradient(circle_at_bottom_left,rgba(99,102,241,0.06),transparent_28%)]" />
              <div className="pointer-events-none absolute inset-x-0 top-[5px] h-24 bg-gradient-to-b from-slate-50/90 to-transparent" />

              <div className="relative z-10 p-6 sm:p-7">
                {/* Header */}
                <div
                  className="auto-modal-header mb-6 flex items-start justify-between gap-4 border-b border-slate-200/80 pb-5 bg-white cursor-move select-none"
                  onMouseDown={(e) => {
                    if (isHandheldDevice) return;
                    e.preventDefault();
                    e.stopPropagation();
                    dragAutoModal.onMouseDown(e);
                  }}
                >
                  <div className="flex min-w-0 items-start gap-4">
                    <div
                      className="flex h-[88px] w-[88px] shrink-0 items-center justify-center rounded-[24px] border border-slate-200 bg-gradient-to-b from-white to-slate-50 shadow-[0_16px_36px_rgba(15,23,42,0.11)]"
                      title="VeWorld"
                    >
                      <img
                        src={autoModalVeWorldLogo}
                        alt="VeWorld"
                        className="h-[52px] w-[52px] object-contain"
                      />
                    </div>

                    <div className="min-w-0 pt-1">
                      <div className="mb-2 flex flex-wrap items-center gap-2">
                        <span className="inline-flex items-center rounded-full border border-slate-200 bg-white px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-600">
                          VeWorld
                        </span>

                        {autoTransactionData?.isUpgrade && (
                          <span className="inline-flex items-center rounded-full border border-amber-200 bg-amber-50 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-amber-700">
                            {language === 'en' ? 'Upgrade' : 'Upgrade'}
                          </span>
                        )}
                      </div>

                      <h2 className="text-[23px] font-semibold leading-tight tracking-[-0.02em] text-slate-900">
                        {autoModalTitle}
                      </h2>

                      <p className="mt-1 text-sm font-medium text-slate-500">
                        {autoModalSubtitle}
                      </p>
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={() => {
                      setShowAutoTransactionModal(false);
                      setAutoTransactionData(null);
                      setAutoTransactionError(null);
                      setAutoTransactionSuccess(false);
                    }}
                    onMouseDown={(e) => e.stopPropagation()}
                    className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-slate-200 bg-white/95 text-slate-500 transition-all duration-200 hover:border-slate-300 hover:bg-slate-50 hover:text-slate-700"
                    title={language === 'en' ? 'Close' : 'Schließen'}
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>

                {/* Premium summary */}
                <div className="mb-5 rounded-[24px] border border-slate-200 bg-white/95 p-5 shadow-[0_10px_30px_rgba(15,23,42,0.06)]">
                  <div className="grid grid-cols-1 gap-5 sm:grid-cols-[1fr_auto] sm:items-start">
                    <div>
                      <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-400">
                        {language === 'en' ? 'Payment total' : 'Zahlungsbetrag'}
                      </div>

                      <div className="mt-2 flex flex-wrap items-end gap-x-2 gap-y-1">
                        <span className="text-[27px] sm:text-[31px] font-semibold tracking-[-0.04em] text-slate-950">
                          {autoModalAmount}
                        </span>
                        <span className="pb-1 text-sm font-semibold uppercase tracking-[0.08em] text-slate-500">
                          {autoModalTokenMeta.label}
                        </span>
                      </div>

                      <div className="mt-2 text-sm text-slate-500">
                        €{autoTransactionData.priceEUR}
                      </div>
                    </div>

                    <div className="flex min-w-[172px] flex-col gap-2">
                      <div
                        className={`group relative overflow-hidden rounded-2xl border border-slate-200 bg-slate-50/80 px-4 py-3 text-right transition-all duration-300 transform-gpu hover:-translate-y-0.5 hover:shadow-md ${accentClassByPlanId(autoModalPlanId)}`}
                      >
                        <span className="pointer-events-none absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-gradient-to-r from-transparent via-white/60 to-transparent" />
                        <span className="pointer-events-none absolute -left-1/2 top-0 h-full w-1/2 rotate-12 bg-white/35 blur-md opacity-0 translate-x-0 group-hover:opacity-100 group-hover:translate-x-[220%] transition-all duration-700" />

                        <div className="relative z-10 text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400">
                          {language === 'en' ? 'Plan' : 'Plan'}
                        </div>

                        <div className="relative z-10 mt-2 flex items-center justify-end gap-2">
                          <PlanBadge
                            planId={autoModalPlanId}
                            size="sm"
                            variant="compact"
                            title={autoModalPlanName}
                            className="shrink-0 transition-transform duration-300 group-hover:scale-[1.06]"
                          />
                          <span className="text-sm font-semibold text-slate-900">
                            {autoModalPlanName}
                          </span>
                        </div>
                      </div>

                      <div className="rounded-2xl border border-slate-200 bg-slate-50/80 px-4 py-3 text-right">
                        <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400">
                          {language === 'en' ? 'Year' : 'Jahr'}
                        </div>
                        <div className="mt-1 text-sm font-semibold text-slate-900">
                          {autoTransactionData.year}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Detail rows */}
                <div className="mb-5 overflow-hidden rounded-[22px] border border-slate-200 bg-slate-50/70">
                  <div className="flex items-start justify-between gap-4 px-5 py-4">
                    <div>
                      <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400">
                        {language === 'en' ? 'Token price' : 'Token-Preis'}
                      </div>
                      <div className="mt-1 text-sm font-semibold text-slate-900 break-all">
                        €{autoTransactionData.tokenPriceEUR ? autoTransactionData.tokenPriceEUR.toFixed(9) : 'N/A'} / {autoModalTokenMeta.label}
                      </div>
                    </div>
                  </div>

                  <div className="h-px bg-slate-200" />

                  <div className="flex items-start justify-between gap-4 px-5 py-4">
                    <div>
                      <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400">
                        {language === 'en' ? 'Wallet scope' : 'Wallet-Umfang'}
                      </div>
                      <div className="mt-1 text-sm font-semibold text-slate-900">
                        {autoTransactionData?.isUpgrade
                          ? (language === 'en' ? 'Plan upgrade only' : 'Nur Plan-Upgrade')
                          : `${autoTransactionData.walletCount || 0} ${language === 'en' ? 'wallet(s)' : 'Wallet(s)'}`}
                      </div>
                    </div>

                    <div className="text-right min-w-[170px]">
                      <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400">
                        {language === 'en' ? 'Method' : 'Methode'}
                      </div>

                      <div
                        className={`group mt-2 inline-flex min-w-[132px] items-center justify-center gap-3 rounded-[18px] border px-4 py-2.5 shadow-sm transition-all duration-300 transform-gpu hover:-translate-y-0.5 hover:shadow-md ${autoModalTokenMeta.pillClass} ${autoModalTokenMeta.pillHoverClass}`}
                      >
                        {autoModalTokenMeta.icon && (
                          <span
                            className={`inline-flex h-9 w-9 items-center justify-center rounded-[14px] border shadow-sm transition-all duration-300 group-hover:scale-[1.06] ${autoModalTokenMeta.iconWrapClass}`}
                          >
                            <img
                              src={autoModalTokenMeta.icon}
                              alt={autoModalTokenMeta.label}
                              className="h-5.5 w-5.5 object-contain transition-transform duration-300 group-hover:scale-110"
                            />
                          </span>
                        )}
                        <span className="text-sm font-semibold text-slate-900">
                          {autoModalTokenMeta.label}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="h-px bg-slate-200" />

                  <div className="px-5 py-4">
                    <div className="mb-3 flex items-center justify-between gap-3">
                      <div>
                        <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400">
                          {language === 'en' ? 'Recipient' : 'Empfänger'}
                        </div>
                        <div className="mt-1 text-sm font-semibold text-slate-900">
                          v3tax.veworld.vet
                        </div>
                      </div>

                      <button
                        onClick={copyWalletAddressToClipboard}
                        className={`inline-flex items-center gap-2 rounded-xl border px-3 py-2 text-sm font-semibold transition-all duration-200 ${
                          copySuccess
                            ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                            : 'border-slate-200 bg-white text-slate-700 hover:border-blue-200 hover:bg-blue-50 hover:text-blue-700'
                        }`}
                      >
                        {copySuccess ? (
                          <>
                            <Check className="h-4 w-4" />
                            {language === 'en' ? 'Copied' : 'Kopiert'}
                          </>
                        ) : (
                          <>
                            <Copy className="h-4 w-4" />
                            {language === 'en' ? 'Copy' : 'Kopieren'}
                          </>
                        )}
                      </button>
                    </div>

                    <div className="rounded-2xl border border-slate-200 bg-white px-4 py-3 font-mono text-sm text-slate-700 break-all shadow-sm">
                      {autoTransactionData.to}
                    </div>
                  </div>
                </div>

                {/* Notice */}
                <div className="mb-5 rounded-[20px] border border-blue-100 bg-blue-50/80 px-4 py-3 text-sm leading-6 text-blue-900">
                  {language === 'en' ? (
                    <>
                      <span>Please confirm the transaction in the VeWorld Browser Extension.</span>
                      <span className="block mt-1">Once signed, the payment will be matched automatically.</span>
                    </>
                  ) : (
                    <>
                      <span>Bitte bestätige die Transaktion in der VeWorld Browser Extension.</span>
                      <span className="block mt-1">Nach der Signatur wird die Zahlung automatisch zugeordnet.</span>
                    </>
                  )}
                </div>

                {/* Success / Error */}
                {autoTransactionSuccess && (
                  <div className="mb-5 rounded-[20px] border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-semibold text-emerald-700">
                    {language === 'en'
                      ? 'Transaction started successfully.'
                      : 'Transaktion erfolgreich gestartet.'}
                  </div>
                )}

                {autoTransactionError && (
                  <div className="mb-5 rounded-[20px] border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700">
                    {autoTransactionError}
                  </div>
                )}

                {/* QR Code */}
                {autoTransactionData?.method === 'qrCode' && autoTransactionData?.qrCodeData && (
                  <div className="mb-5 rounded-[22px] border border-slate-200 bg-slate-50 p-5 shadow-sm">
                    <div className="mb-4 text-center text-sm font-semibold text-slate-900">
                      {language === 'en'
                        ? 'Scan with your VeWorld app'
                        : 'Mit deiner VeWorld App scannen'}
                    </div>

                    <div className="mb-4 flex justify-center">
                      <div className="rounded-2xl border border-slate-200 bg-white p-3 shadow-sm">
                        <img
                          src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(autoTransactionData.qrCodeData)}`}
                          alt="VeWorld QR Code"
                          className="rounded-xl"
                        />
                      </div>
                    </div>

                    <div className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs text-slate-600 break-all">
                      {autoTransactionData.qrCodeData}
                    </div>
                  </div>
                )}

                {/* Actions */}
                <div className="flex gap-3">
                  <button
                    onClick={async () => {
                      try {
                        const pendingOrders = await fetch(apiUrl('/api/veworld/pending-orders'), {
                          method: 'GET',
                          credentials: 'include'
                        });

                        if (pendingOrders.ok) {
                          const orders = await pendingOrders.json();
                          const latestOrder = orders.find(order =>
                            order.status === 'pending' || order.status === 'processing'
                          );

                          if (latestOrder) {
                            await fetch(apiUrl('/api/veworld/cancel-pending-order'), {
                              method: 'POST',
                              headers: {
                                'Content-Type': 'application/json'
                              },
                              body: JSON.stringify({ paymentId: latestOrder.payment_id }),
                              credentials: 'include'
                            });
                                                      }
                        }
                      } catch (cancelError) {
                                              }

                      setShowAutoTransactionModal(false);
                      setAutoTransactionData(null);
                      setAutoTransactionError(null);
                      setAutoTransactionSuccess(false);
                    }}
                    className="flex-1 rounded-2xl border border-slate-200 bg-white px-5 py-3 font-semibold text-slate-700 transition-all duration-200 hover:border-slate-300 hover:bg-slate-50"
                  >
                    {language === 'en' ? 'Cancel' : 'Abbrechen'}
                  </button>

                  <button
                    onClick={() => {
                      setAutoTransactionError(null);
                      setAutoTransactionSuccess(false);
                      handleStartVeWorldTransaction();
                    }}
                    disabled={autoTransactionLoading}
                    className="flex-1 rounded-2xl bg-gradient-to-r from-blue-600 to-indigo-600 px-5 py-3 font-semibold text-white shadow-[0_12px_28px_rgba(59,130,246,0.28)] transition-all duration-200 hover:from-blue-700 hover:to-indigo-700 hover:shadow-[0_16px_34px_rgba(59,130,246,0.34)] disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {autoTransactionLoading ? (
                      <span className="flex items-center justify-center gap-2">
                        <Loader2 className="h-5 w-5 animate-spin" />
                        <span>{language === 'en' ? 'Starting...' : 'Starte...'}</span>
                      </span>
                    ) : (
                      <span className="flex items-center justify-center gap-2">
                        <Zap className="h-5 w-5" />
                        <span>{language === 'en' ? 'Start transaction' : 'Transaktion starten'}</span>
                      </span>
                    )}
                  </button>
                </div>
              </div>
            </div>
        ) : null}
      </ModalMotionShell>

      <div className="mt-2 sm:mt-3 lg:mt-5">
        <Footer
          onOpenSupport={() => {
            setHelpActiveSection("support");
            setShowHelpModal(true);
          }}
        />
      </div>
    </div>
      </div>

      {/* 🔗 Wallet Connect Modal */}
      <WalletConnectModal
        isOpen={showWalletModal}
        onClose={() => {
          setShowWalletModal(false);
          setForceFreshVeWorldLogin(false);
        }}
        resetQrCode={resetQrCode}
        forceFreshVeWorldLogin={forceFreshVeWorldLogin}
        onOpenLegal={() => setShowLegalModal(true)}
        onSuccess={async (account, walletType) => {
          setForceFreshVeWorldLogin(false);

                                        
          // 🚀 FIX: Beim neuen Login sicherstellen dass alte Auth gelöscht ist
          const oldAuth = localStorage.getItem("v3tax_auth");
          if (oldAuth) {
            try {
              const oldAuthData = JSON.parse(oldAuth);
              if (oldAuthData.walletAddress !== account) {
                                localStorage.removeItem('v3tax_auth');
              }
            } catch (e) {
                            localStorage.removeItem('v3tax_auth');
            }
          }
          
          // 🆕 WICHTIG: Setze den source Parameter korrekt für den useWallet Hook UND AuthContext
          if (walletType === 'wallet-connect') {
                        setSource('wallet-connect');
            // 🆕 WICHTIG: Auch im AuthContext setzen für AnalysisApp.jsx
            setAuthSource('wallet-connect');
          } else if (walletType === 'metamask') {
                        setSource('MetaMask Extension');
            // 🆕 WICHTIG: Auch im AuthContext setzen für AnalysisApp.jsx
            setAuthSource('MetaMask Extension');
          } else if (walletType === 'ledger') {
                        setSource(null);
            // 🆕 WICHTIG: Auch im AuthContext setzen für AnalysisApp.jsx
            setAuthSource(null);
          }
          
          setShowWalletModal(false);
          
          // 🚨 CRITICAL FIX: VeWorld-Wallet-Adresse im Backend authentifizieren
          if (walletType === 'wallet-connect' && account) {
            try {
                            
              // 🎯 VeWorld-Wallet-Adresse im Backend authentifizieren
              // Extrahiere die echte Wallet-Adresse aus dem VeChain-Format
              const walletAddress = account.includes(':') ? account.split(':').pop() : account;
                            
              // 🚀 WICHTIG: Backend-Authentifizierung über devBypass
              const loginResult = await devBypass(walletAddress);
              
              if (loginResult.success) {
                                
                // 🚀 WICHTIG: Lokale Authentifizierung für VeWorld-Integration setzen
                const authData = {
                  walletAddress: walletAddress,
                  plan: loginResult.data?.plan || 'free',
                  isDev: loginResult.data?.isDev || false,
                  authenticated: true,
                  loginTime: new Date().toISOString()
                };
                localStorage.setItem('v3tax_auth', JSON.stringify(authData));

                trackPlatformActivity("login", {
                  source: walletType || authData.source || "wallet_connect_success",
                  walletAddress: authData.walletAddress,
                  plan: authData.plan || "free",
                }).catch(() => {});
                                
                // 🔹 UI sofort aktualisieren (Header zeigt Wallet direkt)
                applyLocalAuth(authData);
                
                // 🔹 Trigger Event für expliziten Same-Tab-Update
                window.dispatchEvent(new Event('v3tax-auth'));
                
                // Optional danach nochmal Status ziehen (Plan etc.)
                checkAuthStatus();
                
                // 🎉 Zeige Success Toast
                showSuccessToast(account, walletType);
                
                              } else {
                                
                // 🚀 Fallback: Lokale Authentifizierung
                                const authData = {
                  walletAddress: walletAddress,
                  plan: 'free',
                  isDev: false,
                  authenticated: true,
                  loginTime: new Date().toISOString()
                };
                
                localStorage.setItem('v3tax_auth', JSON.stringify(authData));
                
                // 🔹 UI sofort aktualisieren (Header zeigt Wallet direkt)
                applyLocalAuth(authData);
                
                // 🔹 Trigger Event für expliziten Same-Tab-Update
                window.dispatchEvent(new Event('v3tax-auth'));
                
                // Optional danach nochmal Status ziehen (Plan etc.)
                checkAuthStatus();
                
                // 🎉 Zeige Success Toast
                showSuccessToast(account, walletType);
              }
            } catch (error) {
                            
              // 🚀 Fallback: Lokale Authentifizierung bei Backend-Fehler
              try {
                const walletAddress = account.includes(':') ? account.split(':').pop() : account;
                                
                const authData = {
                  walletAddress: walletAddress,
                  plan: 'free',
                  isDev: false,
                  authenticated: true,
                  loginTime: new Date().toISOString()
                };
                
                localStorage.setItem('v3tax_auth', JSON.stringify(authData));
                
                // 🔹 UI sofort aktualisieren (Header zeigt Wallet direkt)
                applyLocalAuth(authData);
                
                // 🔹 Trigger Event für expliziten Same-Tab-Update
                window.dispatchEvent(new Event('v3tax-auth'));
                
                // Optional danach nochmal Status ziehen (Plan etc.)
                checkAuthStatus();
                
                              } catch (fallbackError) {
                              }
              
              // Trotzdem Toast anzeigen
              showSuccessToast(account, walletType);
            }
          } else {
            // 🚨 CRITICAL FIX: Auth-Status sofort überprüfen nach erfolgreichem Login
            try {
                            
              // 🚀 WICHTIG: ZUERST localStorage setzen, DANN checkAuthStatus aufrufen!
              const walletAddress = account.includes(':') ? account.split(':').pop() : account;
              const authData = {
                walletAddress: walletAddress,
                plan: 'free', // Standard-Plan
                isDev: false,
                authenticated: true,
                loginTime: new Date().toISOString()
              };
              localStorage.setItem('v3tax_auth', JSON.stringify(authData));
                            
              // 🔹 UI sofort aktualisieren (Header zeigt Wallet direkt)
              applyLocalAuth(authData);
              
              // Optional danach nochmal Status ziehen (Plan etc.)
              checkAuthStatus();
              
              // 🎉 Zeige Success Toast für BEIDE Wallet-Typen
              showSuccessToast(account, walletType);
              
                          } catch (error) {
                            // Trotzdem Toast anzeigen
              showSuccessToast(account, walletType);
            }
          }
          
          // 🚀 WICHTIG: Pairing speichern für zukünftige Logins
          try {
                        // Das Pairing wird bereits im WalletConnectModal gespeichert,
            // aber wir können hier nochmal sicherstellen, dass es funktioniert
                      } catch (pairingError) {
                      }
          
          // Nach erfolgreicher Wallet-Verbindung den Kaufvorgang fortsetzen
          // Der handleBuyClick wird automatisch neu ausgeführt da isAuthenticated jetzt true ist
        }}
        {...(isHandheldDevice
          ? { allowedWallets: ['veworld'], uiVariant: 'desktop' }
          : {})}
      />
      
      {/* Toast Container für GPS-Funktionen */}
      <ToastContainer 
        position="top-right" 
        autoClose={6000} 
        newestOnTop 
        closeOnClick 
        draggable 
        pauseOnHover 
      />

      <AdminActivityDashboard isAdmin={isAdminWallet} />
      <B2BInvoiceAdmin isAdmin={isAdminWallet} />
    </>
  );
}

export default App;